import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, Package, MessageSquare, DollarSign, Settings,
  TrendingUp, ShoppingCart, Bike, Store, AlertCircle, CheckCircle,
  XCircle, Clock, Search, Filter, Download, Eye, Edit, Trash2,
  Plus, X, ChevronRight, LogOut, Bell, BarChart3, PieChart,
  Activity, UserCheck, UserX, Mail, Phone, MapPin, Calendar,
  FileText, Award, Target, Zap, ArrowUpRight, ArrowDownRight, User,
  ClipboardList, UserPlus, Check, Power, ShoppingBag, Lightbulb, Tag, AlertTriangle, Calculator
} from 'lucide-react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { collection, query, onSnapshot, doc, updateDoc, deleteDoc, where, orderBy, getDocs, addDoc, serverTimestamp, setDoc, getDoc, writeBatch } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Credenziali admin gestite da Firebase Auth + custom claim

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const generateTempPassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const FarmaciaSubitoAdmin = () => {
  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // PIN State — fasi: 'login' | 'pin' | 'dashboard'
  const [authStep, setAuthStep] = useState('login');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [showCambiaPinModal, setShowCambiaPinModal] = useState(false);

  // App State
  const [currentView, setCurrentView] = useState('dashboard');
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [showContrattoModal, setShowContrattoModal] = useState(false);
  const [selectedContratto, setSelectedContratto] = useState(null);
  const [candidature, setCandidature] = useState([]);
  const [richiesteRicetta, setRichiesteRicetta] = useState([]);
  const [selectedCandidatura, setSelectedCandidatura] = useState(null);
  const [showCandidaturaModal, setShowCandidaturaModal] = useState(false);


  // Stats State
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    activeRiders: 0,
    activePharmacies: 0,
    todayOrders: 0,
    todayRevenue: 0,
    avgOrderValue: 0,
    completionRate: 0,
    newCandidature: 0
  });

  // Mock Data
  const [riders, setRiders] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [adminNotifiche, setAdminNotifiche] = useState([]);
  const prevCounts = React.useRef({ orders: 0, candidature: 0, suggerimenti: 0, segnalazioni: 0, ricette: 0 });
  const prevOrderStatuses = React.useRef({});
  const isFirstLoad = React.useRef({ orders: true, candidature: true, suggerimenti: true, segnalazioni: true, ricette: true });

  const [tickets, setTickets] = useState([]);
  const [suggerimenti, setSuggerimenti] = useState([]);
  const [ridersFotoPending, setRidersFotoPending] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  // ============ EXPORT CSV FUNCTIONS ============

  const exportCSV = (data, filename, headers, rowMapper) => {
    if (data.length === 0) { alert('Nessun dato da esportare'); return; }
    const csvContent = [headers, ...data.map(rowMapper)]
      .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportOrdiniCSV = () => exportCSV(
    orders,
    `ordini-globali-${new Date().toISOString().split('T')[0]}.csv`,
    ['ID', 'Data', 'Cliente', 'Email', 'Codice Fiscale', 'Farmacia', 'Indirizzo', 'Prodotti', 'Subtotale', 'Consegna', 'Totale', 'Stato', 'Pagamento'],
    o => [
      o.id.substring(0, 8),
      new Date(o.createdAt).toLocaleDateString('it-IT'),
      o.customerName || '',
      o.userEmail || '',
      o.codiceFiscale || '',
      o.pharmacyName || '',
      `${o.deliveryAddress || ''} ${o.deliveryCity || ''}`.trim(),
      o.items?.map(i => `${i.quantity}x ${i.name}`).join(' | ') || '',
      (o.subtotal || 0).toFixed(2),
      (o.deliveryFee || 0).toFixed(2),
      (o.total || 0).toFixed(2),
      o.status || '',
      o.paymentStatus || ''
    ]
  );

  const exportFarmacieCSV = () => exportCSV(
    pharmacies,
    `farmacie-${new Date().toISOString().split('T')[0]}.csv`,
    ['ID', 'Nome', 'Email', 'Telefono', 'Indirizzo', 'P.IVA', 'Commissione %', 'Stato', 'Data Creazione'],
    f => [
      f.id.substring(0, 8),
      f.name || '',
      f.email || '',
      f.telefono || '',
      f.indirizzo || '',
      f.piva || '',
      f.commission || 10,
      f.status || '',
      f.createdAt ? new Date(f.createdAt.seconds * 1000).toLocaleDateString('it-IT') : ''
    ]
  );

  const exportCandidatureCSV = () => exportCSV(
    candidature,
    `candidature-rider-${new Date().toISOString().split('T')[0]}.csv`,
    ['ID', 'Nome', 'Cognome', 'Email', 'Telefono', 'Mezzo', 'Città', 'Stato', 'Data'],
    c => [
      c.id.substring(0, 8),
      c.nome || '',
      c.cognome || '',
      c.email || '',
      c.telefono || '',
      c.mezzoTrasporto || '',
      c.citta || '',
      c.status || '',
      c.createdAt ? new Date(c.createdAt.seconds * 1000).toLocaleDateString('it-IT') : ''
    ]
  );

  // ============ REPORT FATTURATO ============
  const getReportFatturato = () => {
    const report = {};
    orders.forEach(order => {
      if (!order.farmaciaId) return;
      if (!report[order.farmaciaId]) {
        report[order.farmaciaId] = {
          farmaciaId: order.farmaciaId,
          nome: order.pharmacyName || 'Sconosciuta',
          totaleOrdini: 0,
          numeroOrdini: 0,
          ordiniCompletati: 0,
          commissioni: 0,
          dovutoFarmacia: 0
        };
      }
      report[order.farmaciaId].totaleOrdini += (order.total || 0);
      report[order.farmaciaId].numeroOrdini += 1;
      if (order.status === 'delivered') {
        report[order.farmaciaId].ordiniCompletati += 1;
      }
    });
    Object.values(report).forEach(r => {
      r.commissioni = r.totaleOrdini * 0.10;
      r.dovutoFarmacia = r.totaleOrdini * 0.90;
    });
    return Object.values(report).sort((a, b) => b.totaleOrdini - a.totaleOrdini);
  };

  const playAdminSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const ctx = new AudioContext();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.4, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
        osc.start(start); osc.stop(start + dur);
      };
      const now = ctx.currentTime;
      playTone(800, now, 0.15);
      playTone(1000, now + 0.18, 0.15);
      playTone(1200, now + 0.36, 0.25);
    } catch(e) {}
  };

  const aggiungiNotifica = (tipo, messaggio, icon) => {
    const notifica = { id: Date.now(), tipo, messaggio, icon, time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), letta: false };
    setAdminNotifiche(prev => [notifica, ...prev].slice(0, 20));
    playAdminSound();
    if (Notification.permission === 'granted') {
      new Notification(`FarmaciaSubito Admin — ${icon} ${tipo}`, { body: messaggio, icon: '/logo192.png' });
    }
  };

// Richiedi permesso notifiche
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Auth Check — verifica custom claim admin:true
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        if (tokenResult.claims.admin === true) {
          setIsLoggedIn(true);
          // Se l'authStep è ancora 'login', vai al PIN
          setAuthStep(prev => prev === 'login' ? 'pin' : prev);
        } else {
          await signOut(auth);
          setIsLoggedIn(false);
          setAuthStep('login');
        }
      } else {
        setIsLoggedIn(false);
        setAuthStep('login');
      }
    });
    return () => unsubscribe();
  }, []);

  // Listen to orders
  useEffect(() => {
    if (!isLoggedIn) return;

    const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedOrders = [];
      snapshot.forEach((doc) => {
        fetchedOrders.push({ id: doc.id, ...doc.data() });
      });
      setOrders(fetchedOrders);

      // Notifiche per cambio stato ordine (confronta stato precedente con nuovo)
      if (!isFirstLoad.current.orders) {
        fetchedOrders.forEach(o => {
          const statoPrecedente = prevOrderStatuses.current[o.id];
          // Solo se lo stato è effettivamente cambiato
          if (statoPrecedente && statoPrecedente !== o.status) {
            if (o.status === 'delivering') {
              aggiungiNotifica('Ordine Ritirato', `Il rider ha ritirato l'ordine #${o.id.substring(0,8)}`, '📦');
            } else if (o.status === 'delivered') {
              aggiungiNotifica('Ordine Consegnato', `Ordine #${o.id.substring(0,8)} consegnato al cliente`, '✅');
            } else if (o.status === 'cancelled') {
              aggiungiNotifica('Ordine Annullato', `L'ordine #${o.id.substring(0,8)} è stato annullato`, '❌');
            }
          }
          // Nuovo ordine pronto che prima non esisteva
          if (!statoPrecedente && (o.status === 'ready' || o.status === 'preparing')) {
            aggiungiNotifica('Nuovo Ordine', `È arrivato un nuovo ordine!`, '🛒');
          }
        });
      }
      // Aggiorna la mappa degli stati per il prossimo confronto
      const nuoviStati = {};
      fetchedOrders.forEach(o => { nuoviStati[o.id] = o.status; });
      prevOrderStatuses.current = nuoviStati;
      if (isFirstLoad.current.orders) isFirstLoad.current.orders = false;

      // Calculate stats
      // P2 FIX: il fatturato conta SOLO gli ordini effettivamente consegnati (delivered).
      // Esclude cancelled, in_revisione_admin e qualsiasi ordine non concluso/rimborsato.
      const deliveredOrders = fetchedOrders.filter(o => o.status === 'delivered');
      const totalRevenue = deliveredOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const todayOrders = fetchedOrders.filter(o => {
        const orderDate = new Date(o.createdAt);
        const today = new Date();
        return orderDate.toDateString() === today.toDateString();
      });
      // Fatturato di oggi: solo ordini consegnati oggi.
      const todayRevenue = todayOrders
        .filter(o => o.status === 'delivered')
        .reduce((sum, o) => sum + (o.total || 0), 0);

      setStats(prev => ({
        ...prev,
        totalOrders: fetchedOrders.length,
        totalRevenue,
        activeRiders: riders.length,
        activePharmacies: pharmacies.length,
        todayOrders: todayOrders.length,
        todayRevenue,
        // Valore medio calcolato sul fatturato reale (ordini consegnati).
        avgOrderValue: deliveredOrders.length > 0 ? totalRevenue / deliveredOrders.length : 0,
        completionRate: fetchedOrders.length > 0 ? (deliveredOrders.length / fetchedOrders.length) * 100 : 0
      }));
    });

    return () => unsubscribe();
  }, [isLoggedIn, riders.length, pharmacies.length]);

  // Listen to richieste ricetta
useEffect(() => {
  if (!isLoggedIn) return;
  const q = query(collection(db, 'richieste-ricetta'), orderBy('createdAt', 'desc'));
  const unsubscribe = onSnapshot(q, snap => {
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setRichiesteRicetta(data);
    const pendingCount = data.filter(r => r.status === 'pending').length;
    if (!isFirstLoad.current.ricette && pendingCount > prevCounts.current.ricette) {
      aggiungiNotifica('Richiesta Ricetta', 'Un cliente ha inviato una richiesta con ricetta!', '📋');
    }
    if (isFirstLoad.current.ricette) isFirstLoad.current.ricette = false;
    prevCounts.current.ricette = pendingCount;
  });
  return () => unsubscribe();
}, [isLoggedIn]);

// ════════════════════════════════════════════════════════════════
// LIQUIDAZIONE MENSILE
// ════════════════════════════════════════════════════════════════
const LiquidazioneView = () => {
  const now = new Date();
  const [meseSelezionato, setMeseSelezionato] = React.useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [riders, setRiders] = React.useState([]);
  const [ordini, setOrdini] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pagandoId, setPagandoId] = React.useState(null);
  const [pagandoTutti, setPagandoTutti] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState(null);
  const [inviandoProspettoId, setInviandoProspettoId] = React.useState(null);
  const [prospettiInviati, setProspettiInviati] = React.useState({}); // { riderId: true }

  // Genera lista mesi (ultimi 12)
  const mesiDisponibili = React.useMemo(() => {
    const mesi = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      mesi.push({ val, label });
    }
    return mesi;
  }, []);

  // Carica riders approvati
  React.useEffect(() => {
    const q = query(collection(db, 'riders'), where('status', '==', 'approved'));
    const unsub = onSnapshot(q, snap => {
      setRiders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // Carica ordini del mese selezionato
  React.useEffect(() => {
    setLoading(true);
    const [anno, mese] = meseSelezionato.split('-');
    const inizioMese = `${anno}-${mese}-01`;
    const fineMese = `${anno}-${mese}-31`;
    const q = query(
      collection(db, 'orders'),
      where('status', '==', 'delivered'),
      where('deliveredAt', '>=', inizioMese),
      where('deliveredAt', '<=', fineMese)
    );
    getDocs(q).then(snap => {
      setOrdini(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [meseSelezionato]);

  // Calcola dati per ogni rider
  const datiRider = React.useMemo(() => {
    return riders.map(rider => {
      const ordiniRider = ordini.filter(o =>
        o.riderId === rider.id || o.assignedRiderId === rider.id
      );
      const nOrdini = ordiniRider.length;
      const compensoTotale = ordiniRider.reduce((sum, o) =>
        sum + (parseFloat(o.compensoRider) || (3 + (o.calculatedDistance || 0) * 0.20)), 0
      );
      const contantiDaScalare = ordiniRider.reduce((sum, o) =>
        sum + (parseFloat(o.contanti_daScalareInLiquidazione) || 0), 0
      );
      const netto = compensoTotale - contantiDaScalare;
      return {
        rider,
        nOrdini,
        compensoTotale,
        contantiDaScalare,
        netto,
        giaLiquidato: ordiniRider.some(o => o.liquidatoMese === meseSelezionato),
      };
    }).filter(d => d.nOrdini > 0);
  }, [riders, ordini, meseSelezionato]);

  // Invia prospetto mensile al rider via email
  const inviaProspetto = async (dati) => {
    setInviandoProspettoId(dati.rider.id);
    try {
      const [anno, mese] = meseSelezionato.split('-');
      const nomeMeseProspetto = new Date(parseInt(anno), parseInt(mese) - 1, 1)
        .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

      // Recupera dettaglio ordini del rider
      const ordiniRider = ordini.filter(o =>
        o.riderId === dati.rider.id || o.assignedRiderId === dati.rider.id
      );

      const righeOrdini = ordiniRider.map((o, i) => `
        <tr style="background:${i % 2 === 0 ? '#f9f9f9' : '#fff'}">
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">${new Date(o.deliveredAt).toLocaleDateString('it-IT')}</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">#${o.id.substring(0, 8)}</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">${(o.calculatedDistance || 0).toFixed(1)} km</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">€${(parseFloat(o.compensoRider) || 3).toFixed(2)}</td>
          <td style="padding:6px 8px;border:1px solid #e5e7eb;">€${(parseFloat(o.contanti_daScalareInLiquidazione) || 0).toFixed(2)}</td>
        </tr>
      `).join('');

      const htmlProspetto = `
        <div style="font-family:sans-serif;max-width:700px;margin:0 auto;padding:20px;">
          <div style="background:#1a1a2e;color:white;padding:20px;border-radius:8px;margin-bottom:20px;">
            <h1 style="margin:0;font-size:20px;">💊 FarmaciaSubito</h1>
            <p style="margin:4px 0 0;opacity:0.8;">Prospetto Mensile Rider</p>
          </div>
          <h2 style="color:#1a1a2e;">Prospetto ${nomeMeseProspetto}</h2>
          <p>Gentile <strong>${dati.rider.name}</strong>,</p>
          <p>Di seguito il riepilogo della tua attività per il mese di <strong>${nomeMeseProspetto}</strong>.</p>
          <table style="width:100%;border-collapse:collapse;margin:16px 0;">
            <thead>
              <tr style="background:#1a1a2e;color:white;">
                <th style="padding:8px;text-align:left;">Data</th>
                <th style="padding:8px;text-align:left;">Ordine</th>
                <th style="padding:8px;text-align:left;">Km</th>
                <th style="padding:8px;text-align:left;">Compenso</th>
                <th style="padding:8px;text-align:left;">Contanti scalati</th>
              </tr>
            </thead>
            <tbody>${righeOrdini}</tbody>
          </table>
          <div style="background:#f0f9ff;border:2px solid #0ea5e9;border-radius:8px;padding:16px;margin:16px 0;">
            <table style="width:100%;">
              <tr><td><strong>Totale ordini</strong></td><td style="text-align:right;">${dati.nOrdini}</td></tr>
              <tr><td><strong>Compenso totale</strong></td><td style="text-align:right;color:#16a34a;">€${dati.compensoTotale.toFixed(2)}</td></tr>
              <tr><td><strong>Contanti da scalare</strong></td><td style="text-align:right;color:#ea580c;">-€${dati.contantiDaScalare.toFixed(2)}</td></tr>
              ${dati.netto >= 0
                ? `<tr style="border-top:2px solid #0ea5e9;"><td><strong>NETTO DA RICEVERE</strong></td><td style="text-align:right;font-size:18px;font-weight:bold;color:#16a34a;">€${dati.netto.toFixed(2)}</td></tr>`
                : `<tr style="border-top:2px solid #dc2626;"><td><strong>SALDO A TUO CARICO</strong></td><td style="text-align:right;font-size:18px;font-weight:bold;color:#dc2626;">€${Math.abs(dati.netto).toFixed(2)}</td></tr>`
              }
            </table>
          </div>
          ${dati.netto >= 0
            ? `<p style="color:#6b7280;font-size:12px;">Il pagamento verrà effettuato tramite bonifico bancario sull'IBAN che hai registrato, entro il 12 del mese successivo.</p>`
            : `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:16px;margin:16px 0;">
                 <p style="margin:0 0 8px;color:#991b1b;font-weight:bold;">💳 Istruzioni per il versamento</p>
                 <p style="margin:0 0 8px;color:#7f1d1d;font-size:13px;">
                   Questo mese hai incassato in contanti, per conto di FarmaciaSubito, più di quanto ammonta il tuo compenso.
                   Ti chiediamo di versare il saldo di <strong>€${Math.abs(dati.netto).toFixed(2)}</strong> tramite bonifico:
                 </p>
                 <table style="width:100%;font-size:13px;color:#7f1d1d;">
                   <tr><td>Intestatario:</td><td style="text-align:right;"><strong>Mirco Piscopo</strong></td></tr>
                   <tr><td>IBAN:</td><td style="text-align:right;"><strong>IT87 F360 8105 1382 9396 2793 966</strong></td></tr>
                   <tr><td>Causale:</td><td style="text-align:right;">Saldo contanti ${nomeMeseProspetto} - ${dati.rider.name}</td></tr>
                 </table>
                 <p style="margin:8px 0 0;color:#7f1d1d;font-size:12px;">Effettua il versamento entro il 12 del mese successivo.</p>
               </div>`
          }

          <!-- ── PRO-FORMA: dati pronti per emettere la fattura ── -->
          <div style="background:#fffbeb;border:2px solid #f59e0b;border-radius:8px;padding:16px;margin:20px 0;">
            <p style="margin:0 0 4px;color:#78350f;font-weight:bold;font-size:15px;">🧾 Dati per la tua fattura</p>
            <p style="margin:0 0 12px;color:#92400e;font-size:12px;">
              Usa questi dati per emettere la fattura elettronica verso FarmaciaSubito.
            </p>

            <table style="width:100%;font-size:13px;color:#78350f;border-collapse:collapse;">
              <tr style="background:#fef3c7;">
                <td style="padding:8px;"><strong>IMPORTO DA FATTURARE</strong></td>
                <td style="padding:8px;text-align:right;font-size:17px;font-weight:bold;">
                  €${dati.compensoTotale.toFixed(2)}
                </td>
              </tr>
            </table>

            <p style="margin:10px 0 4px;color:#92400e;font-size:12px;"><strong>Descrizione da inserire in fattura:</strong></p>
            <div style="background:white;border:1px solid #fcd34d;border-radius:4px;padding:10px;font-size:13px;color:#1f2937;">
              Servizio di consegna a domicilio per conto di FarmaciaSubito &mdash;
              periodo ${nomeMeseProspetto} &mdash; n. ${dati.nOrdini} consegne effettuate.
            </div>

            <p style="margin:12px 0 4px;color:#92400e;font-size:12px;"><strong>Intestare la fattura a:</strong></p>
            <table style="width:100%;font-size:13px;color:#1f2937;background:white;border:1px solid #fcd34d;border-radius:4px;">
              <tr><td style="padding:6px 10px;">Denominazione:</td><td style="padding:6px 10px;text-align:right;"><strong>Piscopo Mirco</strong></td></tr>
              <tr><td style="padding:6px 10px;">Partita IVA:</td><td style="padding:6px 10px;text-align:right;"><strong>01838720629</strong></td></tr>
              <tr><td style="padding:6px 10px;">Sede:</td><td style="padding:6px 10px;text-align:right;">Benevento (BN), Italia</td></tr>
              <tr><td style="padding:6px 10px;">Codice Destinatario SdI:</td><td style="padding:6px 10px;text-align:right;"><strong>0000000</strong></td></tr>
            </table>

            <p style="margin:12px 0 4px;color:#92400e;font-size:12px;"><strong>Dicitura obbligatoria (regime forfettario):</strong></p>
            <div style="background:white;border:1px solid #fcd34d;border-radius:4px;padding:10px;font-size:12px;color:#1f2937;font-style:italic;">
              "Operazione effettuata ai sensi dell'art. 1, commi 54-89, L. 190/2014 &mdash; Regime forfettario.
              Non soggetta a IVA né a ritenuta d'acconto."
            </div>

            <div style="background:#fef3c7;border-radius:4px;padding:10px;margin-top:12px;">
              <p style="margin:0;color:#78350f;font-size:12px;">
                <strong>⚠️ Importante:</strong> la fattura va emessa per l'<strong>intero compenso di
                €${dati.compensoTotale.toFixed(2)}</strong>${dati.contantiDaScalare > 0
                  ? `, NON al netto dei €${dati.contantiDaScalare.toFixed(2)} incassati in contanti.
                     I contanti sono somme di FarmaciaSubito che restituisci: sono una compensazione di cassa,
                     non uno sconto sulla prestazione. Il bonifico sarà di €${Math.abs(dati.netto).toFixed(2)}
                     ${dati.netto >= 0 ? '(compenso al netto della compensazione)' : '(a tuo carico)'}.`
                  : '.'}
              </p>
            </div>

            ${dati.compensoTotale > 77.47
              ? `<p style="margin:10px 0 0;color:#78350f;font-size:12px;">
                   <strong>Marca da bollo:</strong> l'importo supera €77,47 &rarr; serve la marca da bollo da €2,00
                   (da assolvere virtualmente nella fattura elettronica).
                 </p>`
              : `<p style="margin:10px 0 0;color:#78350f;font-size:12px;">
                   <strong>Marca da bollo:</strong> non necessaria (importo inferiore a €77,47).
                 </p>`
            }
          </div>

          <p style="color:#6b7280;font-size:12px;">— Il team di FarmaciaSubito</p>
        </div>
      `;

      const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: dati.rider.email,
          subject: `📊 Prospetto ${nomeMeseProspetto} — FarmaciaSubito`,
          html: htmlProspetto,
        }),
      });

      if (!res.ok) throw new Error('Errore invio email');

      setProspettiInviati(prev => ({ ...prev, [dati.rider.id]: true }));
      alert(`✅ Prospetto inviato a ${dati.rider.email}`);
    } catch (e) {
      alert(`❌ Errore invio prospetto: ${e.message}`);
    } finally {
      setInviandoProspettoId(null);
    }
  };

  // Segna un rider come liquidato (il bonifico è reale e fuori app, verso il suo IBAN)
  const pagaRider = async (dati) => {
    setPagandoId(dati.rider.id);
    try {
      // Segna ordini del rider come liquidati
      const ordiniRider = ordini.filter(o =>
        o.riderId === dati.rider.id || o.assignedRiderId === dati.rider.id
      );
      await Promise.all(ordiniRider.map(o =>
        updateDoc(doc(db, 'orders', o.id), {
          liquidatoMese: meseSelezionato,
          liquidatoAt: new Date().toISOString(),
        })
      ));
      alert(`✅ ${dati.rider.name} segnato come liquidato per €${Math.max(0, dati.netto).toFixed(2)}.\n\nRicorda: il bonifico va effettuato manualmente verso l'IBAN del rider.`);
      // Ricarica ordini
      setOrdini(prev => prev.map(o =>
        (o.riderId === dati.rider.id || o.assignedRiderId === dati.rider.id)
          ? { ...o, liquidatoMese: meseSelezionato }
          : o
      ));
    } catch (e) {
      alert(`❌ Errore: ${e.message}`);
    } finally {
      setPagandoId(null);
      setConfirmModal(null);
    }
  };

  // Paga tutti
  const pagaTutti = async () => {
    const daPagare = datiRider.filter(d => !d.giaLiquidato && d.nOrdini > 0);
    if (daPagare.length === 0) { alert('Tutti i rider sono già stati liquidati.'); return; }
    setPagandoTutti(true);
    for (const dati of daPagare) {
      await pagaRider(dati);
    }
    setPagandoTutti(false);
    alert(`✅ Liquidazione completata per ${daPagare.length} rider!`);
  };

  const [anno, mese] = meseSelezionato.split('-');
  const nomeMese = new Date(parseInt(anno), parseInt(mese) - 1, 1)
    .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  const totaleCompensoDovuto = datiRider.reduce((s, d) => s + d.compensoTotale, 0);
  const totaleContantiDaScalare = datiRider.reduce((s, d) => s + d.contantiDaScalare, 0);
  const totaleNetto = datiRider.reduce((s, d) => s + d.netto, 0);

  return (
    <div className="space-y-6">
      {/* SELEZIONE MESE */}
      <div className="bg-white rounded-xl shadow p-4 flex items-center gap-4 flex-wrap">
        <label className="font-bold text-gray-700">Periodo:</label>
        <select
          value={meseSelezionato}
          onChange={e => setMeseSelezionato(e.target.value)}
          className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 outline-none font-semibold"
        >
          {mesiDisponibili.map(m => (
            <option key={m.val} value={m.val}>{m.label}</option>
          ))}
        </select>
        <span className="text-sm text-gray-500 capitalize">{nomeMese}</span>
      </div>

      {/* RIEPILOGO TOTALI */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-xs text-gray-500 font-bold uppercase mb-1">Compensi da Pagare</p>
          <p className="text-2xl font-black text-green-600">€{totaleCompensoDovuto.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-xs text-gray-500 font-bold uppercase mb-1">Contanti da Scalare</p>
          <p className="text-2xl font-black text-orange-600">-€{totaleContantiDaScalare.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-xs text-gray-500 font-bold uppercase mb-1">
            {totaleNetto >= 0 ? 'Da Pagare ai Rider' : 'A Carico dei Rider'}
          </p>
          <p className={`text-2xl font-black ${totaleNetto >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
            €{Math.abs(totaleNetto).toFixed(2)}
          </p>
          <p className="text-[10px] text-gray-400 mt-1">
            {totaleNetto >= 0 ? 'FarmaciaSubito → Rider' : 'Rider → FarmaciaSubito'}
          </p>
        </div>
      </div>

      {/* LISTA RIDER */}
      <div className="bg-white rounded-xl shadow">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-black text-gray-800">Rider — {nomeMese}</h3>
          <button
            onClick={() => {
              const msg = totaleNetto >= 0
                ? `Segnare come liquidati tutti i rider per ${nomeMese}?\n\nDa pagare ai rider: €${totaleNetto.toFixed(2)}\n(FarmaciaSubito → Rider)`
                : `Segnare come liquidati tutti i rider per ${nomeMese}?\n\nA carico dei rider: €${Math.abs(totaleNetto).toFixed(2)}\n(Rider → FarmaciaSubito: sono loro a dover versare)`;
              if (!window.confirm(msg)) return;
              pagaTutti();
            }}
            disabled={pagandoTutti || datiRider.every(d => d.giaLiquidato)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-purple-700 transition disabled:bg-gray-300 flex items-center gap-2"
          >
            {pagandoTutti ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Pagamento in corso...</>
            ) : (
              <>💸 Paga Tutti</>
            )}
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
            Caricamento ordini...
          </div>
        ) : datiRider.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Calculator size={48} className="mx-auto mb-2 opacity-30" />
            <p>Nessun ordine completato in questo periodo</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {datiRider.map(dati => (
              <div key={dati.rider.id} className="p-4 flex items-center gap-4 flex-wrap">
                {/* Info rider */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-black text-gray-900">{dati.rider.name}</p>
                    {dati.giaLiquidato ? (
                      <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">✅ Liquidato</span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">⏳ Da pagare</span>
                    )}
                  </div>
                  {dati.rider.iban ? (
                    <p className="text-xs font-mono text-gray-600 mb-1">🏦 {dati.rider.iban}</p>
                  ) : (
                    <p className="text-xs font-bold text-red-500 mb-1">⚠️ IBAN mancante — il rider deve inserirlo nel profilo</p>
                  )}
                  <p className="text-xs text-gray-500">{dati.rider.email}</p>
                </div>

                {/* Dati contabili */}
                <div className="flex gap-4 text-sm flex-wrap">
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Ordini</p>
                    <p className="font-black text-gray-800">{dati.nOrdini}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Compenso</p>
                    <p className="font-black text-green-600">€{dati.compensoTotale.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Contanti scalati</p>
                    <p className="font-black text-orange-600">-€{dati.contantiDaScalare.toFixed(2)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">
                      {dati.netto >= 0 ? 'Da pagare' : 'A suo carico'}
                    </p>
                    <span className={`inline-block px-3 py-1 rounded-full font-black text-sm ${
                      dati.netto >= 0
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      €{Math.abs(dati.netto).toFixed(2)}
                    </span>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {dati.netto >= 0 ? 'gli devi' : 'ti deve'}
                    </p>
                  </div>
                </div>

                {/* Bottone Invia Prospetto */}
                <button
                  onClick={() => inviaProspetto(dati)}
                  disabled={inviandoProspettoId === dati.rider.id || dati.giaLiquidato || prospettiInviati[dati.rider.id]}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg font-bold hover:bg-blue-700 transition disabled:bg-gray-200 disabled:text-gray-400 flex items-center gap-1 text-sm"
                >
                  {inviandoProspettoId === dati.rider.id ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div></>
                  ) : prospettiInviati[dati.rider.id] ? (
                    '✅ Inviato'
                  ) : (
                    '📄 Prospetto'
                  )}
                </button>

                {/* Bottone Segna Liquidato — abilitato solo dopo invio prospetto e con IBAN presente */}
                <button
                  onClick={() => setConfirmModal(dati)}
                  disabled={dati.giaLiquidato || pagandoId === dati.rider.id || !dati.rider.iban || (!prospettiInviati[dati.rider.id] && !dati.giaLiquidato)}
                  title={!dati.rider.iban ? 'Il rider deve inserire il suo IBAN' : (!prospettiInviati[dati.rider.id] && !dati.giaLiquidato ? 'Invia prima il prospetto al rider' : '')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-green-700 transition disabled:bg-gray-200 disabled:text-gray-400 flex items-center gap-2 text-sm"
                >
                  {pagandoId === dati.rider.id ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Salvando...</>
                  ) : dati.giaLiquidato ? (
                    '✅ Liquidato'
                  ) : !dati.rider.iban ? (
                    '⚠️ No IBAN'
                  ) : (
                    '✅ Segna liquidato'
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL CONFERMA PAGAMENTO */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-xl font-black text-gray-900 mb-4">Conferma Pagamento</h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-500">Rider</span>
                <span className="font-bold">{confirmModal.rider.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Ordini</span>
                <span className="font-bold">{confirmModal.nOrdini}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Compenso totale</span>
                <span className="font-bold text-green-600">€{confirmModal.compensoTotale.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Contanti da scalare</span>
                <span className="font-bold text-orange-600">-€{confirmModal.contantiDaScalare.toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-3">
                <span className="font-black text-gray-900">
                  {confirmModal.netto >= 0 ? 'Netto da pagare' : 'Saldo a carico del rider'}
                </span>
                <span className={`font-black text-lg ${confirmModal.netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  €{Math.abs(confirmModal.netto).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                Annulla
              </button>
              <button
                onClick={() => pagaRider(confirmModal)}
                disabled={pagandoId === confirmModal.rider.id}
                className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-300"
              >
                {pagandoId === confirmModal.rider.id ? 'Pagando...' : '✅ Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RichiesteRicettaView = () => {
  const aggiornaStato = async (id, stato) => {
    try {
      // La sezione ricette è ora di sola GESTIONE: l'ordine viene creato
      // automaticamente all'invio della richiesta (lato cliente, via Cloud
      // Function). Qui l'admin aggiorna solo lo stato per gestione/problemi.
      await updateDoc(doc(db, 'richieste-ricetta', id), { status: stato });
    } catch (e) {
      alert('❌ Errore: ' + e.message);
    }
  };

  const colore = (s) => {
    if (s === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (s === 'ordine_creato') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'rejected') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const label = (s) => {
    if (s === 'pending') return '⏳ In elaborazione';
    if (s === 'ordine_creato') return '✅ Ordine creato';
    if (s === 'in_progress') return '🔄 In lavorazione';
    if (s === 'completed') return '✅ Completata';
    if (s === 'rejected') return '❌ Rifiutata';
    return s;
  };

  return (
    <div className="space-y-4">
      {richiesteRicetta.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p className="font-semibold">Nessuna richiesta ricetta</p>
        </div>
      ) : (
        richiesteRicetta.map(r => (
          <div key={r.id} className="bg-white rounded-2xl shadow-md p-5 border border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-black text-gray-900">{r.customerName || r.userEmail || 'Cliente'}</p>
                <p className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString('it-IT')}</p>
                {r.customerPhone && <p className="text-xs text-gray-500 mt-1">📞 {r.customerPhone}</p>}
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${colore(r.status)}`}>
                {label(r.status)}
              </span>
            </div>

            {r.note && (
              <div className="bg-gray-50 rounded-xl p-3 mb-3">
                <p className="text-xs text-gray-500 font-bold mb-1">Note cliente:</p>
                <p className="text-sm text-gray-700">{r.note}</p>
              </div>
            )}

            <div className="flex gap-2 flex-wrap mb-3">
              {r.ricettaUrl ? (
                <button onClick={() => window.open(r.ricettaUrl, '_blank')}
                  className="bg-blue-600 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-blue-700 transition">
                  👁 Vedi Ricetta
                </button>
              ) : (
                <span className="bg-gray-100 text-gray-400 text-xs font-bold px-4 py-2 rounded-lg">
                  🔒 Ricetta eliminata (consegna completata)
                </span>
              )}
              {r.ordineCreatoId && (
                <span className="bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-3 py-2 rounded-lg">
                  📦 Ordine: #{r.ordineCreatoId.substring(0, 8)}
                </span>
              )}
            </div>

            <div className="flex gap-2 flex-wrap">
              {/* L'ordine parte automaticamente all'invio. Qui solo gestione problemi. */}
              {r.status !== 'rejected' && !r.ordineCreatoId && (
                <button onClick={() => aggiornaStato(r.id, 'rejected')}
                  className="bg-red-50 border border-red-200 text-red-700 text-xs font-bold px-3 py-2 rounded-lg hover:bg-red-100 transition">
                  ❌ Segna problema / Rifiuta
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Notifica nuove candidature — gestita nell'onSnapshot sotto

// Listen to candidature rider - FIXED
  useEffect(() => {
    if (!isLoggedIn) return;

    const q = query(collection(db, 'candidature-rider'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const fetchedCandidature = [];
        snapshot.forEach((doc) => {
          fetchedCandidature.push({ id: doc.id, ...doc.data() });
        });
        setCandidature(fetchedCandidature);

        // Notifica nuove candidature
        const pendingCount = fetchedCandidature.filter(c => c.status === 'pending').length;
        if (!isFirstLoad.current.candidature && pendingCount > prevCounts.current.candidature) {
          aggiungiNotifica('Nuova Candidatura', 'Un nuovo rider ha inviato la candidatura!', '🛵');
        }
        if (isFirstLoad.current.candidature) isFirstLoad.current.candidature = false;
        prevCounts.current.candidature = pendingCount;
        const newCount = fetchedCandidature.filter(c => c.status === 'nuovo').length;
        setStats(prev => ({ ...prev, newCandidature: newCount }));
      }, 
      (error) => {
        console.error('❌ ERRORE Firestore:', error);
      }
    );

    return () => unsubscribe();
  }, [isLoggedIn]);

// Filtra rider attivi dalle candidature approvate
useEffect(() => {
  const approvedRiders = candidature
    .filter(c => c.status === 'approvato')
    .map((c, index) => ({
      id: c.id,
      name: `${c.nome} ${c.cognome}`,
      email: c.email,
      telefono: c.telefono,
      mezzoTrasporto: c.mezzoTrasporto,
      nome: c.nome,
      cognome: c.cognome,
      status: 'active',
      deliveries: 0,
      rating: 5.0,
      earnings: 0.0
    }));
  
  setRiders(approvedRiders);
}, [candidature]);

// Listen to pharmacies
useEffect(() => {
  if (!isLoggedIn) return;

  const q = query(collection(db, 'farmacie'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedPharmacies = [];
    snapshot.forEach((doc) => {
      fetchedPharmacies.push({ id: doc.id, ...doc.data() });
    });
    setPharmacies(fetchedPharmacies);
  });

  return () => unsubscribe();
}, [isLoggedIn]);

// Listen to suggerimenti
useEffect(() => {
  if (!isLoggedIn) return;
  const q = query(collection(db, 'suggerimenti-prodotti'), orderBy('createdAt', 'desc'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    setSuggerimenti(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    const count = snapshot.docs.length;
    if (!isFirstLoad.current.suggerimenti && count > prevCounts.current.suggerimenti) {
      aggiungiNotifica('Nuovo Suggerimento', 'Un cliente ha suggerito un nuovo prodotto!', '💡');
    }
    if (isFirstLoad.current.suggerimenti) isFirstLoad.current.suggerimenti = false;
    prevCounts.current.suggerimenti = count;
  });
  return () => unsubscribe();
}, [isLoggedIn]);

// Listen to segnalazioni
useEffect(() => {
  if (!isLoggedIn) return;
  const q = query(collection(db, 'segnalazioni'), orderBy('createdAt', 'desc'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const sCount = snapshot.docs.length;
    if (!isFirstLoad.current.segnalazioni && sCount > prevCounts.current.segnalazioni) {
      aggiungiNotifica('Nuova Segnalazione', 'Un cliente ha inviato una segnalazione!', '⚠️');
    }
    if (isFirstLoad.current.segnalazioni) isFirstLoad.current.segnalazioni = false;
    prevCounts.current.segnalazioni = sCount;
  });
  return () => unsubscribe();
}, [isLoggedIn]);

// Listen to foto rider pending
useEffect(() => {
  if (!isLoggedIn) return;
  const q = query(collection(db, 'riders'), where('photoStatus', '==', 'pending'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    setRidersFotoPending(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
  return () => unsubscribe();
}, [isLoggedIn]);

// Listen to support tickets
useEffect(() => {
  if (!isLoggedIn) return;

  const q = query(collection(db, 'support-tickets'), orderBy('createdAt', 'desc'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedTickets = [];
    snapshot.forEach((doc) => {
      fetchedTickets.push({ id: doc.id, ...doc.data() });
    });

    // Notifica per nuovi messaggi rider su qualsiasi ticket
    snapshot.docChanges().forEach(change => {
      if (change.type === 'modified') {
        const ticket = { id: change.doc.id, ...change.doc.data() };
        if (ticket.lastMessage && ticket.status !== 'closed') {
          if (Notification.permission === 'granted') {
            new Notification(`Nuovo messaggio da ${ticket.riderName} 🛵`, {
              body: ticket.lastMessage,
              icon: '/logo192.png'
            });
          }
        }
      }
    });

    setTickets(fetchedTickets);
  });

  return () => unsubscribe();
}, [isLoggedIn]);

  // Login Handler
  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('Inserisci email e password');
      return;
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const tokenResult = await userCredential.user.getIdTokenResult();
      if (tokenResult.claims.admin !== true) {
        await signOut(auth);
        setLoginError('Account non autorizzato come admin');
        return;
      }
      // onAuthStateChanged gestirà il resto
    } catch (error) {
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        setLoginError('Email o password errati');
      } else {
        setLoginError('Errore durante il login');
      }
    }
  };

  // Verifica PIN tramite Cloud Function
  const handleVerificaPin = async () => {
    if (pinInput.length !== 6) {
      setPinError('Inserisci 6 cifre');
      return;
    }
    setPinLoading(true);
    setPinError('');
    try {
      const functions = getFunctions();
      const verificaAdminPin = httpsCallable(functions, 'verificaAdminPin');
      const result = await verificaAdminPin({ pin: pinInput });
      if (result.data.success) {
        setAuthStep('dashboard');
        if (result.data.primoAccesso) {
          alert('✅ PIN impostato! Ricordatelo — non è recuperabile senza accesso Firebase.');
        }
      } else {
        setPinError('PIN errato. Riprova.');
        setPinInput('');
      }
    } catch (error) {
      setPinError('Errore verifica PIN. Riprova.');
      console.error(error);
    } finally {
      setPinLoading(false);
    }
  };

  // Cambia PIN
  const handleCambiaPin = async (nuovoPin) => {
    if (!/^\d{6}$/.test(nuovoPin)) {
      alert('Il PIN deve essere esattamente 6 cifre numeriche');
      return;
    }
    try {
      const functions = getFunctions();
      const setAdminPin = httpsCallable(functions, 'setAdminPin');
      await setAdminPin({ pin: nuovoPin });
      alert('✅ PIN aggiornato con successo!');
      setShowCambiaPinModal(false);
    } catch (error) {
      alert('❌ Errore aggiornamento PIN: ' + error.message);
    }
  };

  // Logout
  const handleLogout = async () => {
    await signOut(auth);
    setIsLoggedIn(false);
    setAuthStep('login');
    setPinInput('');
    setPinError('');
  };

  // Modal Cambia PIN — definito dentro il componente per accedere a handleCambiaPin e setShowCambiaPinModal
  const CambiaPinModalInline = () => {
    const [nuovoPin, setNuovoPin] = React.useState('');
    const [confermaPin, setConfermaPin] = React.useState('');
    const [errore, setErrore] = React.useState('');

    const handleSalva = async () => {
      if (!/^\d{6}$/.test(nuovoPin)) { setErrore('Il PIN deve essere 6 cifre numeriche'); return; }
      if (nuovoPin !== confermaPin) { setErrore('I PIN non coincidono'); return; }
      await handleCambiaPin(nuovoPin);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-5 text-white">
            <h2 className="text-xl font-bold">🔐 Cambia PIN Admin</h2>
            <p className="text-purple-100 text-sm mt-1">Il PIN viene salvato cifrato su Firebase</p>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Nuovo PIN (6 cifre)</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={nuovoPin}
                onChange={e => { setNuovoPin(e.target.value.replace(/\D/g,'')); setErrore(''); }}
                placeholder="••••••"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none text-center text-2xl tracking-widest"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Conferma PIN</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={confermaPin}
                onChange={e => { setConfermaPin(e.target.value.replace(/\D/g,'')); setErrore(''); }}
                placeholder="••••••"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none text-center text-2xl tracking-widest"
              />
            </div>
            {errore && <p className="text-red-600 text-sm font-semibold">⚠️ {errore}</p>}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button onClick={() => setShowCambiaPinModal(false)} className="py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition">
                Annulla
              </button>
              <button onClick={handleSalva} className="py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:from-purple-700 hover:to-pink-700 transition">
                Salva PIN
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Delete Rider
  const handleDeleteRider = async (riderId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo rider?')) return;
    
    try {
      await deleteDoc(doc(db, 'candidature-rider', riderId));
      alert('✅ Rider eliminato con successo!');
    } catch (error) {
      console.error('Errore:', error);
      alert('❌ Errore durante l\'eliminazione');
    }
  };

  // Delete Pharmacy
const handleDeletePharmacy = async (pharmacyId) => {
  if (!window.confirm('Sei sicuro di voler eliminare questa farmacia?')) return;
  
  try {
    await deleteDoc(doc(db, 'farmacie', pharmacyId));
    alert('✅ Farmacia eliminata con successo!');
  } catch (error) {
    console.error('Errore:', error);
    alert('❌ Errore durante l\'eliminazione');
  }
};

  // ── SCHERMATA LOGIN ──────────────────────────────────────────────
  if (authStep === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-md border-4 border-purple-500">
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 transform rotate-3">
              <div className="text-white text-5xl font-bold">⚡</div>
            </div>
            <h1 className="text-4xl font-black text-gray-900 mb-2">Admin Panel</h1>
            <p className="text-purple-600 font-semibold">FarmaciaSubito Control Center</p>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email Admin</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="admin@farmaciasubito.it"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-200 outline-none transition"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-200 outline-none transition"
              />
            </div>
            {loginError && (
              <div className="bg-red-50 border-2 border-red-300 text-red-700 px-4 py-3 rounded-xl text-sm font-semibold">
                ⚠️ {loginError}
              </div>
            )}
            <button
              onClick={handleLogin}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-black text-lg hover:from-purple-700 hover:to-pink-700 transition shadow-lg transform hover:scale-105"
            >
              ACCEDI AL PANNELLO
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── SCHERMATA PIN ─────────────────────────────────────────────
  if (authStep === 'pin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-10 w-full max-w-sm border-4 border-purple-500">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-4xl">🔐</span>
            </div>
            <h2 className="text-2xl font-black text-gray-900">Verifica PIN</h2>
            <p className="text-gray-500 text-sm mt-1">Inserisci il PIN a 6 cifre</p>
          </div>

          {/* Display cifre PIN */}
          <div className="flex justify-center gap-3 mb-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`w-10 h-12 rounded-xl border-2 flex items-center justify-center text-xl font-black transition ${
                pinInput.length > i ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 bg-gray-50'
              }`}>
                {pinInput.length > i ? '●' : ''}
              </div>
            ))}
          </div>

          {/* Tastierino numerico */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
              <button
                key={i}
                onClick={() => {
                  if (k === '⌫') {
                    setPinInput(prev => prev.slice(0, -1));
                    setPinError('');
                  } else if (k !== '' && pinInput.length < 6) {
                    const newPin = pinInput + String(k);
                    setPinInput(newPin);
                    setPinError('');
                    if (newPin.length === 6) {
                      // Auto-submit quando 6 cifre
                      setTimeout(() => {
                        document.getElementById('btn-verifica-pin')?.click();
                      }, 100);
                    }
                  }
                }}
                className={`py-4 rounded-xl font-black text-lg transition ${
                  k === '' ? 'invisible' :
                  k === '⌫' ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' :
                  'bg-gray-50 text-gray-900 hover:bg-purple-50 hover:text-purple-700 border-2 border-gray-100 hover:border-purple-200'
                }`}
              >
                {k}
              </button>
            ))}
          </div>

          {pinError && (
            <div className="bg-red-50 border-2 border-red-300 text-red-700 px-4 py-3 rounded-xl text-sm font-semibold text-center mb-4">
              ❌ {pinError}
            </div>
          )}

          <button
            id="btn-verifica-pin"
            onClick={handleVerificaPin}
            disabled={pinLoading || pinInput.length !== 6}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-black text-lg hover:from-purple-700 hover:to-pink-700 transition disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-400 flex items-center justify-center gap-2"
          >
            {pinLoading ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> Verifica...</> : 'CONFERMA PIN'}
          </button>

          <button
            onClick={handleLogout}
            className="w-full mt-3 py-2 text-sm text-gray-400 hover:text-gray-600 transition"
          >
            ← Torna al login
          </button>
        </div>
      </div>
    );
  }

  // DASHBOARD VIEW
  const DashboardView = () => {
    // Grafico ultimi 7 giorni — dati REALI
    const chartData = React.useMemo(() => {
      const giorni = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
      const result = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        const ordiniGiorno = orders.filter(o => o.createdAt?.startsWith(dateStr));
        result.push({
          name: giorni[d.getDay()],
          ordini: ordiniGiorno.length,
          // P2: fatturato del grafico solo su ordini consegnati
          fatturato: ordiniGiorno
            .filter(o => o.status === 'delivered')
            .reduce((sum, o) => sum + (o.total || 0), 0)
        });
      }
      return result;
    }, [orders]);

    // Calcola % vs mese scorso — dati REALI
    const now = new Date();
    const inizioMeseCorrente = new Date(now.getFullYear(), now.getMonth(), 1);
    const inizioMesePrecedente = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const fineMesePrecedente = new Date(now.getFullYear(), now.getMonth(), 0);

    const ordiniMeseCorrente = orders.filter(o => new Date(o.createdAt) >= inizioMeseCorrente);
    const ordiniMesePrecedente = orders.filter(o => {
      const d = new Date(o.createdAt);
      return d >= inizioMesePrecedente && d <= fineMesePrecedente;
    });

    const calcPercent = (curr, prev) => {
      if (prev === 0) return curr > 0 ? '+100%' : '0%';
      const p = ((curr - prev) / prev * 100).toFixed(0);
      return p >= 0 ? `+${p}%` : `${p}%`;
    };

    const percentOrdini = calcPercent(ordiniMeseCorrente.length, ordiniMesePrecedente.length);
    const percentFatturato = calcPercent(
      ordiniMeseCorrente.filter(o => o.status === 'delivered').reduce((s, o) => s + (o.total || 0), 0),
      ordiniMesePrecedente.filter(o => o.status === 'delivered').reduce((s, o) => s + (o.total || 0), 0)
    );

    const statusData = [
      { name: 'Consegnati', value: orders.filter(o => o.status === 'delivered').length },
      { name: 'In consegna', value: orders.filter(o => o.status === 'delivering').length },
      { name: 'Pronti', value: orders.filter(o => o.status === 'ready').length },
      { name: 'Preparazione', value: orders.filter(o => o.status === 'preparing').length }
    ];

    return (
      <div className="space-y-6">
        {/* Top Stats */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-green-100 text-sm font-semibold">Fatturato Totale</p>
                <p className="text-4xl font-black">€{stats.totalRevenue.toFixed(0)}</p>
              </div>
              <DollarSign size={48} className="text-green-300 opacity-50" />
            </div>
            <div className="flex items-center gap-2 text-green-100">
              <ArrowUpRight size={16} />
              <span className="text-sm font-semibold">{percentFatturato} vs mese scorso</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-blue-100 text-sm font-semibold">Ordini Totali</p>
                <p className="text-4xl font-black">{stats.totalOrders}</p>
              </div>
              <Package size={48} className="text-blue-300 opacity-50" />
            </div>
            <div className="flex items-center gap-2 text-blue-100">
              <ArrowUpRight size={16} />
              <span className="text-sm font-semibold">{percentOrdini} vs mese scorso</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-purple-100 text-sm font-semibold">Rider Attivi</p>
                <p className="text-4xl font-black">{stats.activeRiders}</p>
              </div>
              <Bike size={48} className="text-purple-300 opacity-50" />
            </div>
            <div className="flex items-center gap-2 text-purple-100">
              <Activity size={16} />
              <span className="text-sm font-semibold">Online adesso</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 text-white shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-orange-100 text-sm font-semibold">Farmacie Attive</p>
                <p className="text-4xl font-black">{stats.activePharmacies}</p>
              </div>
              <Store size={48} className="text-orange-300 opacity-50" />
            </div>
            <div className="flex items-center gap-2 text-orange-100">
              <CheckCircle size={16} />
              <span className="text-sm font-semibold">Tutte operative</span>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
            <h3 className="text-xl font-black text-gray-800 mb-4">📈 Andamento Settimanale</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="ordini" stroke="#8b5cf6" strokeWidth={3} />
                <Line type="monotone" dataKey="fatturato" stroke="#10b981" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
            <h3 className="text-xl font-black text-gray-800 mb-4">📊 Stato Ordini</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RePieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold">Valore Medio Ordine</p>
                <p className="text-2xl font-black text-gray-900">€{stats.avgOrderValue.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold">Tasso Completamento</p>
                <p className="text-2xl font-black text-gray-900">{stats.completionRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold">Ordini Oggi</p>
                <p className="text-2xl font-black text-gray-900">{stats.todayOrders}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-semibold">Fatturato Oggi</p>
                <p className="text-2xl font-black text-gray-900">€{stats.todayRevenue.toFixed(0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <h3 className="text-xl font-black text-gray-800 mb-4">📦 Ordini Recenti</h3>
          <div className="space-y-3">
            {orders.slice(0, 5).map(order => (
              <div key={order.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    order.status === 'delivered' ? 'bg-green-100' :
                    order.status === 'delivering' ? 'bg-purple-100' :
                    order.status === 'ready' ? 'bg-blue-100' : 'bg-orange-100'
                  }`}>
                    <Package className={
                      order.status === 'delivered' ? 'text-green-600' :
                      order.status === 'delivering' ? 'text-purple-600' :
                      order.status === 'ready' ? 'text-blue-600' : 'text-orange-600'
                    } size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">#{order.id.substring(0, 8)}</p>
                    <p className="text-sm text-gray-600">{order.userEmail}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-black text-gray-900">€{order.total?.toFixed(2)}</p>
                    <p className="text-sm text-gray-500">{new Date(order.createdAt).toLocaleTimeString('it-IT')}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-lg font-bold text-sm ${
                    order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    order.status === 'delivering' ? 'bg-purple-100 text-purple-700' :
                    order.status === 'ready' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ORDERS VIEW
  const OrdersView = () => {
    const filteredOrders = orders.filter(o => 
      o.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      o.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black text-gray-900">Tutti gli Ordini</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Cerca ordini..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-200 outline-none"
                />
              </div>
              <button className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-purple-700 transition flex items-center gap-2">
                <Download size={20} />
                Export CSV
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {filteredOrders.map(order => (
              <div key={order.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-black text-gray-900">#{order.id.substring(0, 8)}</p>
                      <span className={`px-3 py-1 rounded-lg font-bold text-xs ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'delivering' ? 'bg-purple-100 text-purple-700' :
                        order.status === 'in_revisione_admin' ? 'bg-orange-100 text-orange-700' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        order.status === 'ready' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {order.status === 'in_revisione_admin' ? 'in revisione' : order.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {order.userEmail}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {order.address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {new Date(order.createdAt).toLocaleString('it-IT')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-black text-gray-900">€{order.total?.toFixed(2)}</p>
                      <p className="text-sm text-gray-500">{order.items?.length} prodotti</p>
                    </div>
                    <button
                      onClick={() => { setSelectedOrder(order); setShowModal(true); }}
                      className="p-3 bg-purple-100 hover:bg-purple-200 rounded-xl transition"
                    >
                      <Eye className="text-purple-600" size={20} />
                    </button>
                    {['ready', 'accepted', 'preparing'].includes(order.status) && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Annullare l'ordine #${order.id.substring(0,8)}?\n\nSe pagato con carta verrà rimborsato automaticamente.`)) return;
                          try {
                            await updateDoc(doc(db, 'orders', order.id), {
                              status: 'cancelled',
                              cancelledAt: new Date().toISOString(),
                              cancelReason: 'Annullato dall\'Admin',
                              cancelledBy: 'admin'
                            });
                            alert('✅ Ordine annullato. Se pagato con carta il rimborso è automatico.');
                          } catch(e) {
                            alert('❌ Errore: ' + e.message);
                          }
                        }}
                        className="p-3 bg-red-100 hover:bg-red-200 rounded-xl transition"
                        title="Annulla ordine"
                      >
                        <X className="text-red-600" size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ── P1: REVISIONE & RIMBORSI ────────────────────────────────────────────────
  const RevisioneView = () => {
    const ordiniRevisione = orders.filter(o => o.status === 'in_revisione_admin');
    // Storico: ordini che SONO STATI in revisione (hanno segnalatoAt) ma ora
    // sono risolti (stato diverso da in_revisione_admin).
    const ordiniStorico = orders.filter(o =>
      o.segnalatoAt && o.status !== 'in_revisione_admin'
    ).sort((a, b) => new Date(b.segnalatoAt) - new Date(a.segnalatoAt));
    const [importiParziali, setImportiParziali] = React.useState({});
    const [loadingId, setLoadingId] = React.useState(null);

    const chiamaRevisione = async (orderId, azione, rimborsoImporto, note, paymentMethod) => {
      setLoadingId(orderId + azione);
      try {
        const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/gestisciRevisioneOrdine', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId, azione, rimborsoImporto, note })
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Errore');
        if (azione !== 'rimborso') {
          alert('✅ Ordine rimesso in consegna.');
        } else if (paymentMethod === 'cash') {
          alert('✅ Ordine annullato. Pagamento in contanti: nessun rimborso dovuto.');
        } else {
          alert('✅ Rimborso avviato. Il cliente riceverà l\'accredito entro 5-10 giorni lavorativi.');
        }
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setLoadingId(null);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={26} />
              Ordini in Revisione
            </h2>
            <span className="bg-orange-100 text-orange-700 px-4 py-2 rounded-xl font-black">
              {ordiniRevisione.length} in attesa
            </span>
          </div>

          {ordiniRevisione.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="text-green-400 mx-auto mb-3" size={48} />
              <p className="text-gray-500 font-bold">Nessun ordine in revisione. Tutto in regola!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ordiniRevisione.map(order => {
                const mancanti = order.prodottiNonDisponibiliDettaglio || [];
                const idsMancanti = order.prodottiNonDisponibili || [];
                const disponibili = (order.items || []).filter(i => !idsMancanti.includes(i.id || i.name));
                const totaleDisponibili = disponibili.reduce((s, i) => s + (i.price * i.quantity), 0);
                const importoParziale = importiParziali[order.id] ?? '';

                return (
                  <div key={order.id} className="p-5 bg-orange-50 rounded-2xl border-2 border-orange-200">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-black text-gray-900 text-lg">#{order.id.substring(0, 8)}</p>
                        <p className="text-sm text-gray-600">{order.customerName} — {order.userEmail}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Segnalato da {order.segnalatoRiderName || 'rider'} il {order.segnalatoAt ? new Date(order.segnalatoAt).toLocaleString('it-IT') : '—'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-black text-gray-900">€{order.total?.toFixed(2)}</p>
                        <span className="text-xs font-bold px-2 py-1 rounded bg-gray-200 text-gray-700">
                          {order.paymentMethod === 'cash' ? '💵 Contanti' : '💳 Carta'}
                        </span>
                      </div>
                    </div>

                    {/* Prodotti non disponibili */}
                    <div className="grid md:grid-cols-2 gap-3 mb-4">
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                        <p className="text-xs font-black text-red-700 mb-2">❌ NON DISPONIBILI</p>
                        {mancanti.length > 0 ? mancanti.map((p, i) => (
                          <p key={i} className="text-sm text-gray-800">• {p.name} ×{p.quantity} — €{(p.price * p.quantity).toFixed(2)}</p>
                        )) : <p className="text-sm text-gray-500">{idsMancanti.join(', ') || '—'}</p>}
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                        <p className="text-xs font-black text-green-700 mb-2">✅ DISPONIBILI (€{totaleDisponibili.toFixed(2)})</p>
                        {disponibili.length > 0 ? disponibili.map((p, i) => (
                          <p key={i} className="text-sm text-gray-800">• {p.name} ×{p.quantity} — €{(p.price * p.quantity).toFixed(2)}</p>
                        )) : <p className="text-sm text-gray-500">Nessuno</p>}
                      </div>
                    </div>

                    {/* Azioni Admin */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        disabled={loadingId === order.id + 'rimborso'}
                        onClick={() => {
                          if (order.paymentMethod === 'cash') {
                            if (!window.confirm(`Annullare l'ordine #${order.id.substring(0,8)}?\n\nPagamento in contanti: nessun rimborso dovuto, il cliente non ha pagato.`)) return;
                            chiamaRevisione(order.id, 'rimborso', null, 'Ordine annullato in revisione (contanti, nessun rimborso)', 'cash');
                          } else {
                            if (!window.confirm(`Rimborso TOTALE di €${order.total?.toFixed(2)} al cliente?`)) return;
                            chiamaRevisione(order.id, 'rimborso', null, 'Rimborso totale da revisione');
                          }
                        }}
                        className="bg-red-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition disabled:bg-gray-400 text-sm"
                      >
                        {order.paymentMethod === 'cash' ? '🚫 Annulla ordine' : '💳 Rimborso totale'}
                      </button>

                      {order.paymentMethod !== 'cash' && (
                        <div className="flex items-center gap-1 bg-white border-2 border-orange-300 rounded-xl p-1">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="€ parziale"
                            value={importoParziale}
                            onChange={e => setImportiParziali(prev => ({ ...prev, [order.id]: e.target.value }))}
                            className="w-24 px-2 py-1 text-sm outline-none"
                          />
                          <button
                            disabled={loadingId === order.id + 'rimborso'}
                            onClick={() => {
                              const imp = parseFloat(importoParziale);
                              if (!imp || imp <= 0) { alert('Inserisci un importo valido'); return; }
                              if (imp >= (order.total || 0)) { alert('Per il totale usa "Rimborso totale"'); return; }
                              if (!window.confirm(`Rimborso PARZIALE di €${imp.toFixed(2)} al cliente?`)) return;
                              chiamaRevisione(order.id, 'rimborso', imp, 'Rimborso parziale da revisione');
                            }}
                            className="bg-orange-500 text-white px-3 py-1 rounded-lg font-bold hover:bg-orange-600 transition text-sm"
                          >
                            Rimborsa
                          </button>
                        </div>
                      )}

                      <button
                        disabled={loadingId === order.id + 'prosegui'}
                        onClick={() => {
                          if (!window.confirm('Far consegnare i prodotti disponibili senza rimborso? L\'ordine torna in consegna.')) return;
                          chiamaRevisione(order.id, 'prosegui', null, 'Consegna prodotti disponibili');
                        }}
                        className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400 text-sm"
                      >
                        ✅ Consegna il resto
                      </button>

                      <button
                        onClick={() => { setSelectedOrder(order); setShowModal(true); }}
                        className="ml-auto p-2 bg-purple-100 hover:bg-purple-200 rounded-xl transition"
                        title="Dettagli ordine"
                      >
                        <Eye className="text-purple-600" size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* STORICO revisioni gestite */}
        {ordiniStorico.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
            <h2 className="text-xl font-black text-gray-900 flex items-center gap-2 mb-4">
              <Clock className="text-gray-400" size={22} />
              Storico revisioni gestite
            </h2>
            <div className="space-y-2">
              {ordiniStorico.map(order => {
                const esitoLabel =
                  order.status === 'cancelled'
                    ? (order.rimborsoImporto ? `Rimborso parziale €${order.rimborsoImporto.toFixed(2)}` : 'Rimborsato / Annullato')
                    : order.revisioneEsito === 'prosegui_consegna'
                      ? 'Consegna proseguita'
                      : order.status;
                return (
                  <div key={order.id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="min-w-0">
                      <p className="font-black text-gray-900 text-sm">#{order.id.substring(0, 8)} — {order.customerName}</p>
                      <p className="text-xs text-gray-500">
                        Segnalato il {order.segnalatoAt ? new Date(order.segnalatoAt).toLocaleString('it-IT') : '—'}
                        {order.prodottiNonDisponibili?.length ? ` · ${order.prodottiNonDisponibili.length} prod. mancanti` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700'
                          : order.status === 'delivered' ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {esitoLabel}
                      </span>
                      <button
                        onClick={() => { setSelectedOrder(order); setShowModal(true); }}
                        className="p-2 bg-purple-100 hover:bg-purple-200 rounded-lg transition"
                        title="Dettagli ordine"
                      >
                        <Eye className="text-purple-600" size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── P4: RIMBORSO RIDER (scontrino) ──────────────────────────────────────────
  const RimborsoRiderView = () => {
    const [filtro, setFiltro] = React.useState('da_rimborsare');
    const [loadingId, setLoadingId] = React.useState(null);

    const ordiniRimborso = orders.filter(o =>
      !o.isRicettaOrder &&
      o.rimborsoRiderStatus && o.rimborsoRiderStatus !== 'non_dovuto' &&
      (filtro === 'tutti' || o.rimborsoRiderStatus === filtro)
    );

    const segnaRimborsato = async (order) => {
      if (!window.confirm(`Confermi di aver rimborsato €${(order.rimborsoRiderImporto || order.farmaciAnticipati || 0).toFixed(2)} al rider ${order.riderName || ''}?\n\nIl bonifico va effettuato a parte: questa azione lo registra solo come "rimborsato".`)) return;
      setLoadingId(order.id);
      try {
        await updateDoc(doc(db, 'orders', order.id), {
          rimborsoRiderStatus: 'rimborsato',
          rimborsoRiderAt: new Date().toISOString(),
        });
        alert('✅ Segnato come rimborsato. Lo trovi ora nel filtro "Rimborsati".');
        setFiltro('rimborsato'); // sposta la vista dove è finito l'ordine
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setLoadingId(null);
      }
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
              <DollarSign className="text-green-600" size={26} />
              Rimborso Rider
            </h2>
            <div className="flex gap-2">
              {[
                { val: 'da_rimborsare', label: '⏳ Da rimborsare' },
                { val: 'rimborsato', label: '✅ Rimborsati' },
                { val: 'tutti', label: 'Tutti' },
              ].map(f => (
                <button key={f.val} onClick={() => setFiltro(f.val)}
                  className={`px-3 py-2 rounded-xl font-bold text-sm transition ${
                    filtro === f.val ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-blue-800">
            ℹ️ Il rider anticipa con carta personale e va rimborsato velocemente. Il bonifico è reale e fuori app: qui registri solo l'avvenuto rimborso. A fine mese si scalano solo i contanti incassati.
          </div>

          {ordiniRimborso.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="text-green-400 mx-auto mb-3" size={48} />
              <p className="text-gray-500 font-bold">Nessun ordine da rimborsare in questo filtro.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {ordiniRimborso.map(order => {
                const importo = order.rimborsoRiderImporto || order.farmaciAnticipati || 0;
                return (
                  <div key={order.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-black text-gray-900">#{order.id.substring(0, 8)}</p>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            order.rimborsoRiderStatus === 'rimborsato' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {order.rimborsoRiderStatus === 'rimborsato' ? '✅ Rimborsato' : '⏳ Da rimborsare'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 flex items-center gap-1">
                          <Bike size={14} /> {order.riderName || '—'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Consegnato il {order.deliveredAt ? new Date(order.deliveredAt).toLocaleString('it-IT') : '—'}
                          {order.rimborsoRiderAt && ` · Rimborsato il ${new Date(order.rimborsoRiderAt).toLocaleDateString('it-IT')}`}
                        </p>
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-gray-500">Anticipato</p>
                        <p className="text-xl font-black text-gray-900">€{importo.toFixed(2)}</p>
                      </div>

                      {order.scontrinoUrl ? (
                        <button
                          onClick={() => window.open(order.scontrinoUrl, '_blank')}
                          className="px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl font-bold text-sm flex items-center gap-1"
                        >
                          <FileText size={16} /> Scontrino
                        </button>
                      ) : (
                        <span className="px-3 py-2 bg-gray-100 text-gray-400 rounded-xl font-bold text-sm">
                          No scontrino
                        </span>
                      )}

                      {order.rimborsoRiderStatus === 'da_rimborsare' && (
                        <button
                          disabled={loadingId === order.id}
                          onClick={() => segnaRimborsato(order)}
                          className="px-4 py-2 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400 text-sm"
                        >
                          ✅ Rimborsa Rider
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // RIDERS VIEW
  const RidersView = () => {
    const [pendingRiders, setPendingRiders] = React.useState([]);
    const [approvedRiders, setApprovedRiders] = React.useState([]);

    React.useEffect(() => {
      const q = query(collection(db, 'riders'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setPendingRiders(all.filter(r => r.status === 'pending'));
        setApprovedRiders(all.filter(r => r.status === 'approved'));
      });
      return () => unsubscribe();
    }, []);

    const approveRider = async (rider) => {
  try {
    // 1. Approva rider su Firestore
    await updateDoc(doc(db, 'riders', rider.id), { status: 'approved' });

    // (Stripe Connect e carta Qonto rimossi: i rider ora pagano con carta personale
    //  e ricevono il compenso via bonifico manuale sul loro IBAN.)

    // 2. Invia email di benvenuto
    try {
      await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendApprovalEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          riderEmail: rider.email,
          riderName: rider.name || `${rider.nome} ${rider.cognome}`
        })
      });
    } catch (emailError) {
      console.warn('Email non inviata:', emailError);
    }

    alert(`✅ Rider ${rider.name} approvato!`);
  } catch (error) {
    console.error('Errore approvazione:', error);
    alert(`❌ Errore: ${error.message}`);
  }
};

    const rejectRider = async (rider) => {
      if (!window.confirm(`Vuoi rifiutare il rider ${rider.name}?`)) return;
      try {
        await updateDoc(doc(db, 'riders', rider.id), { status: 'rejected' });
        alert(`❌ Rider ${rider.name} rifiutato.`);
      } catch (error) {
        alert('Errore durante il rifiuto');
      }
    };

    const approvePhoto = async (rider) => {
      try {
        await updateDoc(doc(db, 'riders', rider.id), {
          photoUrl: rider.photoPending,
          photoStatus: 'approved',
          photoPending: null
        });
        alert(`✅ Foto di ${rider.name} approvata!`);
      } catch (error) {
        alert('Errore approvazione foto: ' + error.message);
      }
    };

    const rejectPhoto = async (rider) => {
      try {
        await updateDoc(doc(db, 'riders', rider.id), {
          photoStatus: 'rejected',
          photoPending: null
        });
        alert(`❌ Foto di ${rider.name} rifiutata.`);
      } catch (error) {
        alert('Errore rifiuto foto: ' + error.message);
      }
    };

    const forceOffline = async (rider) => {
      if (!window.confirm(`Vuoi mandare offline ${rider.name}?`)) return;
      try {
        const todayStr = new Date().toISOString().split('T')[0];
        const newShifts = { ...(rider.shifts || {}) };
        if (newShifts[todayStr]) {
          newShifts[todayStr] = { morning: false, afternoon: false };
        }
        await updateDoc(doc(db, 'riders', rider.id), {
          isOnline: false,
          shifts: newShifts
        });
        // Rimuovi anche dalla mappa live
        await updateDoc(doc(db, 'rider-positions', rider.id), {
          isActive: false
        }).catch(() => {}); // Ignora se non esiste
        alert(`✅ Rider ${rider.name} portato offline e turno rimosso!`);
      } catch (error) {
        alert('Errore durante l\'operazione');
      }
    };

    const ridersFotoInAttesa = [...pendingRiders, ...approvedRiders].filter(r => r.photoStatus === 'pending');

    return (
      <div className="space-y-6">

        {/* FOTO PROFILO IN ATTESA */}
        {ridersFotoInAttesa.length > 0 && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-6">
            <h3 className="text-lg font-black text-yellow-800 mb-4">📸 Foto Profilo in Attesa di Approvazione ({ridersFotoInAttesa.length})</h3>
            <div className="space-y-4">
              {ridersFotoInAttesa.map(rider => (
                <div key={rider.id} className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm">
                  <img src={rider.photoPending} alt={rider.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-yellow-300" />
                  <div className="flex-1">
                    <p className="font-black text-gray-900">{rider.name}</p>
                    <p className="text-xs font-bold text-gray-400 tracking-widest">ID: {rider.id}</p>
                    <p className="text-sm text-gray-500">{rider.email}</p>
                    <p className="text-xs text-yellow-600 mt-1">Richiesta: {rider.photoRequestedAt ? new Date(rider.photoRequestedAt).toLocaleDateString('it-IT') : 'N/D'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => approvePhoto(rider)}
                      className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-green-700 transition">
                      ✅ Approva
                    </button>
                    <button onClick={() => rejectPhoto(rider)}
                      className="bg-red-500 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-red-600 transition">
                      ❌ Rifiuta
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* RIDER IN ATTESA */}
        {pendingRiders.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-orange-200">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-black text-gray-900">Rider in Attesa</h2>
              <span className="bg-orange-500 text-white text-sm font-black px-3 py-1 rounded-full">
                {pendingRiders.length}
              </span>
            </div>
            <div className="space-y-3">
              {pendingRiders.map(rider => (
                <div key={rider.id} className="p-6 bg-orange-50 rounded-xl border-2 border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center text-white text-xl font-black">
                        {rider.name?.split(' ').map(n => n[0]).join('') || '?'}
                      </div>
                      <div>
                        <p className="font-black text-gray-900 text-lg">{rider.name}</p>
                        <p className="text-xs font-bold text-gray-400 tracking-widest">ID: {rider.id}</p>
                        <p className="text-sm text-gray-600">{rider.email}</p>
                        <p className="text-xs text-orange-600 font-bold mt-1">⏳ In attesa di approvazione</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap justify-end">
                      {/* Stato documenti */}
                      {!rider.documentiInviati ? (
                        <button
                          onClick={async () => {
                            try {
                              const link = `https://www.farmaciasubito.it/documenti-rider.html?id=${rider.id}`;
                              await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  to: rider.email,
                                  subject: '📄 Carica i tuoi documenti — FarmaciaSubito',
                                  html: `
                                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f0fdf4; padding: 20px; border-radius: 12px;">
                                      <div style="background: #16a34a; padding: 24px; border-radius: 8px; text-align: center; margin-bottom: 24px;">
                                        <h1 style="color: white; margin: 0; font-size: 24px;">🛵 FarmaciaSubito</h1>
                                        <p style="color: #bbf7d0; margin: 8px 0 0 0;">Portale Rider</p>
                                      </div>
                                      <h2 style="color: #15803d;">Ciao ${rider.name}! 📄</h2>
                                      <p style="color: #374151; line-height: 1.6;">
                                        Per completare la tua registrazione come rider FarmaciaSubito, devi caricare i seguenti documenti:
                                      </p>
                                      <ul style="color: #374151; line-height: 2;">
                                        <li>🪪 Carta d'Identità (fronte e retro)</li>
                                        <li>🚗 Patente di Guida (fronte e retro)</li>
                                        <li>💳 Tessera Sanitaria (fronte e retro)</li>
                                        <li>🤳 Selfie con documento in mano</li>
                                      </ul>
                                      <div style="text-align: center; margin: 32px 0;">
                                        <a href="${link}" 
                                           style="background: #16a34a; color: white; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px;">
                                          📤 Carica i tuoi documenti
                                        </a>
                                      </div>
                                      <div style="background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 14px; margin-bottom: 24px;">
                                        <p style="margin: 0; color: #92400e; font-size: 13px;">
                                          ⚠️ I documenti devono essere validi e non scaduti. Il link è personale e sicuro.
                                        </p>
                                      </div>
                                      <p style="color: #6b7280; font-size: 13px; text-align: center;">
                                        Per qualsiasi domanda, contattaci a <a href="mailto:info@farmaciasubito.it">info@farmaciasubito.it</a>
                                      </p>
                                      <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
                                        <p style="color: #9ca3af; font-size: 12px;">© 2026 FarmaciaSubito — Benevento, Italia</p>
                                      </div>
                                    </div>
                                  `
                                })
                              });
                              alert(`✅ Email inviata a ${rider.email} con il link per caricare i documenti!`);
                            } catch (error) {
                              alert(`❌ Errore: ${error.message}`);
                            }
                          }}
                          className="bg-blue-100 text-blue-600 px-4 py-2 rounded-xl font-bold hover:bg-blue-200 transition flex items-center gap-2"
                        >
                          📎 Richiedi Documenti
                        </button>
                      ) : rider.documentiStatus === 'in_revisione' ? (
                        <button
                          onClick={() => {
                            const docs = rider.documenti || {};
                            const msg = `📄 DOCUMENTI DI ${rider.name}\n\n` +
                              `🪪 CI Fronte: ${docs.cartaIdentitaFronte ? '✅' : '❌'}\n` +
                              `🪪 CI Retro: ${docs.cartaIdentitaRetro ? '✅' : '❌'}\n` +
                              `🚗 Patente Fronte: ${docs.patenteFronte ? '✅' : '❌'}\n` +
                              `🚗 Patente Retro: ${docs.patenteRetro ? '✅' : '❌'}\n` +
                              `💳 TS Fronte: ${docs.tesseraSanitariaFronte ? '✅' : '❌'}\n` +
                              `💳 TS Retro: ${docs.tesseraSanitariaRetro ? '✅' : '❌'}\n` +
                              `🤳 Selfie: ${docs.selfie ? '✅' : '❌'}\n\n` +
                              `Apri i link:\n` +
                              Object.entries(docs).map(([k, v]) => `${k}: ${v}`).join('\n');
                            alert(msg);
                            // Apri tutti i documenti
                            const docsToOpen = [
                              docs.cartaIdentitaFronte,
                              docs.cartaIdentitaRetro,
                              docs.patenteFronte,
                              docs.patenteRetro,
                              docs.tesseraSanitariaFronte,
                              docs.tesseraSanitariaRetro,
                              docs.selfie
                            ].filter(Boolean);
                            docsToOpen.forEach((url, idx) => {
                              setTimeout(() => window.open(url, '_blank'), idx * 500);
                            });
                          }}
                          className="bg-purple-100 text-purple-600 px-4 py-2 rounded-xl font-bold hover:bg-purple-200 transition flex items-center gap-2"
                        >
                          📄 Vedi Documenti
                        </button>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full">
                          ✅ Documenti verificati
                        </span>
                      )}
                      {/* CONTRATTO */}
                      {rider.documentiInviati && !rider.contrattoFirmato && (
                        <button
                          onClick={async () => {
                            try {
                              const link = `https://www.farmaciasubito.it/firma-contratto.html?id=${rider.id}`;
                              await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  to: rider.email,
                                  subject: '📝 Firma il tuo contratto — FarmaciaSubito',
                                  html: `
                                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f0fdf4; padding: 20px; border-radius: 12px;">
                                      <div style="background: #16a34a; padding: 24px; border-radius: 8px; text-align: center; margin-bottom: 24px;">
                                        <h1 style="color: white; margin: 0; font-size: 24px;">🛵 FarmaciaSubito</h1>
                                        <p style="color: #bbf7d0; margin: 8px 0 0 0;">Portale Rider</p>
                                      </div>
                                      <h2 style="color: #15803d;">Ciao ${rider.name}! 📝</h2>
                                      <p style="color: #374151; line-height: 1.6;">I tuoi documenti sono stati verificati con successo! ✅</p>
                                      <p style="color: #374151; line-height: 1.6;">Ora devi firmare il contratto di collaborazione per completare la registrazione come rider FarmaciaSubito.</p>
                                      <div style="text-align: center; margin: 32px 0;">
                                        <a href="${link}" style="background: #16a34a; color: white; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px;">
                                          📝 Firma il Contratto
                                        </a>
                                      </div>
                                      <div style="background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 14px; margin-bottom: 24px;">
                                        <p style="margin: 0; color: #92400e; font-size: 13px;">⚠️ Leggi attentamente il contratto prima di firmarlo. La firma è vincolante.</p>
                                      </div>
                                      <p style="color: #6b7280; font-size: 13px; text-align: center;">Per qualsiasi domanda: <a href="mailto:info@farmaciasubito.it">info@farmaciasubito.it</a></p>
                                      <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
                                        <p style="color: #9ca3af; font-size: 12px;">© 2026 FarmaciaSubito — Benevento, Italia</p>
                                      </div>
                                    </div>
                                  `
                                })
                              });
                              alert(`✅ Email inviata a ${rider.email} con il link per firmare il contratto!`);
                            } catch (error) {
                              alert(`❌ Errore: ${error.message}`);
                            }
                          }}
                          className="bg-green-100 text-green-700 px-4 py-2 rounded-xl font-bold hover:bg-green-200 transition flex items-center gap-2"
                        >
                          📝 Invia Contratto
                        </button>
                      )}

                      {rider.contrattoFirmato && (
  <button
    onClick={() => {
      setSelectedContratto({
  name: rider.name,
  email: rider.email,
  firmaUrl: rider.contrattoFirmaUrl,
  ...(rider.contrattoData || {})
});
      setShowContrattoModal(true);
    }}
    className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-xl font-bold hover:bg-emerald-200 transition flex items-center gap-2"
  >
    ✅ Vedi Contratto
  </button>
)}

                      <button
                        onClick={() => approveRider(rider)}
                        className={`px-4 py-2 rounded-xl font-bold transition flex items-center gap-2 ${
                          rider.documentiInviati && rider.contrattoFirmato
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                        disabled={!rider.documentiInviati || !rider.contrattoFirmato}
                        title={!rider.documentiInviati ? 'Richiedi prima i documenti' : !rider.contrattoFirmato ? 'Attendi firma contratto' : 'Approva rider'}
                      >
                        <Check size={16} />
                        Approva
                      </button>
                      <button
                        onClick={() => rejectRider(rider)}
                        className="bg-red-100 text-red-600 px-4 py-2 rounded-xl font-bold hover:bg-red-200 transition flex items-center gap-2"
                      >
                        <X size={16} />
                        Rifiuta
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIDER APPROVATI */}
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black text-gray-900">Rider Attivi</h2>
          </div>
          <div className="space-y-3">
            {approvedRiders.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Nessun rider approvato</p>
            ) : (
              approvedRiders.map(rider => (
                <div key={rider.id} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white text-xl font-black">
                        {rider.name?.split(' ').map(n => n[0]).join('') || '?'}
                      </div>
                      <div>
                        <p className="font-black text-gray-900 text-lg">{rider.name}</p>
                        <p className="text-xs font-bold text-gray-400 tracking-widest">ID: {rider.id}</p>
                        <p className="text-sm text-gray-600">{rider.email}</p>
                        <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-0.5 rounded-full">✅ Attivo</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {rider.iban ? (
                        <span className="text-xs bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full font-mono">
                          🏦 {rider.iban}
                        </span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-600 font-bold px-3 py-1 rounded-full">
                          ⚠️ IBAN mancante
                        </span>
                      )}
                      <button
                        onClick={() => forceOffline(rider)}
                        className="bg-orange-100 text-orange-600 px-4 py-2 rounded-xl font-bold hover:bg-orange-200 transition flex items-center gap-2"
                      >
                        <Power size={16} />
                        Forza Offline
                      </button>
                      <button
                        onClick={() => rejectRider(rider)}
                        className="p-3 bg-red-100 hover:bg-red-200 rounded-xl transition"
                      >
                        <Trash2 className="text-red-600" size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // PHARMACIES VIEW
const PharmaciesView = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-black text-gray-900">Gestione Farmacie</h2>
        <button 
          onClick={() => { setSelectedPharmacy(null); setShowPharmacyModal(true); }}
          className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-purple-700 transition flex items-center gap-2"
        >
          <Plus size={20} />
          Nuova Farmacia
        </button>
      </div>

      <div className="space-y-3">
        {pharmacies.map(pharmacy => (
          <div key={pharmacy.id} className="p-6 bg-gray-50 rounded-xl border-2 border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center text-white text-3xl">
                  +
                </div>
                <div>
                  <p className="font-black text-gray-900 text-lg">{pharmacy.name}</p>
                  <p className="text-sm text-gray-600">{pharmacy.email}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span className="flex items-center gap-1">
                      <Package size={14} />
                      {pharmacy.orders || 0} ordini
                    </span>
                    <span className="flex items-center gap-1 text-green-600 font-bold">
                      <DollarSign size={14} />
                      €{(pharmacy.revenue || 0).toFixed(2)}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg font-bold">
                      Commissione: {pharmacy.commission || 10}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-lg font-bold ${
                  pharmacy.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {pharmacy.status === 'active' ? 'Attiva' : 'Inattiva'}
                </span>
                <button 
                  onClick={() => { setSelectedPharmacy(pharmacy); setShowPharmacyModal(true); }}
                  className="p-3 bg-blue-100 hover:bg-blue-200 rounded-xl transition"
                >
                  <Edit className="text-blue-600" size={20} />
                </button>
                <button 
                  onClick={() => handleDeletePharmacy(pharmacy.id)}
                  className="p-3 bg-red-100 hover:bg-red-200 rounded-xl transition"
                >
                  <Trash2 className="text-red-600" size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// CANDIDATURE VIEW
  const CandidatureView = () => {
    const filteredCandidature = candidature.filter(c => 
      c.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.cognome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleApprove = async (id) => {
      try {
        const candidatura = candidature.find(c => c.id === id);
        if (!candidatura) return;

        // Approva su Firestore
        await updateDoc(doc(db, 'candidature-rider', id), { status: 'approvato' });

        // Crea documento in 'riders' così appare nella sezione Rider.
        // NB: uid resta null finché il rider non completa la registrazione
        // sull'app (il collegamento avviene lì tramite email).
        await setDoc(doc(db, 'riders', id), {
          name: `${candidatura.nome} ${candidatura.cognome}`,
          nome: candidatura.nome,
          cognome: candidatura.cognome,
          email: (candidatura.email || '').trim().toLowerCase(),
          telefono: candidatura.telefono || '',
          mezzoTrasporto: candidatura.mezzoTrasporto || '',
          status: 'pending',
          uid: null,
          isOnline: false,
          ratingMedia: 0,
          ratingCount: 0,
          createdAt: new Date().toISOString()
        }, { merge: true });

        // Invia email al rider con link registrazione
        await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: candidatura.email,
            subject: '🎉 Candidatura approvata — Registrati su FarmaciaSubito!',
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f0fdf4; padding: 20px; border-radius: 12px;">
                <div style="background: #16a34a; padding: 24px; border-radius: 8px; text-align: center; margin-bottom: 24px;">
                  <h1 style="color: white; margin: 0; font-size: 24px;">🛵 FarmaciaSubito</h1>
                  <p style="color: #bbf7d0; margin: 8px 0 0 0;">Portale Rider</p>
                </div>
                
                <h2 style="color: #15803d;">Ciao ${candidatura.nome}! 🎉</h2>
                <p style="color: #374151; line-height: 1.6;">
                  La tua candidatura come rider FarmaciaSubito è stata <strong>approvata</strong>!
                </p>
                <p style="color: #374151; line-height: 1.6;">
                  Per iniziare, registrati sull'App Rider cliccando il pulsante qui sotto:
                </p>
                
                <div style="text-align: center; margin: 32px 0;">
                  <a href="https://rider.farmaciasubito.it" 
                     style="background: #16a34a; color: white; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px;">
                    🚀 Registrati sull'App Rider
                  </a>
                </div>
                
                <div style="background: white; border-left: 4px solid #16a34a; padding: 16px; border-radius: 4px; margin-bottom: 24px;">
                  <p style="margin: 0; color: #374151; font-size: 14px;">
                    <strong>📋 Prossimi passi:</strong><br/>
                    1. Registrati con questa email: <strong>${candidatura.email}</strong><br/>
                    2. Attendi la conferma di attivazione account<br/>
                    3. Carica i tuoi documenti quando richiesto<br/>
                    4. Inizia a consegnare! 🛵
                  </p>
                </div>
                
                <p style="color: #6b7280; font-size: 13px; text-align: center;">
                  Per qualsiasi domanda, contattaci a <a href="mailto:info@farmaciasubito.it">info@farmaciasubito.it</a>
                </p>
                
                <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
                  <p style="color: #9ca3af; font-size: 12px;">© 2026 FarmaciaSubito — Benevento, Italia</p>
                </div>
              </div>
            `
          })
        });

        alert(`✅ Candidatura approvata!\n\n📧 Email inviata a ${candidatura.email} con il link per registrarsi.`);
      } catch (error) {
        console.error('Errore approvazione:', error);
        alert(`❌ Errore: ${error.message}`);
      }
    };

    const handleReject = async (id) => {
      await updateDoc(doc(db, 'candidature-rider', id), { status: 'rifiutato' });
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900">📋 Candidature Rider</h2>
              <p className="text-gray-600">Gestisci le richieste di diventare rider</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Cerca candidature..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-4 focus:ring-purple-200 outline-none"
                />
              </div>
              {stats.newCandidature > 0 && (
                <div className="px-4 py-2 bg-red-100 text-red-700 rounded-xl font-bold">
                  🆕 {stats.newCandidature} nuove
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {filteredCandidature.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <UserPlus size={48} className="mx-auto mb-4 opacity-50" />
                <p className="font-semibold">Nessuna candidatura trovata</p>
              </div>
            ) : (
              filteredCandidature.map(candidatura => (
                <div key={candidatura.id} className={`p-6 rounded-xl border-2 transition ${
                  candidatura.status === 'nuovo' ? 'bg-blue-50 border-blue-300' :
                  candidatura.status === 'approvato' ? 'bg-green-50 border-green-300' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-black text-lg">
                          {candidatura.nome?.[0]}{candidatura.cognome?.[0]}
                        </div>
                        <div>
                          <p className="font-black text-gray-900 text-lg">
                            {candidatura.nome} {candidatura.cognome}
                          </p>
                          <p className="text-sm text-gray-600">{candidatura.email}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-lg font-bold text-xs ml-3 ${
                          candidatura.status === 'nuovo' ? 'bg-blue-100 text-blue-700' :
                          candidatura.status === 'approvato' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {candidatura.status === 'nuovo' ? '🆕 NUOVA' :
                           candidatura.status === 'approvato' ? '✅ APPROVATA' : '❌ RIFIUTATA'}
                        </span>
                      </div>

                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 font-semibold">Telefono</p>
                          <p className="text-gray-900 font-bold">{candidatura.telefono}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-semibold">Città</p>
                          <p className="text-gray-900 font-bold">{candidatura.citta}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-semibold">Mezzo</p>
                          <p className="text-gray-900 font-bold">{candidatura.mezzoTrasporto}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 font-semibold">Data</p>
                          <p className="text-gray-900 font-bold">
                            {candidatura.createdAt?.seconds ? 
                              new Date(candidatura.createdAt.seconds * 1000).toLocaleDateString('it-IT') : 
                              'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 ml-6">
                      <button
                        onClick={() => { setSelectedCandidatura(candidatura); setShowCandidaturaModal(true); }}
                        className="p-3 bg-blue-100 hover:bg-blue-200 rounded-xl transition"
                        title="Visualizza dettagli"
                      >
                        <Eye className="text-blue-600" size={20} />
                      </button>
                      {candidatura.status === 'nuovo' && (
                        <>
                          <button
                            onClick={() => handleApprove(candidatura.id)}
                            className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition flex items-center gap-2"
                          >
                            <CheckCircle size={20} />
                            Approva
                          </button>
                          <button
                            onClick={() => handleReject(candidatura.id)}
                            className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition flex items-center gap-2"
                          >
                            <XCircle size={20} />
                            Rifiuta
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // SUPPORT VIEW
const SupportView = () => {
  const [selectedTicketChat, setSelectedTicketChat] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const messagesEndRef = React.useRef(null);

  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm('Sei sicuro di voler chiudere questo ticket?')) return;
    try {
      await updateDoc(doc(db, 'support-tickets', ticketId), { status: 'closed' });
      if (selectedTicketChat?.id === ticketId) setSelectedTicketChat(null);
      alert('✅ Ticket chiuso!');
    } catch (error) {
      alert('❌ Errore durante la chiusura');
    }
  };

  // Ascolta messaggi del ticket selezionato
  useEffect(() => {
    if (!selectedTicketChat?.id) return;
    const q = query(
      collection(db, 'support-tickets', selectedTicketChat.id, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Notifica per nuovi messaggi dal rider
      if (msgs.length > ticketMessages.length) {
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg.sender === 'rider') {
          if (Notification.permission === 'granted') {
            new Notification(`Messaggio da ${lastMsg.senderName} 🛵`, {
              body: lastMsg.text,
              icon: '/logo192.png'
            });
          }
        }
      }
      
      setTicketMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsubscribe();
  }, [selectedTicketChat?.id, ticketMessages.length]);

  const sendAdminReply = async () => {
    const msg = adminReply.trim();
    if (!msg || !selectedTicketChat?.id) return;
    setAdminReply('');
    try {
      await addDoc(collection(db, 'support-tickets', selectedTicketChat.id, 'messages'), {
        text: msg,
        sender: 'admin',
        senderName: 'Supporto FarmaciaSubito',
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'support-tickets', selectedTicketChat.id), {
        updatedAt: serverTimestamp(),
        lastMessage: msg,
        status: 'in_progress'
      });
    } catch (error) {
      console.error('Errore invio risposta:', error);
    }
  };

  if (selectedTicketChat) {
    return (
      <div className="flex flex-col h-screen">
        <div className="bg-white border-b-2 border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setSelectedTicketChat(null); setTicketMessages([]); }}
              className="p-2 hover:bg-gray-100 rounded-xl transition"
            >
              ← Indietro
            </button>
            <div>
              <h2 className="font-black text-gray-900">Chat con {selectedTicketChat.riderName}</h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                selectedTicketChat.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                selectedTicketChat.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                'bg-green-100 text-green-700'
              }`}>
                {selectedTicketChat.status === 'open' ? 'APERTO' :
                 selectedTicketChat.status === 'in_progress' ? 'IN CORSO' : 'CHIUSO'}
              </span>
            </div>
          </div>
          {selectedTicketChat.status !== 'closed' && (
            <button
              onClick={() => handleCloseTicket(selectedTicketChat.id)}
              className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-green-700 transition"
            >
              ✅ Chiudi Ticket
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {ticketMessages.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Nessun messaggio ancora</p>
            </div>
          )}
          {ticketMessages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] ${
                msg.sender === 'admin'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              } p-3 rounded-2xl shadow-sm`}>
                {msg.sender === 'rider' && (
                  <p className="text-xs font-bold text-orange-600 mb-1">{msg.senderName}</p>
                )}
                <p className="text-sm">{msg.text}</p>
                <p className={`text-xs mt-1 ${msg.sender === 'admin' ? 'text-purple-200' : 'text-gray-400'}`}>
                  {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '...'}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {selectedTicketChat.status !== 'closed' && (
          <div className="bg-white border-t-2 border-gray-200 p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={adminReply}
                onChange={(e) => setAdminReply(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendAdminReply()}
                placeholder="Rispondi al rider..."
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 outline-none text-sm"
              />
              <button
                onClick={sendAdminReply}
                className="bg-purple-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-purple-700 transition"
              >
                Invia
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-gray-900">💬 Supporto Rider</h2>
          <div className="flex items-center gap-2">
            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-bold">
              {tickets.filter(t => t.status === 'open').length} aperti
            </span>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-bold">
              {tickets.filter(t => t.status === 'in_progress').length} in corso
            </span>
          </div>
        </div>

        <div className="space-y-3">
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-semibold">Nessun ticket trovato</p>
            </div>
          ) : (
            tickets.map(ticket => (
              <div key={ticket.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 font-black text-sm">🛵</span>
                    </div>
                    <div>
                      <p className="font-black text-gray-900">{ticket.riderName || 'Rider'}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {ticket.lastMessage || 'Nessun messaggio'} 
                      </p>
                      <p className="text-xs text-gray-400">
                        {ticket.updatedAt?.toDate ? ticket.updatedAt.toDate().toLocaleDateString('it-IT') : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-lg font-bold text-xs ${
                      ticket.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                      ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {ticket.status === 'open' ? 'APERTO' :
                       ticket.status === 'in_progress' ? 'IN CORSO' : 'CHIUSO'}
                    </span>
                    <button
                      onClick={() => setSelectedTicketChat(ticket)}
                      className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-purple-700 transition"
                    >
                      Apri Chat
                    </button>
                    {ticket.status !== 'closed' && (
                      <button
                        onClick={() => handleCloseTicket(ticket.id)}
                        className="p-2 bg-green-100 hover:bg-green-200 rounded-xl transition"
                        title="Chiudi ticket"
                      >
                        <CheckCircle className="text-green-600" size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

  // CANDIDATURA MODAL
  const CandidaturaModal = () => {
    if (!showCandidaturaModal || !selectedCandidatura) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black">Dettagli Candidatura</h2>
                <p className="text-purple-100">{selectedCandidatura.nome} {selectedCandidatura.cognome}</p>
              </div>
              <button onClick={() => setShowCandidaturaModal(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition">
                <X className="text-white" size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-black text-gray-900 mb-3">👤 Dati Personali</h3>
                <div className="bg-gray-50 p-4 rounded-xl space-y-2 text-sm">
                  <div><span className="text-gray-500">Nome:</span> <span className="font-bold">{selectedCandidatura.nome} {selectedCandidatura.cognome}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-bold">{selectedCandidatura.email}</span></div>
                  <div><span className="text-gray-500">Telefono:</span> <span className="font-bold">{selectedCandidatura.telefono}</span></div>
                  <div><span className="text-gray-500">Data Nascita:</span> <span className="font-bold">{selectedCandidatura.dataNascita}</span></div>
                </div>
              </div>

              <div>
                <h3 className="font-black text-gray-900 mb-3">📍 Residenza</h3>
                <div className="bg-gray-50 p-4 rounded-xl space-y-2 text-sm">
                  <div><span className="text-gray-500">Città:</span> <span className="font-bold">{selectedCandidatura.citta}</span></div>
                  <div><span className="text-gray-500">Indirizzo:</span> <span className="font-bold">{selectedCandidatura.indirizzo}</span></div>
                </div>
              </div>
            </div>

            <div>
  <h3 className="font-black text-gray-900 mb-3">🚴 Mezzo di Trasporto</h3>
  <div className="bg-gray-50 p-4 rounded-xl grid grid-cols-2 gap-4 text-sm">
    <div><span className="text-gray-500">Mezzo:</span> <span className="font-bold">{selectedCandidatura.mezzoTrasporto}</span></div>
    <div><span className="text-gray-500">Smartphone:</span> <span className="font-bold">{selectedCandidatura.sistemaOperativo === 'android' ? '📱 Android' : '🍎 iOS'}</span></div>
    <div><span className="text-gray-500">Patente:</span> <span className="font-bold">{selectedCandidatura.patente || 'N/A'}</span></div>
    <div><span className="text-gray-500">Assicurazione:</span> <span className="font-bold">{selectedCandidatura.assicurazione || 'N/A'}</span></div>
  </div>
</div>

            <div>
  <h3 className="font-black text-gray-900 mb-3">💼 Partita IVA</h3>
  <div className="bg-gray-50 p-4 rounded-xl space-y-2 text-sm">
    <div>
      <span className="text-gray-500">Ha P.IVA:</span>{' '}
      <span className="font-bold">
        {selectedCandidatura.haPartitaIva === 'si' ? '✅ SÌ' : '❌ NO'}
      </span>
    </div>
    {selectedCandidatura.haPartitaIva === 'si' && (
      <>
        <div>
          <span className="text-gray-500">Numero P.IVA:</span>{' '}
          <span className="font-bold">{selectedCandidatura.numeroPartitaIva}</span>
        </div>
        <div>
          <span className="text-gray-500">Regime:</span>{' '}
          <span className="font-bold">{selectedCandidatura.regimeFiscale}</span>
        </div>
      </>
    )}
  </div>
</div>

            <div>
              <h3 className="font-black text-gray-900 mb-3">⏰ Disponibilità</h3>
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedCandidatura.disponibilita?.map((disp, idx) => (
                    <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-bold text-sm">
                      {disp}
                    </span>
                  ))}
                </div>
                <p className="text-sm"><strong>Ore settimanali:</strong> {selectedCandidatura.oreSettimanali || 'N/A'}</p>
              </div>
            </div>

            {selectedCandidatura.note && (
              <div>
                <h3 className="font-black text-gray-900 mb-3">📝 Note</h3>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-gray-700">{selectedCandidatura.note}</p>
                </div>
              </div>
            )}

            {(selectedCandidatura.status === 'nuovo' || selectedCandidatura.status === 'pending') && (
              <div className="flex gap-3 pt-4">
                <button
                  onClick={async () => {
                    try {
                      const c = selectedCandidatura;
                      // 1. Approva la candidatura
                      await updateDoc(doc(db, 'candidature-rider', c.id), { status: 'approvato' });

                      // 2. Crea il documento in 'riders' (così appare nella sezione Rider
                      //    con il tasto "Invia documenti"). uid null finché il rider non si registra.
                      await setDoc(doc(db, 'riders', c.id), {
                        name: `${c.nome} ${c.cognome}`,
                        nome: c.nome,
                        cognome: c.cognome,
                        email: (c.email || '').trim().toLowerCase(),
                        telefono: c.telefono || '',
                        mezzoTrasporto: c.mezzoTrasporto || c.mezzo || '',
                        status: 'pending',
                        uid: null,
                        isOnline: false,
                        ratingMedia: 0,
                        ratingCount: 0,
                        createdAt: new Date().toISOString()
                      }, { merge: true });

                      // 3. Invia email con il link di registrazione
                      try {
                        await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            to: c.email,
                            subject: '🎉 Candidatura approvata — Registrati su FarmaciaSubito!',
                            html: `
                              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f0fdf4; padding: 20px; border-radius: 12px;">
                                <div style="background: #16a34a; padding: 24px; border-radius: 8px; text-align: center; margin-bottom: 24px;">
                                  <h1 style="color: white; margin: 0; font-size: 24px;">🛵 FarmaciaSubito</h1>
                                  <p style="color: #bbf7d0; margin: 8px 0 0 0;">Portale Rider</p>
                                </div>
                                <h2 style="color: #15803d;">Ciao ${c.nome}! 🎉</h2>
                                <p style="color: #374151; line-height: 1.6;">La tua candidatura come rider FarmaciaSubito è stata <strong>approvata</strong>!</p>
                                <p style="color: #374151; line-height: 1.6;">Per iniziare, registrati sull'App Rider cliccando il pulsante qui sotto:</p>
                                <div style="text-align: center; margin: 32px 0;">
                                  <a href="https://rider.farmaciasubito.it" style="background: #16a34a; color: white; padding: 16px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px;">🚀 Registrati sull'App Rider</a>
                                </div>
                                <p style="color: #6b7280; font-size: 13px; text-align: center;">Usa questa email per registrarti: <strong>${c.email}</strong></p>
                                <div style="text-align: center; margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb;">
                                  <p style="color: #9ca3af; font-size: 12px;">© 2026 FarmaciaSubito — Benevento, Italia</p>
                                </div>
                              </div>
                            `
                          })
                        });
                      } catch (mailErr) {
                        console.warn('Email approvazione non inviata (non bloccante):', mailErr);
                      }

                      alert(`✅ Candidatura approvata!\n\n📧 Email inviata a ${c.email} con il link per registrarsi.`);
                      setShowCandidaturaModal(false);
                    } catch (error) {
                      console.error('Errore approvazione:', error);
                      alert(`❌ Errore: ${error.message}`);
                    }
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-black transition"
                >
                  ✅ APPROVA
                </button>
                <button
                  onClick={async () => {
                    await updateDoc(doc(db, 'candidature-rider', selectedCandidatura.id), { status: 'rifiutato' });
                    setShowCandidaturaModal(false);
                  }}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white py-4 rounded-xl font-black transition"
                >
                  ❌ RIFIUTA
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // TICKET MODAL
const TicketModal = () => {
  const [formData, setFormData] = useState(selectedTicket || {
    user: '',
    subject: '',
    message: '',
    type: 'order',
    priority: 'medium',
    status: 'open'
  });

  if (!showTicketModal) return null;

  const handleSave = async () => {
    try {
      if (selectedTicket) {
        // Modifica esistente (risposta)
        await updateDoc(doc(db, 'support-tickets', selectedTicket.id), {
          status: 'in_progress',
          response: formData.response,
          respondedAt: new Date().toISOString()
        });
        alert('✅ Risposta inviata!');
      } else {
        // Nuovo ticket
        await addDoc(collection(db, 'support-tickets'), {
          user: formData.user,
          subject: formData.subject,
          message: formData.message,
          type: formData.type,
          priority: formData.priority,
          status: 'open',
          createdAt: new Date().toLocaleString('it-IT')
        });
        alert('✅ Ticket creato!');
      }
      setShowTicketModal(false);
      setSelectedTicket(null);
    } catch (error) {
      console.error('Errore:', error);
      alert('❌ Errore durante il salvataggio');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-gray-900">
            {selectedTicket ? 'Dettagli Ticket' : 'Nuovo Ticket'}
          </h2>
          <button onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }} className="p-2 hover:bg-gray-100 rounded-xl">
            <X size={24} />
          </button>
        </div>

        {selectedTicket ? (
          // Visualizza ticket esistente
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500 mb-1">Da:</p>
              <p className="font-bold text-gray-900">{selectedTicket.user}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500 mb-1">Oggetto:</p>
              <p className="font-bold text-gray-900">{selectedTicket.subject}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-500 mb-1">Messaggio:</p>
              <p className="text-gray-900">{selectedTicket.message}</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Tipo:</p>
                <p className="font-bold text-gray-900">{selectedTicket.type}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Priorità:</p>
                <p className="font-bold text-gray-900">{selectedTicket.priority}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Stato:</p>
                <p className="font-bold text-gray-900">{selectedTicket.status}</p>
              </div>
            </div>

            {selectedTicket.status !== 'closed' && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Risposta</label>
                <textarea
                  value={formData.response || ''}
                  onChange={(e) => setFormData({...formData, response: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                  placeholder="Scrivi la tua risposta..."
                  rows="4"
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              {selectedTicket.status !== 'closed' && (
                <button
                  onClick={handleSave}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition"
                >
                  Invia Risposta
                </button>
              )}
              <button
                onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition"
              >
                Chiudi
              </button>
            </div>
          </div>
        ) : (
          // Form nuovo ticket
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email Utente</label>
              <input
                type="email"
                value={formData.user}
                onChange={(e) => setFormData({...formData, user: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="utente@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Oggetto</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="Descrivi il problema"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Messaggio</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="Dettagli del problema..."
                rows="4"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                >
                  <option value="order">Ordine</option>
                  <option value="payment">Pagamento</option>
                  <option value="delivery">Consegna</option>
                  <option value="account">Account</option>
                  <option value="other">Altro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Priorità</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                >
                  <option value="low">Bassa</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Stato</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                >
                  <option value="open">Aperto</option>
                  <option value="in_progress">In corso</option>
                  <option value="closed">Chiuso</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition"
              >
                Crea Ticket
              </button>
              <button
                onClick={() => { setShowTicketModal(false); setSelectedTicket(null); }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

  // PHARMACY MODAL
const PharmacyModal = () => {
  const [formData, setFormData] = useState(selectedPharmacy || {
  name: '',
  email: '',
  piva: '',
  indirizzo: '',
  telefono: '',
  lat: '',
  lng: '',
  commission: 10,
  status: 'active'
});

  if (!showPharmacyModal) return null;

  const handleSave = async () => {
  try {
    if (selectedPharmacy) {
      // Modifica esistente
      await updateDoc(doc(db, 'farmacie', selectedPharmacy.id), {
        name: formData.name,
        email: formData.email,
        piva: formData.piva,
        indirizzo: formData.indirizzo,
        telefono: formData.telefono,
        lat: Number(formData.lat),
        lng: Number(formData.lng),
        commission: Number(formData.commission),
        status: formData.status
      });
      alert('✅ Farmacia aggiornata!');
    } else {
      // Nuova farmacia
// 1. Genera password temporanea (serve per creazione account)
const tempPassword = generateTempPassword();

// 2. Crea account Firebase Authentication
let userId = null;
try {
  // Usa fetch alla Cloud Function invece di createUserWithEmailAndPassword
  // così non sovrascrive la sessione admin
  const createResponse = await fetch(
    'https://us-central1-farmacia-subito.cloudfunctions.net/createFarmaciaAccount',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: formData.email })
    }
  );
  const createData = await createResponse.json();
  if (!createResponse.ok) throw new Error(createData.error);
  userId = createData.uid;
  
} catch (authError) {
  if (authError.code === 'auth/email-already-in-use') {
    alert('❌ Questa email è già registrata!');
    return;
  }
  throw authError;
}
      
      // 3. Salva dati farmacia in Firestore con userId
      const docRef = await addDoc(collection(db, 'farmacie'), {
        userId: userId,
        name: formData.name,
        email: formData.email,
        piva: formData.piva,
        indirizzo: formData.indirizzo,
        telefono: formData.telefono,
        lat: Number(formData.lat),
        lng: Number(formData.lng),
        commission: Number(formData.commission),
        status: formData.status,
        orders: 0,
        revenue: 0,
        createdAt: serverTimestamp()
      });
      
      console.log('✅ Farmacia salvata in Firestore:', docRef.id);
      
      // 4. TODO: Invia email con credenziali
      // Qui andrà la funzione per inviare email automatica
      // Per ora mostra alert con credenziali
alert(
  `✅ Farmacia creata con successo!\n\n` +
  `📧 Email inviata a: ${formData.email}\n\n` +
  `La farmacia riceverà un'email per impostare la propria password.\n` +
  `Link dashboard: https://farmacia.farmaciasubito.it`
);
      
      // Copia credenziali in clipboard
      navigator.clipboard.writeText(
        `Credenziali accesso FarmaciaSubito:\n` +
        `Email: ${formData.email}\n` +
        `Password: ${tempPassword}\n\n` +
        `Link dashboard: https://farmacia-app.farmaciasubito.it`
      );
    }
    
    setShowPharmacyModal(false);
    setSelectedPharmacy(null);
  } catch (error) {
    console.error('❌ Errore:', error);
    alert(`❌ Errore durante il salvataggio: ${error.message}`);
  }
};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-black text-gray-900">
            {selectedPharmacy ? 'Modifica Farmacia' : 'Nuova Farmacia'}
          </h2>
          <button onClick={() => { setShowPharmacyModal(false); setSelectedPharmacy(null); }} className="p-2 hover:bg-gray-100 rounded-xl">
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Nome Farmacia</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="Farmacia Centrale" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
            <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="info@farmacia.it" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">P.IVA</label>
            <input type="text" value={formData.piva} onChange={(e) => setFormData({...formData, piva: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="01234567890" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Telefono</label>
            <input type="tel" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="+39 081 123 4567" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-bold text-gray-700 mb-2">Indirizzo</label>
            <input type="text" value={formData.indirizzo} onChange={(e) => setFormData({...formData, indirizzo: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="Via Roma 123, 80100 Napoli" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Commissione %</label>
            <input type="number" value={formData.commission} onChange={(e) => setFormData({...formData, commission: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="10" min="0" max="100" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Latitudine</label>
            <input type="number" step="0.000001" value={formData.lat} onChange={(e) => setFormData({...formData, lat: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="es. 40.8518" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Longitudine</label>
  <          input type="number" step="0.000001" value={formData.lng} onChange={(e) => setFormData({...formData, lng: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none" placeholder="es. 14.2681" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Stato</label>
            <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none">
              <option value="active">Attiva</option>
              <option value="inactive">Inattiva</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <button onClick={handleSave} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition">
            {selectedPharmacy ? 'Salva Modifiche' : 'Aggiungi Farmacia'}
          </button>
          <button onClick={() => { setShowPharmacyModal(false); setSelectedPharmacy(null); }} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition">
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
};

// CONTRATTO MODAL
  const ContrattoModal = () => {
    const [generatingPdf, setGeneratingPdf] = React.useState(false);
    const [sendingEmail, setSendingEmail] = React.useState(false);
    const [showFirmaAdmin, setShowFirmaAdmin] = React.useState(false);
    const [firmatario, setFirmatario] = React.useState('Mirco Piscopo');
    const canvasRef = React.useRef(null);
    const isDrawing = React.useRef(false);

    if (!showContrattoModal || !selectedContratto) return null;
    const d = selectedContratto;

    // ── Canvas firma ──────────────────────────────────────────
    const startDrawing = (e) => {
      isDrawing.current = true;
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1e40af';
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDrawing = () => { isDrawing.current = false; };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const isCanvasEmpty = () => {
      const canvas = canvasRef.current;
      if (!canvas) return true;
      const ctx = canvas.getContext('2d');
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      return !data.some(v => v !== 0);
    };

    // ── Genera PDF con entrambe le firme ─────────────────────
    const buildPDF = async (firmaAdminBase64) => {
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210; const margin = 20; let y = 20;

      // Header
      pdf.setFillColor(37, 99, 235);
      pdf.rect(0, 0, W, 30, 'F');
      pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255,255,255);
      pdf.text('CONTRATTO DI COLLABORAZIONE OCCASIONALE', W/2, 13, { align: 'center' });
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');
      pdf.text('Servizio di Consegna Farmaci a Domicilio — FarmaciaSubito', W/2, 22, { align: 'center' });
      y = 40;

      // Parti
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(0,0,0);
      pdf.text('TRA', margin, y); y+=7;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      pdf.text('FarmaciaSubito (Committente) — Benevento (BN), Italia', margin, y); y+=5;
      pdf.text('Email: info@farmaciasubito.it', margin, y); y+=5;
      pdf.text('Rappresentata da: Mirco Piscopo e Alessandro Rauso (Co-founder)', margin, y); y+=8;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
      pdf.text('E', margin, y); y+=7;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      pdf.text(`Il Sig./Sig.ra: ${d.nome} ${d.cognome}`, margin, y); y+=5;
      pdf.text(`Nato/a a: ${d.luogoNascita} il ${d.dataNascita}`, margin, y); y+=5;
      pdf.text(`Codice Fiscale: ${d.codiceFiscale}`, margin, y); y+=5;
      pdf.text(`Residente in: ${d.residenza}`, margin, y); y+=10;

      pdf.setDrawColor(200,200,200); pdf.line(margin, y, W-margin, y); y+=8;

      // Premesse (modello del mandato — base giuridica del servizio)
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(37,99,235);
      pdf.text('PREMESSO CHE', margin, y); y+=5;
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(0,0,0);
      const premesse = pdf.splitTextToSize(
        'Il Committente gestisce una piattaforma digitale per la consegna rapida di farmaci e parafarmaci a domicilio, operando in nome e per conto del cliente secondo il modello del mandato (artt. 1703 e ss. c.c.); il Collaboratore acquista per conto dei clienti i prodotti farmaceutici e parafarmaceutici presso le farmacie, anticipando l\'importo con propri mezzi di pagamento, e li consegna a domicilio; il Collaboratore è disponibile a svolgere, in regime di collaborazione occasionale autonoma, attività di acquisto e consegna di tali prodotti; le parti intendono regolamentare i termini della collaborazione.',
        W - margin*2
      );
      pdf.text(premesse, margin, y); y += premesse.length * 5 + 6;

      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(0,0,0);
      pdf.text('SI CONVIENE E STIPULA QUANTO SEGUE', margin, y); y+=8;

      // Articoli
      const articles = [
        ['Art. 1 - OGGETTO DEL CONTRATTO', 'Il Collaboratore si impegna a svolgere, in piena autonomia e senza vincolo di subordinazione, attività di acquisto presso farmacie e consegna a domicilio di prodotti farmaceutici e parafarmaceutici nell\'ambito del servizio FarmaciaSubito. Il Collaboratore acquista i prodotti richiesti dal cliente presso le farmacie più vicine al punto di consegna, anticipando l\'importo dovuto con il proprio mezzo di pagamento personale (carta o contanti), salvo successivo rimborso da parte del Committente secondo quanto previsto all\'Art. 3. Il Collaboratore opererà esclusivamente tramite l\'applicazione mobile fornita dal Committente.'],
        ['Art. 2 - MODALITÀ DI SVOLGIMENTO', 'Il Collaboratore è libero di accettare o rifiutare ogni singola richiesta di consegna, senza obbligo di giustificazione. Determina autonomamente i propri orari di disponibilità attraverso il sistema di turni dell\'app; durante il turno prenotato non può disconnettersi, salvo comunicazione al supporto con motivazioni valide. Utilizza il proprio mezzo di trasporto e i propri strumenti di lavoro. Si impegna a: recarsi presso la farmacia e acquistare i prodotti anticipandone il costo; consegnare all\'indirizzo indicato entro i tempi previsti; effettuare la consegna al piano ove richiesto e ragionevolmente accessibile; verificare l\'identità del destinatario quando previsto; mantenere la riservatezza sui dati personali e sanitari del cliente; trattare i prodotti con cura e nel rispetto delle norme igienico-sanitarie.'],
        ['Art. 3 - CORRISPETTIVO, ANTICIPI E RIMBORSI', 'Per ogni consegna completata: compenso base € 3,00; maggiorazione distanza € 0,20 per km percorso; maggiorazione festiva +20% per consegne di sabato e domenica. Il Collaboratore anticipa con propri mezzi (carta personale o contanti) l\'importo dei prodotti acquistati in farmacia per conto del cliente: tale anticipo non costituisce compenso ed è oggetto di integrale rimborso da parte del Committente. A prova di ogni acquisto anticipato, il Collaboratore è tenuto a caricare nell\'app lo scontrino rilasciato dalla farmacia: il caricamento dello scontrino è condizione necessaria per il rimborso dell\'anticipo e per il completamento della consegna. Il rimborso degli anticipi effettuati con carta personale avviene con tempistiche rapide. Qualora il Collaboratore incassi contanti dal cliente, l\'importo incassato viene conteggiato e compensato con le somme dovute in sede di liquidazione mensile. Il Collaboratore comunica il proprio IBAN (app, sezione Profilo > Pagamenti), sul quale riceverà sia i rimborsi sia il compenso. Il pagamento avviene tramite bonifico bancario sull\'IBAN indicato, con cadenza mensile e non oltre il dodicesimo giorno del mese successivo, previa messa a disposizione del prospetto riepilogativo. Il compenso si intende al lordo delle ritenute fiscali e previdenziali previste dalla legge.'],
        ['Art. 4 - NATURA DEL RAPPORTO', 'Il presente contratto configura una collaborazione autonoma occasionale ex art. 2222 c.c., senza vincolo di subordinazione. Il Collaboratore non ha diritto a: ferie, permessi retribuiti, malattia, maternità; tredicesima, quattordicesima o altri benefit; TFR o altre indennità di fine rapporto. Il Collaboratore è libero di svolgere altre attività lavorative parallelamente alla presente collaborazione.'],
        ['Art. 5 - OBBLIGHI ASSICURATIVI, FISCALI E TECNICI', 'Il Collaboratore dichiara di essere in possesso di: patente di guida valida per il mezzo utilizzato; assicurazione RC del mezzo in regola; assicurazione RC professionale. Si impegna a: comunicare il proprio IBAN per la ricezione di compensi e rimborsi; caricare nell\'app lo scontrino di ogni acquisto effettuato per conto del cliente; completare l\'autodichiarazione giornaliera sull\'app prima di ogni turno; comunicare tempestivamente eventuali variazioni o scadenze delle coperture assicurative. È responsabile per tutti gli adempimenti fiscali e previdenziali derivanti dalla collaborazione, inclusa l\'apertura di Partita IVA se e quando necessaria per legge. BORSA TERMICA - DOTAZIONE OBBLIGATORIA: il Collaboratore si impegna a dotarsi, a propria cura e spese, di una borsa termica isolante con piastre refrigeranti (siberini) in numero e stato di efficienza adeguati, e a utilizzarla per il trasporto di tutti i prodotti che richiedono protezione dalle escursioni termiche. La borsa deve essere mantenuta pulita, integra e funzionante; le piastre refrigeranti devono essere adeguatamente raffreddate prima di ogni turno. Il turno non può essere iniziato in assenza di tale dotazione, di cui il Committente può richiedere evidenza in qualsiasi momento. DIVIETO DI TRASPORTO DI FARMACI TERMOLABILI: il Collaboratore prende atto che FarmaciaSubito non effettua il trasporto di farmaci termolabili, ossia dei medicinali che richiedono il mantenimento della catena del freddo a temperatura controllata tra +2°C e +8°C (a titolo esemplificativo: insuline, vaccini, farmaci biologici, colliri e preparazioni magistrali refrigerate). Il Collaboratore si impegna a rifiutare il ritiro di tali prodotti e a segnalarlo immediatamente tramite l\'app, anche qualora il farmacista o il cliente ne facciano richiesta.'],
        ['Art. 6 - PRODOTTI CON OBBLIGO DI RICETTA E CATENA DI CUSTODIA', 'Per i prodotti soggetti a obbligo di ricetta, il Collaboratore ritira dal farmacista il prodotto in confezione/sacchetto sigillato, già predisposto dalla farmacia. Il Collaboratore non apre, non manomette e non altera in alcun modo il sacchetto sigillato: la responsabilità sulla corretta dispensazione del farmaco resta in capo alla farmacia. Il Collaboratore consegna il sacchetto sigillato al cliente e, ove previsto, verifica l\'identità del destinatario. I dati sanitari e le ricette eventualmente trattati sono gestiti nel rispetto dell\'art. 9 GDPR e cancellati subito dopo la consegna.'],
        ['Art. 7 - RESPONSABILITÀ', 'Il Collaboratore è responsabile per: danni causati durante l\'acquisto, il trasporto e la consegna dei prodotti; violazioni del Codice della Strada; danni a terzi derivanti dall\'attività svolta; violazioni della privacy e della riservatezza; uso improprio delle credenziali dell\'app o dei mezzi di pagamento personali. Il Committente non risponde di danni causati dal Collaboratore nell\'esecuzione dell\'attività. RESPONSABILITÀ PER MANCATO UTILIZZO DELLA BORSA TERMICA: il Collaboratore che operi in violazione dell\'Art. 5 - ossia senza borsa termica o con piastre refrigeranti non efficienti - risponde in via esclusiva del deterioramento dei prodotti trasportati e dei conseguenti danni, inclusi i rimborsi riconosciuti al cliente e gli oneri sostenuti dal Committente per la gestione del reclamo; il Committente potrà rivalersi sul Collaboratore per tali importi in sede di liquidazione. RESPONSABILITÀ PER TRASPORTO DI FARMACI TERMOLABILI: il Collaboratore che, in violazione dell\'Art. 5, ritiri e trasporti farmaci soggetti a catena del freddo risponde in via esclusiva di ogni conseguenza derivante dalla perdita di efficacia o dall\'alterazione del prodotto, anche nei confronti di terzi. RUOLO DI MERO TRASPORTATORE: il Collaboratore non è un professionista sanitario. Non verifica la corrispondenza tra ricetta e farmaco, non controlla dosaggi o interazioni, non apre i sacchetti sigillati e non fornisce indicazioni terapeutiche o consigli sull\'uso dei prodotti. La verifica della corrispondenza tra prescrizione e farmaco costituisce atto di dispensazione, riservato per legge al farmacista e di sua esclusiva responsabilità professionale.'],
        ['Art. 8 - RISERVATEZZA E TRATTAMENTO DATI', 'Il Collaboratore si impegna a mantenere la massima riservatezza su tutte le informazioni relative a clienti, farmacie e modalità operative del servizio. Tratterà i dati personali e sanitari esclusivamente per le finalità di esecuzione del servizio e nel rispetto del GDPR (Reg. UE 2016/679). Gli obblighi di riservatezza permangono anche dopo la cessazione del rapporto.'],
        ['Art. 9 - DURATA E RISOLUZIONE', 'Il presente contratto ha durata di 12 mesi a partire dall\'avvenuta attivazione dell\'account e può essere rinnovato previo accordo scritto tra le parti. Ciascuna parte può recedere in qualsiasi momento con preavviso scritto di 7 giorni. Il Committente può risolvere immediatamente il contratto in caso di: gravi inadempimenti; violazione degli obblighi di riservatezza; comportamenti lesivi dell\'immagine del servizio; mancanza delle coperture assicurative richieste; manomissione dei sacchetti sigillati, reiterato mancato utilizzo della borsa termica o trasporto di farmaci termolabili in violazione dell\'Art. 5, frodi documentate o uso improprio dei mezzi di pagamento.'],
        ['Art. 10 - CONTROVERSIE', 'Per qualsiasi controversia derivante dal presente contratto sarà competente in via esclusiva il Foro di Benevento. Le parti si impegnano a tentare una composizione amichevole prima di adire l\'autorità giudiziaria.'],
        ['INFORMATIVA PRIVACY (art. 13 GDPR)', 'Titolare del trattamento: FarmaciaSubito, Benevento (BN), Italia. Email: privacy@farmaciasubito.it. Finalità: gestione del rapporto di collaborazione, adempimenti contrattuali, fiscali e previdenziali. Base giuridica: esecuzione del contratto e obblighi di legge. Destinatari dei dati: commercialisti, consulenti del lavoro, autorità fiscali e, limitatamente all\'elaborazione degli incassi dei clienti, il fornitore del servizio di pagamento (Stripe). Il compenso al Collaboratore è erogato tramite bonifico bancario sull\'IBAN da questi indicato. Conservazione: per tutta la durata del rapporto e per i 10 anni successivi (obblighi fiscali). Diritti dell\'interessato: accesso, rettifica, cancellazione, limitazione, opposizione, portabilità (privacy@farmaciasubito.it). Il Collaboratore dichiara di aver letto e compreso l\'informativa.'],
      ];

      for (const [title, body] of articles) {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
        const lines = pdf.splitTextToSize(body, W - margin*2);
        // Altezza necessaria: titolo (5) + righe testo + spaziatura
        const altezzaNecessaria = 5 + lines.length * 5 + 4;
        // Se l'articolo non ci sta nella pagina corrente, vai a pagina nuova
        // (evita che il testo venga tagliato fuori dal foglio)
        if (y + altezzaNecessaria > 270) { pdf.addPage(); y = 20; }
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(37,99,235);
        pdf.text(title, margin, y); y+=5;
        pdf.setFont('helvetica', 'normal'); pdf.setTextColor(0,0,0);
        pdf.text(lines, margin, y); y += lines.length * 5 + 4;
      }

      // Sezione firme
      if (y > 210) { pdf.addPage(); y = 20; }
      pdf.setDrawColor(200,200,200); pdf.line(margin, y, W-margin, y); y+=8;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0,0,0);
      pdf.text('FIRME', W/2, y, { align: 'center' }); y+=8;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
      pdf.text(`Luogo e data: ${d.luogoFirma}, ${d.dataFirma}`, margin, y); y+=12;

      // Colonna sinistra: firma committente
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
      pdf.text('Per il Committente (FarmaciaSubito)', margin, y); y+=4;
      pdf.setFont('helvetica', 'normal');
      pdf.text(firmatario, margin, y); y+=4;
      if (firmaAdminBase64) {
        pdf.addImage(firmaAdminBase64, 'PNG', margin, y, 60, 25);
      }
      const yDopoAdmin = y + 28;

      // Colonna destra: firma rider
      const colDx = W / 2 + 5;
      const yRider = yDopoAdmin - 28;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
      pdf.text(`Il Collaboratore: ${d.nome} ${d.cognome}`, colDx, yRider); 
      pdf.setFont('helvetica', 'normal');
      pdf.text(`C.F.: ${d.codiceFiscale}`, colDx, yRider + 4);
      if (d.firmaUrl) {
        try {
          const response = await fetch(d.firmaUrl);
          const blob = await response.blob();
          const base64Rider = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          pdf.addImage(base64Rider, 'PNG', colDx, yRider + 8, 60, 25);
        } catch {}
      }

      y = yDopoAdmin + 4;

      // Footer GDPR
      if (y > 260) { pdf.addPage(); y = 20; }
      pdf.setDrawColor(200,200,200); pdf.line(margin, y, W-margin, y); y+=5;
      pdf.setFontSize(7); pdf.setTextColor(120,120,120);
      const gdpr = 'INFORMATIVA PRIVACY (art. 13 GDPR): Titolare: FarmaciaSubito, Benevento. Finalità: gestione rapporto di collaborazione, adempimenti contrattuali. Base giuridica: esecuzione del contratto. Conservazione: 10 anni. Diritti: privacy@farmaciasubito.it';
      const gdprLines = pdf.splitTextToSize(gdpr, W - margin*2);
      pdf.text(gdprLines, margin, y);

      return pdf;
    };

    const handleScarica = async () => {
      if (isCanvasEmpty()) { alert('✍️ Firma prima il contratto!'); return; }
      setGeneratingPdf(true);
      try {
        const firmaAdminBase64 = canvasRef.current.toDataURL('image/png');
        const pdf = await buildPDF(firmaAdminBase64);
        pdf.save(`Contratto_${d.nome}_${d.cognome}_${d.dataFirma}.pdf`);
      } catch (e) {
        alert('❌ Errore generazione PDF: ' + e.message);
      } finally {
        setGeneratingPdf(false);
      }
    };

    const handleInviaRider = async () => {
      if (isCanvasEmpty()) { alert('✍️ Firma prima il contratto!'); return; }
      setSendingEmail(true);
      try {
        const firmaAdminBase64 = canvasRef.current.toDataURL('image/png');
        const pdf = await buildPDF(firmaAdminBase64);
        const pdfBase64 = pdf.output('datauristring').split(',')[1];

        await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: d.email,
            subject: '📄 Il tuo contratto FarmaciaSubito — Copia firmata da entrambe le parti',
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <div style="background: #16a34a; padding: 24px; border-radius: 8px; text-align: center; margin-bottom: 24px;">
                  <h1 style="color: white; margin: 0;">🛵 FarmaciaSubito</h1>
                </div>
                <h2 style="color: #15803d;">Ciao ${d.nome}!</h2>
                <p style="color: #374151; line-height: 1.6;">
                  Ti inviamo in allegato la copia del tuo <strong>contratto di collaborazione</strong> 
                  firmato da entrambe le parti.
                </p>
                <p style="color: #374151; line-height: 1.6;">
                  Conserva questo documento per i tuoi archivi personali.
                </p>
                <div style="background: #f0fdf4; border-left: 4px solid #16a34a; padding: 16px; border-radius: 4px; margin: 20px 0;">
                  <p style="margin: 0; color: #374151; font-size: 14px;">
                    <strong>Firmatario per FarmaciaSubito:</strong> ${firmatario}<br/>
                    <strong>Data firma:</strong> ${d.dataFirma}<br/>
                    <strong>Luogo:</strong> ${d.luogoFirma}
                  </p>
                </div>
                <p style="color: #6b7280; font-size: 13px;">
                  Per qualsiasi domanda: <a href="mailto:info@farmaciasubito.it">info@farmaciasubito.it</a>
                </p>
                <p style="margin-top: 24px; color: #374151;">Buon lavoro! 🛵<br/><strong>Il team FarmaciaSubito</strong></p>
              </div>
            `,
            attachments: [{
              content: pdfBase64,
              filename: `Contratto_${d.nome}_${d.cognome}.pdf`,
              type: 'application/pdf',
              disposition: 'attachment'
            }]
          })
        });

        alert(`✅ Contratto firmato inviato a ${d.email}!`);
        setShowFirmaAdmin(false);
        clearCanvas();
      } catch (e) {
        alert('❌ Errore invio: ' + e.message);
      } finally {
        setSendingEmail(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-green-500 p-6 text-white rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black">📝 Contratto Firmato</h2>
                <p className="text-emerald-100 font-semibold">{d.name}</p>
              </div>
              <button
                onClick={() => { setShowContrattoModal(false); setSelectedContratto(null); setShowFirmaAdmin(false); clearCanvas(); }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition"
              >
                <X className="text-white" size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">

            {/* Dati anagrafici */}
            <div>
              <h3 className="font-black text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">👤</span>
                Dati Anagrafici
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Nome', d.nome], ['Cognome', d.cognome],
                  ['Codice Fiscale', d.codiceFiscale], ['Data di Nascita', d.dataNascita],
                  ['Luogo di Nascita', d.luogoNascita], ['Residenza', d.residenza],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">{label}</p>
                    <p className="font-black text-gray-900">{val || '—'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Dati firma rider */}
            <div>
              <h3 className="font-black text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">📅</span>
                Dati Firma
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-100">
                  <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">Luogo di Firma</p>
                  <p className="font-black text-gray-900">{d.luogoFirma || '—'}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-100">
                  <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">Data di Firma</p>
                  <p className="font-black text-gray-900">{d.dataFirma || '—'}</p>
                </div>
              </div>
            </div>

            {/* Firma digitale rider */}
            <div>
              <h3 className="font-black text-gray-900 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">✍️</span>
                Firma Digitale del Rider
              </h3>
              {d.firmaUrl ? (
                <div className="border-2 border-gray-200 rounded-2xl overflow-hidden">
                  <div className="bg-white p-4 flex items-center justify-center min-h-[140px]">
                    <img src={d.firmaUrl} alt="Firma rider" className="max-w-full max-h-36 object-contain" style={{ filter: 'contrast(1.2)' }} />
                  </div>
                  <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-semibold">Firma acquisita digitalmente</p>
                    <a href={d.firmaUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-bold hover:underline">Apri originale ↗</a>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center text-gray-400">
                  <p className="font-semibold">Firma non disponibile</p>
                </div>
              )}
            </div>

            {/* Sezione firma admin */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4">
              <p className="text-sm font-bold text-amber-800 mb-1">✍️ Firma del Committente</p>
              <p className="text-xs text-amber-700 mb-4">Firma qui sotto, poi scarica o invia direttamente al rider.</p>

              {/* Selettore firmatario */}
              <div className="flex gap-3 mb-4">
                {['Mirco Piscopo', 'Alessandro Rauso'].map(nome => (
                  <button
                    key={nome}
                    onClick={() => setFirmatario(nome)}
                    className={`flex-1 py-2 rounded-xl font-bold text-sm transition border-2 ${
                      firmatario === nome
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-white text-amber-700 border-amber-300 hover:border-amber-500'
                    }`}
                  >
                    {nome}
                  </button>
                ))}
              </div>

              {/* Canvas firma */}
              <div className="border-2 border-amber-300 rounded-xl overflow-hidden bg-white mb-3">
                <div className="bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                  Firma qui — {firmatario}
                </div>
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={120}
                  className="w-full cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <button onClick={clearCanvas} className="text-xs text-amber-600 font-bold hover:underline mb-4">
                🗑️ Cancella firma
              </button>

              {/* Pulsanti azione */}
              <div className="flex gap-3">
                <button
                  onClick={handleScarica}
                  disabled={generatingPdf}
                  className="flex-1 bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white py-3 rounded-xl font-bold transition"
                >
                  {generatingPdf ? '⏳ Generando...' : '📄 Scarica PDF'}
                </button>
                <button
                  onClick={handleInviaRider}
                  disabled={sendingEmail}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white py-3 rounded-xl font-bold transition"
                >
                  {sendingEmail ? '⏳ Invio...' : '📧 Firma e Invia al Rider'}
                </button>
              </div>
            </div>

            <button
              onClick={() => { setShowContrattoModal(false); setSelectedContratto(null); setShowFirmaAdmin(false); }}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    );
  };

// SUGGERIMENTI VIEW
  const SuggerimentiView = () => {
    const [selectedSuggerimento, setSelectedSuggerimento] = React.useState(null);
    const [filtro, setFiltro] = React.useState('nuovo');

    const handleStatus = async (id, status) => {
      try {
        await updateDoc(doc(db, 'suggerimenti-prodotti', id), { status });
        if (selectedSuggerimento?.id === id) setSelectedSuggerimento(prev => ({ ...prev, status }));
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    // Estrae un prezzo valido da una nota libera: "5€", "5,90 €", "€ 5",
    // "5 euro", "circa 4,50". Prima cerca un numero vicino a €/euro, poi
    // ripiega sul primo numero del testo. Gestisce la virgola decimale.
    const parsePrezzoDaNote = (note) => {
      if (!note || typeof note !== 'string') return null;
      const testo = note.replace(/\s+/g, ' ');
      const vicino = testo.match(/(\d+(?:[.,]\d{1,2})?)\s*(?:€|euro)|(?:€|euro)\s*(\d+(?:[.,]\d{1,2})?)/i);
      let grezzo = vicino ? (vicino[1] || vicino[2]) : null;
      if (!grezzo) {
        const qualsiasi = testo.match(/\d+(?:[.,]\d{1,2})?/);
        grezzo = qualsiasi ? qualsiasi[0] : null;
      }
      if (!grezzo) return null;
      const num = parseFloat(grezzo.replace(',', '.'));
      return (isNaN(num) || num <= 0) ? null : num;
    };

    // Approva un suggerimento E crea il prodotto a catalogo in un'unica
    // operazione atomica (writeBatch): o riescono entrambe, o nessuna.
    // Così non può più capitare che lo status diventi "aggiunto" senza
    // che il prodotto compaia davvero nel catalogo.
    const handleApprovaEAggiungi = async (s) => {
      if (!s || !s.nomeProdotto) {
        alert('❌ Manca il nome del prodotto. Nulla è stato modificato.');
        return;
      }

      // Il suggerimento potrebbe essere già "aggiunto" ma senza prodotto a
      // catalogo (i casi rotti da recuperare): chiediamo conferma per
      // evitare duplicati accidentali.
      if (s.status === 'aggiunto') {
        if (!window.confirm(
          `"${s.nomeProdotto}" risulta già segnato come aggiunto.\n` +
          `Creare comunque il prodotto a catalogo?\n\n` +
          `(Usa questa opzione per recuperare i prodotti che mancano dal catalogo.)`
        )) return;
      }

      // 1) Prezzo: prima dalle note, poi chiedendo all'admin.
      let prezzo = parsePrezzoDaNote(s.note);
      if (prezzo == null) {
        const inserito = window.prompt(
          `Non riesco a leggere un prezzo dalle note ("${s.note || '—'}").\n` +
          `Inserisci il prezzo in € (es. 5.90):`
        );
        if (inserito == null) return; // admin ha annullato
        prezzo = parseFloat(String(inserito).replace(',', '.'));
        if (isNaN(prezzo) || prezzo <= 0) {
          alert('❌ Prezzo non valido. Operazione annullata: nulla è stato modificato.');
          return;
        }
      }

      try {
        // 2) Scrittura atomica: creazione prodotto + cambio status insieme.
        const batch = writeBatch(db);
        const nuovoProdottoRef = doc(collection(db, 'prodotti'));
        batch.set(nuovoProdottoRef, {
          name: s.nomeProdotto,
          category: 'Farmaci',
          price: prezzo,
          stock: 0,
          description: '',
          prescription: false,
          disponibile: true,
          imageUrl: s.fotoUrl || '',
          createdAt: serverTimestamp(),
          daSuggerimento: s.id   // tracciabilità della provenienza
        });
        batch.update(doc(db, 'suggerimenti-prodotti', s.id), { status: 'aggiunto' });
        await batch.commit();

        if (selectedSuggerimento?.id === s.id) {
          setSelectedSuggerimento(prev => ({ ...prev, status: 'aggiunto' }));
        }
        alert(
          `✅ "${s.nomeProdotto}" aggiunto al catalogo a ${prezzo.toFixed(2)}€.\n` +
          `Completa categoria, stock e descrizione dal Catalogo.`
        );
      } catch (e) {
        // Il batch è atomico: se siamo qui NON è stato scritto nulla,
        // né il prodotto né il cambio di status.
        alert('❌ Errore: nessuna modifica salvata (né prodotto né status).\n' + e.message);
      }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Eliminare questo suggerimento?')) return;
      try {
        await deleteDoc(doc(db, 'suggerimenti-prodotti', id));
        setSelectedSuggerimento(null);
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const filtrati = suggerimenti.filter(s => filtro === 'tutti' || s.status === filtro);
    const nuovi = suggerimenti.filter(s => s.status === 'nuovo').length;

    return (
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-2xl text-center">
            <p className="text-sm font-bold text-purple-600">Nuovi</p>
            <p className="text-4xl font-black text-purple-700">{nuovi}</p>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-2xl text-center">
            <p className="text-sm font-bold text-yellow-600">In Revisione</p>
            <p className="text-4xl font-black text-yellow-700">{suggerimenti.filter(s => s.status === 'in_revisione').length}</p>
          </div>
          <div className="bg-green-50 border-2 border-green-200 p-4 rounded-2xl text-center">
            <p className="text-sm font-bold text-green-600">Aggiunti</p>
            <p className="text-4xl font-black text-green-700">{suggerimenti.filter(s => s.status === 'aggiunto').length}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900">💡 Suggerimenti Prodotti</h2>
              <p className="text-gray-500">{suggerimenti.length} suggerimenti totali</p>
            </div>
            {/* Filtri */}
            <div className="flex gap-2">
              {[
                { val: 'nuovo', label: '🆕 Nuovi' },
                { val: 'in_revisione', label: '🔍 In Revisione' },
                { val: 'aggiunto', label: '✅ Aggiunti' },
                { val: 'tutti', label: 'Tutti' },
              ].map(f => (
                <button key={f.val} onClick={() => setFiltro(f.val)}
                  className={`px-3 py-2 rounded-xl font-bold text-sm transition ${
                    filtro === f.val ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {filtrati.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Lightbulb size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-semibold">Nessun suggerimento trovato</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtrati.map(s => (
                <div key={s.id} className={`p-4 rounded-xl border-2 transition cursor-pointer hover:border-purple-300 ${
                  s.status === 'nuovo' ? 'bg-purple-50 border-purple-200' :
                  s.status === 'in_revisione' ? 'bg-yellow-50 border-yellow-200' :
                  s.status === 'aggiunto' ? 'bg-green-50 border-green-200' :
                  'bg-gray-50 border-gray-200'
                }`} onClick={() => setSelectedSuggerimento(s)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {s.fotoUrl ? (
                        <img src={s.fotoUrl} alt={s.nomeProdotto}
                          className="w-14 h-14 rounded-xl object-cover border-2 border-white shadow" />
                      ) : (
                        <div className="w-14 h-14 bg-gray-200 rounded-xl flex items-center justify-center text-2xl">💊</div>
                      )}
                      <div>
                        <p className="font-black text-gray-900 text-lg">{s.nomeProdotto}</p>
                        {s.farmacia && <p className="text-sm text-gray-600">🏪 {s.farmacia}</p>}
                        <p className="text-xs text-gray-400">{s.userEmail}</p>
                        <p className="text-xs text-gray-400">
                          {s.createdAt?.toDate ? s.createdAt.toDate().toLocaleDateString('it-IT') : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-black ${
                        s.status === 'nuovo' ? 'bg-purple-200 text-purple-800' :
                        s.status === 'in_revisione' ? 'bg-yellow-200 text-yellow-800' :
                        s.status === 'aggiunto' ? 'bg-green-200 text-green-800' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {s.status === 'nuovo' ? '🆕 NUOVO' :
                         s.status === 'in_revisione' ? '🔍 IN REVISIONE' :
                         s.status === 'aggiunto' ? '✅ AGGIUNTO' : s.status}
                      </span>
                      <Eye className="text-gray-400" size={20} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal dettaglio */}
        {selectedSuggerimento && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-6 text-white rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-black">💡 Dettaglio Suggerimento</h2>
                  <button onClick={() => setSelectedSuggerimento(null)}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl">
                    <X className="text-white" size={24} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                {selectedSuggerimento.fotoUrl && (
                  <img src={selectedSuggerimento.fotoUrl} alt={selectedSuggerimento.nomeProdotto}
                    className="w-full h-56 object-cover rounded-2xl border-2 border-gray-100" />
                )}
                <div className="bg-gray-50 p-4 rounded-2xl space-y-2">
                  <div><span className="text-xs text-gray-500 font-bold uppercase">Prodotto</span>
                    <p className="font-black text-gray-900 text-lg">{selectedSuggerimento.nomeProdotto}</p></div>
                  {selectedSuggerimento.farmacia && (
                    <div><span className="text-xs text-gray-500 font-bold uppercase">Farmacia</span>
                      <p className="font-bold text-gray-800">🏪 {selectedSuggerimento.farmacia}</p></div>
                  )}
                  {selectedSuggerimento.note && (
                    <div><span className="text-xs text-gray-500 font-bold uppercase">Note</span>
                      <p className="text-gray-700">{selectedSuggerimento.note}</p></div>
                  )}
                  <div><span className="text-xs text-gray-500 font-bold uppercase">Utente</span>
                    <p className="text-gray-700">{selectedSuggerimento.userEmail}</p></div>
                  <div><span className="text-xs text-gray-500 font-bold uppercase">Data</span>
                    <p className="text-gray-700">{selectedSuggerimento.createdAt?.toDate ? selectedSuggerimento.createdAt.toDate().toLocaleString('it-IT') : ''}</p></div>
                </div>

                {/* Azioni */}
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => handleStatus(selectedSuggerimento.id, 'in_revisione')}
                    className="bg-yellow-100 text-yellow-800 py-3 rounded-xl font-bold hover:bg-yellow-200 transition">
                    🔍 In Revisione
                  </button>
                  <button onClick={() => handleApprovaEAggiungi(selectedSuggerimento)}
                    className="bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition">
                    ✅ Approva e aggiungi a catalogo
                  </button>
                  <button onClick={() => handleStatus(selectedSuggerimento.id, 'rifiutato')}
                    className="bg-gray-200 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-300 transition">
                    ❌ Rifiuta
                  </button>
                  <button onClick={() => handleDelete(selectedSuggerimento.id)}
                    className="bg-red-100 text-red-600 py-3 rounded-xl font-bold hover:bg-red-200 transition">
                    🗑️ Elimina
                  </button>
                </div>

                {/* Bottone aggiungi al catalogo */}
                <button
                  onClick={() => {
                    setSelectedSuggerimento(null);
                    setCurrentView('catalogo');
                  }}
                  className="w-full bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition">
                  💊 Vai al Catalogo per Aggiungerlo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };  

// CATALOGO VIEW
  const CatalogoView = () => {
    const [prodotti, setProdotti] = React.useState([]);
    const [showProdottoModal, setShowProdottoModal] = React.useState(false);
    const [selectedProdotto, setSelectedProdotto] = React.useState(null);
    const [searchProdotto, setSearchProdotto] = React.useState('');
    const [filtroCat, setFiltroCat] = React.useState('all');
    // Stato del servizio (attivo/sospeso) — blocca i NUOVI ordini lato App Cliente
    const [servizioAttivo, setServizioAttivo] = React.useState(true);
    const [salvandoServizio, setSalvandoServizio] = React.useState(false);

    React.useEffect(() => {
      // Legge lo stato del servizio in tempo reale
      const unsub = onSnapshot(doc(db, 'impostazioni', 'servizio'), (snap) => {
        if (snap.exists() && typeof snap.data().attivo === 'boolean') {
          setServizioAttivo(snap.data().attivo);
        } else {
          setServizioAttivo(true); // default: servizio attivo
        }
      }, () => setServizioAttivo(true));
      return () => unsub();
    }, []);

    const toggleServizio = async () => {
      const nuovoStato = !servizioAttivo;
      const msg = nuovoStato
        ? 'RIATTIVARE il servizio?\n\nI clienti potranno di nuovo ordinare dal catalogo e inviare ricette.'
        : 'SOSPENDERE il servizio?\n\nI clienti vedranno "Torniamo presto" e NON potranno creare nuovi ordini né inviare ricette.\n\nGli ordini già in corso proseguiranno normalmente.';
      if (!window.confirm(msg)) return;
      setSalvandoServizio(true);
      try {
        await setDoc(doc(db, 'impostazioni', 'servizio'), {
          attivo: nuovoStato,
          aggiornatoIl: new Date().toISOString(),
        }, { merge: true });
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setSalvandoServizio(false);
      }
    };

    React.useEffect(() => {
      const q = query(collection(db, 'prodotti'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setProdotti(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      });
      return () => unsubscribe();
    }, []);

    const handleDeleteProdotto = async (id) => {
      if (!window.confirm('Eliminare questo prodotto?')) return;
      try {
        await deleteDoc(doc(db, 'prodotti', id));
        alert('✅ Prodotto eliminato!');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const exportCatalogoCSV = () => {
      const headers = ['ID', 'Nome', 'Categoria', 'Prezzo (€)', 'Stock', 'Disponibile', 'Con Ricetta', 'Descrizione', 'Immagine URL'];
      const rows = prodotti.map(p => [
        p.id,
        p.name || '',
        p.category || '',
        p.price != null ? String(p.price).replace('.', ',') : '',
        p.stock != null ? p.stock : '',
        p.disponibile ? 'Sì' : 'No',
        p.prescription ? 'Sì' : 'No',
        (p.description || '').replace(/"/g, '""'),
        p.imageUrl || ''
      ]);
      const csvContent = [headers, ...rows]
        .map(row => row.map(v => `"${v}"`).join(';'))
        .join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalogo_prodotti_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    const filtrati = prodotti.filter(p => {
      const matchSearch = p.name?.toLowerCase().includes(searchProdotto.toLowerCase());
      const matchCat = filtroCat === 'all' || p.category === filtroCat;
      return matchSearch && matchCat;
    });

    const categorie = ['Farmaci', 'Integratori', 'Cosmesi', 'Dispositivi'];

    const ProdottoModal = () => {
      // I prodotti esistenti possono avere campi mancanti (undefined): Firestore
      // rifiuta undefined in updateDoc. Applichiamo i default anche a quelli,
      // così un campo assente diventa un valore valido invece di far crashare.
      const [form, setForm] = React.useState({
        name: '', category: 'Farmaci', price: '', stock: '',
        description: '', prescription: false, disponibile: true,
        imageUrl: '',
        ...(selectedProdotto || {})
      });
      const [uploadingImg, setUploadingImg] = React.useState(false);

      if (!showProdottoModal) return null;

      const handleImageUpload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { alert('⚠️ Immagine troppo grande. Massimo 5MB.'); return; }
        setUploadingImg(true);
        try {
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const storage = getStorage();
          const storageRef = ref(storage, `prodotti/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          setForm(prev => ({ ...prev, imageUrl: url }));
        } catch (e) {
          alert('❌ Errore upload: ' + e.message);
        } finally {
          setUploadingImg(false);
        }
      };

      const handleSave = async () => {
        if (!form.name || !form.price) {
          alert('Nome e prezzo sono obbligatori');
          return;
        }
        // Normalizza: Firestore rifiuta 'undefined'. Ogni campo deve avere
        // un valore valido, anche se il prodotto originale non ce l'aveva.
        const dati = {
          name: form.name,
          category: form.category || 'Farmaci',
          price: Number(form.price) || 0,
          stock: Number(form.stock) || 0,
          description: form.description || '',
          prescription: form.prescription === true,
          disponibile: form.disponibile !== false,
          imageUrl: form.imageUrl || ''
        };
        try {
          if (selectedProdotto) {
            await updateDoc(doc(db, 'prodotti', selectedProdotto.id), dati);
            alert('✅ Prodotto aggiornato!');
          } else {
            await addDoc(collection(db, 'prodotti'), {
              ...dati,
              createdAt: serverTimestamp()
            });
            alert('✅ Prodotto aggiunto!');
          }
          setShowProdottoModal(false);
          setSelectedProdotto(null);
        } catch (e) {
          alert('❌ Errore: ' + e.message);
        }
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-gray-900">
                {selectedProdotto ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
              </h2>
              <button onClick={() => { setShowProdottoModal(false); setSelectedProdotto(null); }} className="p-2 hover:bg-gray-100 rounded-xl">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nome Prodotto *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                  placeholder="es. Tachipirina 500mg" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Categoria</label>
                  <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none">
                    {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Prezzo (€) *</label>
                  <input type="number" step="0.01" value={form.price} onChange={e => setForm({...form, price: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                    placeholder="4.90" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Stock</label>
                <input type="number" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                  placeholder="es. 50" />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Descrizione</label>
                <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                  placeholder="es. 20 compresse" />
              </div>

              {/* IMMAGINE PRODOTTO */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Immagine Prodotto</label>
                {form.imageUrl ? (
                  <div className="relative">
                    <img src={form.imageUrl} alt={form.name}
                      className="w-full h-40 object-cover rounded-xl border-2 border-gray-200" />
                    <button
                      onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center font-bold text-sm"
                    >✕</button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-300 rounded-xl cursor-pointer hover:bg-purple-50 transition ${uploadingImg ? 'opacity-50' : ''}`}>
                    {uploadingImg ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                        <span className="text-purple-600 font-semibold">Caricamento...</span>
                      </div>
                    ) : (
                      <>
                        <div className="text-3xl mb-1">📸</div>
                        <p className="text-sm font-bold text-purple-600">Clicca per caricare immagine</p>
                        <p className="text-xs text-gray-400">JPG, PNG max 5MB</p>
                      </>
                    )}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => { if (e.target.files[0]) handleImageUpload(e.target.files[0]); }} />
                  </label>
                )}
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.prescription} onChange={e => setForm({...form, prescription: e.target.checked})}
                    className="w-5 h-5 text-purple-600 rounded" />
                  <span className="font-bold text-gray-700">Ricetta Richiesta</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.disponibile} onChange={e => setForm({...form, disponibile: e.target.checked})}
                    className="w-5 h-5 text-purple-600 rounded" />
                  <span className="font-bold text-gray-700">Disponibile</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition">
                {selectedProdotto ? 'Salva Modifiche' : 'Aggiungi Prodotto'}
              </button>
              <button onClick={() => { setShowProdottoModal(false); setSelectedProdotto(null); }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition">
                Annulla
              </button>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">

        {/* ── INTERRUTTORE SERVIZIO ATTIVO / SOSPESO ── */}
        <div className={`rounded-2xl p-6 shadow-xl border-2 ${
          servizioAttivo ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-300'
        }`}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl ${
                servizioAttivo ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {servizioAttivo ? '🟢' : '🔴'}
              </div>
              <div>
                <h3 className={`text-xl font-black ${servizioAttivo ? 'text-green-800' : 'text-red-800'}`}>
                  {servizioAttivo ? 'Servizio ATTIVO' : 'Servizio SOSPESO'}
                </h3>
                <p className={`text-sm ${servizioAttivo ? 'text-green-700' : 'text-red-700'}`}>
                  {servizioAttivo
                    ? 'I clienti possono ordinare dal catalogo e inviare ricette.'
                    : 'I clienti vedono "Torniamo presto" e non possono creare nuovi ordini.'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Gli ordini già in corso proseguono sempre normalmente.
                </p>
              </div>
            </div>
            <button
              onClick={toggleServizio}
              disabled={salvandoServizio}
              className={`px-6 py-3 rounded-xl font-black text-white transition disabled:opacity-50 ${
                servizioAttivo
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {salvandoServizio
                ? 'Salvataggio...'
                : servizioAttivo ? '⏸️ Sospendi servizio' : '▶️ Riattiva servizio'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-black text-gray-900">💊 Catalogo Prodotti</h2>
              <p className="text-gray-500">{prodotti.length} prodotti totali</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportCatalogoCSV}
                className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-green-700 transition flex items-center gap-2"
              >
                <Download size={18} />
                Export CSV
              </button>
              <button
                onClick={() => { setSelectedProdotto(null); setShowProdottoModal(true); }}
                className="bg-purple-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-purple-700 transition flex items-center gap-2"
              >
                <Plus size={20} />
                Nuovo Prodotto
              </button>
            </div>
          </div>

          {/* Filtri */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input type="text" placeholder="Cerca prodotto..." value={searchProdotto}
                onChange={e => setSearchProdotto(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-xl focus:border-purple-500 outline-none" />
            </div>
            <div className="flex gap-2">
              {['all', ...categorie].map(c => (
                <button key={c} onClick={() => setFiltroCat(c)}
                  className={`px-3 py-2 rounded-xl font-bold text-sm transition ${
                    filtroCat === c ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                  {c === 'all' ? 'Tutti' : c}
                </button>
              ))}
            </div>
          </div>

          {/* Lista prodotti */}
          {filtrati.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShoppingBag size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-semibold">Nessun prodotto trovato</p>
              <p className="text-sm mt-1">Aggiungi il primo prodotto al catalogo</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtrati.map(p => (
                <div key={p.id} className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200 hover:border-purple-300 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="font-black text-gray-900">{p.name}</p>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold">
                          {p.category}
                        </span>
                        {p.prescription && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-lg text-xs font-bold">
                            Ricetta
                          </span>
                        )}
                        {!p.disponibile && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-lg text-xs font-bold">
                            Non disponibile
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{p.description}</p>
                      <p className="text-xs text-gray-400 mt-1">Stock: {p.stock || 0}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-2xl font-black text-green-600">€{Number(p.price).toFixed(2)}</p>
                      <button onClick={() => { setSelectedProdotto(p); setShowProdottoModal(true); }}
                        className="p-2 bg-blue-100 hover:bg-blue-200 rounded-xl transition">
                        <Edit className="text-blue-600" size={20} />
                      </button>
                      <button onClick={() => handleDeleteProdotto(p.id)}
                        className="p-2 bg-red-100 hover:bg-red-200 rounded-xl transition">
                        <Trash2 className="text-red-600" size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <ProdottoModal />
      </div>
    );
  };  

  // RIDER MODAL
  const RiderModal = () => {
  const [formData, setFormData] = useState(selectedRider || {
    name: '',
    email: '',
    telefono: '',
    mezzoTrasporto: 'bicicletta'
  });

  if (!showRiderModal) return null;
    

    

    const handleSave = async () => {
      try {
        const nameParts = formData.name.trim().split(' ');
        const nome = nameParts[0];
        const cognome = nameParts.slice(1).join(' ') || '';

        if (selectedRider) {
          // Modifica esistente
          await updateDoc(doc(db, 'candidature-rider', selectedRider.id), {
            nome: nome,
            cognome: cognome,
            email: formData.email,
            telefono: formData.telefono,
            mezzoTrasporto: formData.mezzoTrasporto
          });
          alert('✅ Rider aggiornato!');
        } else {
          // Nuovo rider
          await addDoc(collection(db, 'candidature-rider'), {
            nome: nome,
            cognome: cognome,
            email: formData.email,
            telefono: formData.telefono,
            mezzoTrasporto: formData.mezzoTrasporto,
            status: 'approvato',
            createdAt: serverTimestamp(),
            dataNascita: '',
            citta: '',
            indirizzo: '',
            patente: '',
            assicurazione: '',
            disponibilita: [],
            oreSettimanali: '',
            note: ''
          });
          alert('✅ Rider aggiunto!');
        }
        setShowRiderModal(false);
        setSelectedRider(null);
      } catch (error) {
        console.error('Errore:', error);
        alert('❌ Errore durante il salvataggio');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-black text-gray-900">
              {selectedRider ? 'Modifica Rider' : 'Nuovo Rider'}
            </h2>
            <button onClick={() => { setShowRiderModal(false); setSelectedRider(null); }} className="p-2 hover:bg-gray-100 rounded-xl">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Nome Completo</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="Mario Rossi"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="mario.rossi@email.it"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Telefono</label>
              <input
                type="tel"
                value={formData.telefono}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
                placeholder="+39 333 123 4567"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Mezzo</label>
              <select
                value={formData.mezzoTrasporto}
                onChange={(e) => setFormData({...formData, mezzoTrasporto: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 outline-none"
              >
                <option value="bicicletta">Bicicletta</option>
                <option value="e-bike">E-Bike</option>
                <option value="scooter">Scooter</option>
                <option value="auto">Auto</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl font-bold transition"
              >
                {selectedRider ? 'Salva Modifiche' : 'Aggiungi Rider'}
              </button>
              <button
                onClick={() => { setShowRiderModal(false); setSelectedRider(null); }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-bold transition"
              >
                Annulla
              </button>
            </div>

            {/* RECENSIONI RIDER */}
            {selectedRider && <RecensioniRiderAdmin riderId={selectedRider.id} rider={selectedRider} />}
          </div>
        </div>
      </div>
    );
  };

  // RECENSIONI RIDER — ADMIN
  const RecensioniRiderAdmin = ({ riderId, rider }) => {
    const [recensioni, setRecensioni] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
      if (!riderId) return;
      const q = query(
        collection(db, 'riders', riderId, 'recensioni'),
        orderBy('createdAt', 'desc')
      );
      const unsubscribe = onSnapshot(q, (snap) => {
        setRecensioni(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
      return () => unsubscribe();
    }, [riderId]);

    const media = rider?.ratingMedia || 0;
    const count = rider?.ratingCount || 0;
    const renderStelle = (n) => '⭐'.repeat(n) + '☆'.repeat(5 - n);

    return (
      <div className="mt-6 border-t-2 border-gray-100 pt-6">
        <h3 className="font-black text-gray-900 mb-4 flex items-center gap-2">
          ⭐ Recensioni Clienti
        </h3>

        {/* Media */}
        <div className="bg-yellow-50 rounded-xl p-4 text-center mb-4">
          <p className="text-3xl font-black text-yellow-600">{media > 0 ? media.toFixed(1) : '—'}</p>
          <p className="text-yellow-500 text-base mt-1">{media > 0 ? renderStelle(Math.round(media)) : '☆☆☆☆☆'}</p>
          <p className="text-xs text-gray-500 mt-1">{count} recensioni totali</p>
        </div>

        {loading ? (
          <p className="text-center text-gray-400 text-sm">Caricamento...</p>
        ) : recensioni.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-4">Nessuna recensione ancora</p>
        ) : (
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {recensioni.map(r => (
              <div key={r.id} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm">{renderStelle(r.stelle)}</span>
                  <span className="text-xs text-gray-400">
                    {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('it-IT') : ''}
                  </span>
                </div>
                {r.commento && <p className="text-sm text-gray-600 italic">"{r.commento}"</p>}
                <p className="text-xs text-gray-400 mt-1">{r.userEmail}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // SIDEBAR
  const Sidebar = () => (
    <div className="w-72 bg-gradient-to-b from-slate-900 to-slate-800 border-r-4 border-purple-500 p-6 flex flex-col">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Zap className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-black text-white">Admin Panel</h1>
            <p className="text-xs text-gray-400 font-semibold">Control Center</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto">
        {[
          { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
          { id: 'orders', icon: Package, label: 'Ordini' },
          { id: 'revisione', icon: AlertTriangle, label: 'Revisione & Rimborsi' },
          { id: 'rimborsoRider', icon: DollarSign, label: 'Rimborso Rider' },
          { id: 'riders', icon: Bike, label: 'Rider' },
          { id: 'catalogo', icon: ShoppingBag, label: 'Catalogo' },
          { id: 'pharmacies', icon: Store, label: 'Farmacie' },
          { id: 'support', icon: MessageSquare, label: 'Supporto' },
          { id: 'candidature', icon: ClipboardList, label: 'Candidature' },
          { id: 'ricette', icon: FileText, label: 'Richieste Ricetta' },
          { id: 'suggerimenti', icon: Lightbulb, label: 'Suggerimenti' },
          { id: 'report', icon: BarChart3, label: 'Report & Export' },
          { id: 'promo', icon: Tag, label: 'Promo & Coupon' },
          { id: 'notifiche', icon: Bell, label: 'Notifiche' },
          { id: 'segnalazioni', icon: AlertTriangle, label: 'Segnalazioni' },
          { id: 'liquidazione', icon: Calculator, label: 'Liquidazione' },
          { id: 'livemap', icon: MapPin, label: 'Mappa Live' },
          { id: 'zona', icon: MapPin, label: 'Zona Copertura' },
        ].map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition relative ${
                currentView === item.id
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'text-gray-300 hover:bg-slate-700'
              }`}
            >
              <Icon size={20} />
              {item.label}
              {item.id === 'candidature' && stats.newCandidature > 0 && (
                <span className="absolute right-3 bg-red-500 text-white text-xs font-black rounded-full w-6 h-6 flex items-center justify-center">
                  {stats.newCandidature}
                </span>
              )}
              {item.id === 'revisione' && orders.filter(o => o.status === 'in_revisione_admin').length > 0 && (
                <span className="absolute right-3 bg-red-500 text-white text-xs font-black rounded-full w-6 h-6 flex items-center justify-center">
                  {orders.filter(o => o.status === 'in_revisione_admin').length}
                </span>
              )}
              {item.id === 'rimborsoRider' && orders.filter(o => !o.isRicettaOrder && o.rimborsoRiderStatus === 'da_rimborsare').length > 0 && (
                <span className="absolute right-3 bg-orange-500 text-white text-xs font-black rounded-full w-6 h-6 flex items-center justify-center">
                  {orders.filter(o => !o.isRicettaOrder && o.rimborsoRiderStatus === 'da_rimborsare').length}
                </span>
              )}
              {item.id === 'suggerimenti' && suggerimenti.filter(s => s.status === 'nuovo').length > 0 && (
                <span className="absolute right-3 bg-purple-500 text-white text-xs font-black rounded-full w-6 h-6 flex items-center justify-center">
                  {suggerimenti.filter(s => s.status === 'nuovo').length}
                </span>
              )}
              {item.id === 'riders' && ridersFotoPending.length > 0 && (
                <span className="absolute right-3 bg-yellow-500 text-white text-xs font-black rounded-full w-6 h-6 flex items-center justify-center">
                  {ridersFotoPending.length}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <button
        onClick={() => setShowCambiaPinModal(true)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-purple-400 hover:bg-purple-900 hover:bg-opacity-20 transition"
      >
        🔐 Cambia PIN
      </button>
      <button
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-red-400 hover:bg-red-900 hover:bg-opacity-20 transition"
      >
        <LogOut size={20} />
        Logout
      </button>
    </div>
  );


// ZONA COPERTURA VIEW
  // ═══ NOTIFICHE MARKETING ═══
  // Invia push SOLO agli utenti che hanno dato il consenso (GDPR).
  const NotificheView = () => {
    const [titolo, setTitolo] = React.useState('');
    const [messaggio, setMessaggio] = React.useState('');
    const [inviando, setInviando] = React.useState(false);
    const [esito, setEsito] = React.useState(null);
    const [conteggio, setConteggio] = React.useState(null);
    const [storico, setStorico] = React.useState([]);

    // Quanti utenti hanno dato il consenso?
    React.useEffect(() => {
      const q = query(collection(db, 'users'), where('consensoMarketing', '==', true));
      const unsub = onSnapshot(q, (snap) => {
        const conToken = snap.docs.filter(d => d.data().fcmToken).length;
        setConteggio({ totali: snap.size, raggiungibili: conToken });
      }, (err) => {
        // Non nascondere l'errore: se le regole Firestore bloccano la lettura
        // della collection 'users', va detto chiaramente.
        console.error('Errore lettura destinatari:', err.code, err.message);
        setConteggio({ errore: err.code === 'permission-denied'
          ? 'Le regole Firestore non permettono all\'Admin di leggere la collection users.'
          : err.message });
      });
      return () => unsub();
    }, []);

    // Storico campagne
    React.useEffect(() => {
      const q = query(collection(db, 'campagne-marketing'));
      const unsub = onSnapshot(q, (snap) => {
        const lista = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(b.inviataIl) - new Date(a.inviataIl))
          .slice(0, 10);
        setStorico(lista);
      }, (err) => {
        console.error('Errore lettura storico campagne:', err.code, err.message);
        setStorico([]);
      });
      return () => unsub();
    }, []);

    const invia = async () => {
      if (!titolo.trim() || !messaggio.trim()) {
        alert('⚠️ Inserisci titolo e messaggio.');
        return;
      }
      if (!conteggio || conteggio.raggiungibili === 0) {
        alert('⚠️ Nessun utente ha dato il consenso alle notifiche promozionali.');
        return;
      }
      const conferma = window.confirm(
        `Inviare questa notifica a ${conteggio.raggiungibili} utenti?\n\n` +
        `"${titolo}"\n${messaggio}\n\n` +
        `Verrà inviata SOLO a chi ha dato il consenso.`
      );
      if (!conferma) return;

      setInviando(true);
      setEsito(null);
      try {
        const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/inviaNotificaMarketing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            titolo: titolo.trim(),
            messaggio: messaggio.trim(),
            adminUid: auth.currentUser?.uid
          })
        });
        const data = await res.json();
        if (data.success) {
          setEsito({ ok: true, testo: `✅ Inviate ${data.inviate} notifiche${data.fallite ? ` (${data.fallite} fallite)` : ''}.` });
          setTitolo('');
          setMessaggio('');
        } else {
          setEsito({ ok: false, testo: '❌ ' + (data.error || 'Errore sconosciuto') });
        }
      } catch (e) {
        setEsito({ ok: false, testo: '❌ Errore: ' + e.message });
      }
      setInviando(false);
    };

    return (
      <div className="space-y-6">

        {/* Destinatari */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-1">🔔 Notifiche Promozionali</h2>
          <p className="text-gray-500 text-sm mb-4">
            Invia una notifica push agli utenti dell'App Cliente.
          </p>

          {conteggio && conteggio.errore ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm font-bold text-red-800">⚠️ Impossibile leggere i destinatari</p>
              <p className="text-xs text-red-700 mt-1">{conteggio.errore}</p>
            </div>
          ) : conteggio ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 font-bold uppercase">Con consenso</p>
                <p className="text-3xl font-black text-green-600">{conteggio.totali}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 font-bold uppercase">Raggiungibili</p>
                <p className="text-3xl font-black text-blue-600">{conteggio.raggiungibili}</p>
                <p className="text-[10px] text-gray-400 mt-1">hanno l'app installata</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">Caricamento destinatari...</p>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
            <p className="text-xs text-amber-800">
              <strong>⚠️ GDPR:</strong> le notifiche vengono inviate <strong>solo</strong> a chi ha dato
              il consenso esplicito. Chi non l'ha dato non riceve nulla, ed è corretto così.
            </p>
          </div>
        </div>

        {/* Composizione */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-100 space-y-4">
          <h3 className="text-lg font-black text-gray-900">✍️ Scrivi la notifica</h3>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Titolo</label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              maxLength={50}
              placeholder="Es: Novità nel catalogo"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">{titolo.length}/50 caratteri</p>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Messaggio</label>
            <textarea
              value={messaggio}
              onChange={(e) => setMessaggio(e.target.value)}
              maxLength={150}
              rows={3}
              placeholder="Es: Abbiamo aggiunto nuovi prodotti. Dai un'occhiata!"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 outline-none resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">{messaggio.length}/150 caratteri</p>
          </div>

          {/* Anteprima */}
          {(titolo || messaggio) && (
            <div className="bg-gray-100 rounded-xl p-4">
              <p className="text-xs text-gray-400 font-bold uppercase mb-2">Anteprima</p>
              <div className="bg-white rounded-lg shadow p-3 flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white text-lg flex-shrink-0">💊</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-900 truncate">{titolo || 'Titolo'}</p>
                  <p className="text-xs text-gray-600 line-clamp-2">{messaggio || 'Messaggio...'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-800">
              <strong>💡 Consiglio:</strong> siete in ambito sanitario. Evita messaggi che presumano
              uno stato di salute ("Hai il raffreddore?"). Meglio: "Novità nel catalogo",
              "La farmacia a casa tua in poco tempo".
            </p>
          </div>

          <button
            onClick={invia}
            disabled={inviando || !titolo.trim() || !messaggio.trim()}
            className="w-full bg-green-600 text-white py-3 rounded-xl font-black hover:bg-green-700 transition disabled:bg-gray-300"
          >
            {inviando ? 'Invio in corso...' : '📤 Invia notifica'}
          </button>

          {esito && (
            <div className={`rounded-xl p-3 ${esito.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm font-bold ${esito.ok ? 'text-green-800' : 'text-red-800'}`}>{esito.testo}</p>
            </div>
          )}
        </div>

        {/* Storico */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-100">
          <h3 className="text-lg font-black text-gray-900 mb-4">📜 Ultime campagne</h3>
          {storico.length === 0 ? (
            <p className="text-gray-400 text-sm">Nessuna campagna inviata.</p>
          ) : (
            <div className="space-y-3">
              {storico.map(c => (
                <div key={c.id} className="border border-gray-100 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 text-sm">{c.titolo}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{c.messaggio}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs font-bold text-green-600">{c.inviate} inviate</span>
                      <p className="text-[10px] text-gray-400">
                        {new Date(c.inviataIl).toLocaleDateString('it-IT')}
                      </p>
                      {c.tipo === 'automatica-inattivi' && (
                        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">auto</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    );
  };

  const ZonaView = () => {
    const [zona, setZona] = React.useState({
      latitudine: 41.1297,
      longitudine: 14.7824,
      raggioKm: 5,
      capAccettati: ['82100']
    });
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [newCap, setNewCap] = React.useState('');
    const [saved, setSaved] = React.useState(false);

    // Supplemento festivo (sabato, domenica e festivi) — a carico del cliente.
    // Copre il +20% che viene riconosciuto al rider nei giorni festivi.
    const [supplFestivo, setSupplFestivo] = React.useState({ attivo: true, importo: 1.00 });
    const [savingSuppl, setSavingSuppl] = React.useState(false);
    const [savedSuppl, setSavedSuppl] = React.useState(false);

    React.useEffect(() => {
      const loadZona = async () => {
        try {
          const snap = await getDoc(doc(db, 'impostazioni', 'zona'));
          if (snap.exists()) setZona(snap.data());
        } catch(e) { console.error(e); }
        try {
          const s = await getDoc(doc(db, 'impostazioni', 'supplementoFestivo'));
          if (s.exists()) {
            const d = s.data();
            setSupplFestivo({
              attivo: typeof d.attivo === 'boolean' ? d.attivo : true,
              importo: typeof d.importo === 'number' ? d.importo : 1.00
            });
          }
        } catch(e) { console.error(e); }
        setLoading(false);
      };
      loadZona();
    }, []);

    const salvaSupplFestivo = async () => {
      const imp = parseFloat(supplFestivo.importo);
      if (isNaN(imp) || imp < 0 || imp > 10) {
        alert('⚠️ Inserisci un importo valido (tra €0 e €10).');
        return;
      }
      setSavingSuppl(true);
      try {
        await setDoc(doc(db, 'impostazioni', 'supplementoFestivo'), {
          attivo: supplFestivo.attivo,
          importo: imp,
          aggiornatoIl: new Date().toISOString()
        }, { merge: true });
        setSavedSuppl(true);
        setTimeout(() => setSavedSuppl(false), 3000);
      } catch(e) {
        alert('Errore salvataggio: ' + e.message);
      }
      setSavingSuppl(false);
    };

    const salvaZona = async () => {
      setSaving(true);
      try {
        await setDoc(doc(db, 'impostazioni', 'zona'), zona);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } catch(e) {
        alert('Errore salvataggio: ' + e.message);
      }
      setSaving(false);
    };

    const rimuoviCap = (cap) => {
      setZona(prev => ({ ...prev, capAccettati: prev.capAccettati.filter(c => c !== cap) }));
    };

    const aggiungiCap = () => {
      if (!newCap.trim() || zona.capAccettati.includes(newCap.trim())) return;
      setZona(prev => ({ ...prev, capAccettati: [...prev.capAccettati, newCap.trim()] }));
      setNewCap('');
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Caricamento...</div>;

    return (
      <div className="space-y-6">
        {saved && (
          <div className="bg-green-50 border border-green-400 rounded-xl p-4 text-green-800 font-bold">
            ✅ Zona salvata! Le modifiche sono attive immediatamente.
          </div>
        )}

        {/* ── SUPPLEMENTO FESTIVO ── */}
        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <div>
            <h3 className="text-lg font-black text-gray-900">🗓️ Supplemento Festivo</h3>
            <p className="text-sm text-gray-500 mt-1">
              Costo extra a carico del cliente per gli ordini di <strong>sabato, domenica e festivi</strong>.
              Serve a coprire la maggiorazione del 20% riconosciuta al rider in quei giorni.
            </p>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={supplFestivo.attivo}
              onChange={(e) => setSupplFestivo(p => ({ ...p, attivo: e.target.checked }))}
              className="w-5 h-5 accent-green-600"
            />
            <span className="font-bold text-gray-800">
              {supplFestivo.attivo ? 'Supplemento ATTIVO' : 'Supplemento disattivato'}
            </span>
          </label>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Importo (€)</label>
            <input
              type="number"
              step="0.10"
              min="0"
              max="10"
              value={supplFestivo.importo}
              onChange={(e) => setSupplFestivo(p => ({ ...p, importo: e.target.value }))}
              disabled={!supplFestivo.attivo}
              className="w-40 px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-green-500 outline-none disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Il rider costa circa €0,70–0,90 in più nei festivi. Con €1,00 il costo è coperto.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-800">
              <strong>⚠️ Ricorda:</strong> se modifichi l'importo, aggiorna anche i Termini e Condizioni sul sito,
              dove il supplemento è dichiarato.
            </p>
          </div>

          <button
            onClick={salvaSupplFestivo}
            disabled={savingSuppl}
            className="bg-green-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-300"
          >
            {savingSuppl ? 'Salvataggio...' : '💾 Salva supplemento'}
          </button>

          {savedSuppl && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-sm text-green-800 font-bold">✅ Supplemento salvato! Attivo immediatamente.</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h3 className="text-lg font-black text-gray-900">📍 Centro di Copertura</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Latitudine</label>
              <input
                type="number"
                step="0.0001"
                value={zona.latitudine}
                onChange={e => setZona(prev => ({ ...prev, latitudine: parseFloat(e.target.value) }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Longitudine</label>
              <input
                type="number"
                step="0.0001"
                value={zona.longitudine}
                onChange={e => setZona(prev => ({ ...prev, longitudine: parseFloat(e.target.value) }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h3 className="text-lg font-black text-gray-900">📏 Raggio di Copertura</h3>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="1"
              max="30"
              value={zona.raggioKm}
              onChange={e => setZona(prev => ({ ...prev, raggioKm: parseInt(e.target.value) }))}
              className="flex-1"
            />
            <span className="text-2xl font-black text-purple-600 w-20 text-center">{zona.raggioKm} km</span>
          </div>
          <p className="text-sm text-gray-500">Area di consegna attorno al centro impostato</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h3 className="text-lg font-black text-gray-900">🏷️ CAP Accettati</h3>
          <div className="flex flex-wrap gap-2">
            {zona.capAccettati.map(cap => (
              <span key={cap} className="bg-purple-100 text-purple-800 font-bold px-3 py-1 rounded-full text-sm flex items-center gap-2">
                {cap}
                <button onClick={() => rimuoviCap(cap)} className="text-red-500 hover:text-red-700 font-black">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Aggiungi CAP (es. 82010)"
              value={newCap}
              onChange={e => setNewCap(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && aggiungiCap()}
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              maxLength={5}
            />
            <button onClick={aggiungiCap} className="bg-purple-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-purple-700">
              + Aggiungi
            </button>
          </div>
        </div>

        <button
          onClick={salvaZona}
          disabled={saving}
          className="w-full bg-purple-600 text-white py-4 rounded-xl font-black text-lg hover:bg-purple-700 transition disabled:opacity-50"
        >
          {saving ? 'Salvataggio...' : '💾 Salva Zona di Copertura'}
        </button>
      </div>
    );
  };

// LIVE MAP VIEW
  const LiveMapView = () => {
    const [activeRiders, setActiveRiders] = useState([]);
    const [ridersFirestore, setRidersFirestore] = useState([]);

    useEffect(() => {
      const q = query(
        collection(db, 'rider-positions'),
        where('isActive', '==', true)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const riderPos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // "In consegna" = ha una posizione attiva CON un ordine in corso (orderId valorizzato).
        // Un rider solo online in attesa ha isActive:true ma orderId null: non è in consegna.
        setActiveRiders(riderPos.filter(p => p.orderId));
      });
      return () => unsubscribe();
    }, []);

    // Carica riders direttamente da Firestore con shifts e isOnline aggiornati
    useEffect(() => {
      const q = query(
        collection(db, 'riders'),
        where('status', '==', 'approved')
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        setRidersFirestore(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      });
      return () => unsubscribe();
    }, []);

    // Calcola data odierna locale (no UTC)
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const hour = now.getHours();

    // Rider con turno oggi
    const ridersConTurnoOggi = ridersFirestore.filter(r => {
      const todayShifts = r.shifts?.[todayStr] || {};
      return todayShifts.morning || todayShifts.afternoon;
    });

    // Rider online
    const ridersOnline = ridersFirestore.filter(r => r.isOnline);
    const forzaOffline = async (rider) => {
      if (!window.confirm(`Portare offline ${rider.name}?`)) return;
      try {
        await updateDoc(doc(db, 'riders', rider.id), {
          isOnline: false,
          isOnlineUpdatedAt: new Date().toISOString()
        });
        if (rider.email) {
          await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: rider.email,
              subject: '⚠️ Portato Offline dall\'Admin',
              html: `<p>Ciao ${rider.name}, sei stato portato offline dall'amministratore. Per informazioni contatta il supporto.</p>`
            })
          });
        }
        alert(`✅ ${rider.name} portato offline.`);
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };
    const rimuoviTurno = async (rider, periodo) => {
      if (!window.confirm(`Rimuovere il turno ${periodo === 'morning' ? 'mattina' : 'pomeriggio'} di ${rider.name}?`)) return;
      const nuoviShifts = { ...rider.shifts };
      if (nuoviShifts[todayStr]) {
        nuoviShifts[todayStr] = { ...nuoviShifts[todayStr], [periodo]: false };
      }
      try {
        await updateDoc(doc(db, 'riders', rider.id), { shifts: nuoviShifts });

        // Invia notifica push al rider se ha FCM token
        if (rider.fcmToken) {
          await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: rider.email,
              subject: '⚠️ Turno rimosso',
              html: `<p>Ciao ${rider.name}, il tuo turno ${periodo === 'morning' ? 'mattina (09:00-13:00)' : 'pomeriggio (16:00-20:30)'} di oggi è stato rimosso dall'amministratore. Per informazioni contatta il supporto.</p>`
            })
          });
        }

        alert('✅ Turno rimosso.');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const invitaOnline = async (rider) => {
      try {
        const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/invitaRiderOnline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ riderId: rider.id })
        });
        const data = await res.json();
        if (data.success) {
          alert(`✅ Notifica inviata a ${rider.name}!`);
        } else if (data.reason === 'gia_online') {
          alert(`ℹ️ ${rider.name} è già online.`);
        } else if (data.reason === 'no_token') {
          alert(`⚠️ ${rider.name} non ha le notifiche attive sul dispositivo.`);
        } else {
          alert(`⚠️ ${data.message || 'Impossibile inviare la notifica'}`);
        }
      } catch (error) {
        alert('Errore durante l\'invio della notifica');
      }
    };

    return (
      <div className="space-y-6">
        {/* STATISTICHE */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 border-2 border-green-200 p-4 rounded-2xl">
            <p className="text-sm font-bold text-green-600">🟢 Online Ora</p>
            <p className="text-4xl font-black text-green-700">{ridersOnline.length}</p>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-2xl">
            <p className="text-sm font-bold text-purple-600">🛵 In Consegna</p>
            <p className="text-4xl font-black text-purple-700">{activeRiders.length}</p>
          </div>
          <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-2xl">
            <p className="text-sm font-bold text-orange-600">📅 Turni Oggi</p>
            <p className="text-4xl font-black text-orange-700">{ridersConTurnoOggi.length}</p>
          </div>
        </div>

        {/* TURNI OGGI */}
        <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-gray-100">
          <h3 className="text-xl font-black text-gray-900 mb-4">📅 Turni di Oggi</h3>
          {ridersConTurnoOggi.length === 0 ? (
            <p className="text-gray-400 text-center py-4">Nessun rider con turno oggi</p>
          ) : (
            <div className="space-y-3">
              {ridersConTurnoOggi.map(rider => {
                const todayShifts = rider.shifts?.[todayStr] || {};
                return (
                  <div key={rider.id} className="border-2 border-gray-100 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${rider.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-black text-gray-900">{rider.name}</p>
                        <p className="text-xs font-bold text-gray-400 tracking-widest">ID: {rider.id}</p>
                        <div className="flex gap-2 mt-1">
                          {todayShifts.morning && (
                            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                              ☀️ Mattina 09-13
                            </span>
                          )}
                          {todayShifts.afternoon && (
                            <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">
                              🌤️ Pomeriggio 16-20:30
                            </span>
                          )}
                          {rider.isOnline ? (
                            <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">🟢 Online</span>
                          ) : (
                            <span className="bg-gray-100 text-gray-500 text-xs font-bold px-2 py-0.5 rounded-full">⚫ Offline</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      {rider.isOnline && (
                        <button
                          onClick={() => forzaOffline(rider)}
                          className="bg-gray-800 text-white text-xs font-bold px-3 py-1 rounded-lg hover:bg-gray-900 transition"
                        >
                          ⏻ Forza Offline
                        </button>
                      )}
                      {todayShifts.morning && (
                        <button
                          onClick={() => rimuoviTurno(rider, 'morning')}
                          className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold px-3 py-1 rounded-lg hover:bg-red-100 transition"
                        >
                          ✕ Mattina
                        </button>
                      )}
                      {todayShifts.afternoon && (
                        <button
                          onClick={() => rimuoviTurno(rider, 'afternoon')}
                          className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold px-3 py-1 rounded-lg hover:bg-red-100 transition"
                        >
                          ✕ Pomeriggio
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* RIDER OFFLINE CON TURNO OGGI */}
        {ridersConTurnoOggi.filter(r => !r.isOnline).length > 0 && (
          <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-red-100">
            <h3 className="text-xl font-black text-gray-900 mb-4">⚠️ Rider con Turno ma Offline</h3>
            <div className="space-y-3">
              {ridersConTurnoOggi.filter(r => !r.isOnline).map(rider => {
                const todayShifts = rider.shifts?.[todayStr] || {};
                const now = new Date();
                const hour = now.getHours();
                const turnoAttivo = (todayShifts.morning && hour >= 9 && hour < 13) || (todayShifts.afternoon && hour >= 16 && hour < 21);
                return (
                  <div key={rider.id} className={`border-2 rounded-xl p-4 flex items-center justify-between ${turnoAttivo ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-gray-300" />
                      <div>
                        <p className="font-black text-gray-900">{rider.name}</p>
                        <p className="text-xs text-gray-400">ID: {rider.id}</p>
                        <div className="flex gap-2 mt-1">
                          {todayShifts.morning && <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">☀️ Mattina</span>}
                          {todayShifts.afternoon && <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">🌤️ Pomeriggio</span>}
                          {turnoAttivo && <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">🔴 Dovrebbe essere online!</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => invitaOnline(rider)}
                        className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-lg hover:bg-green-700 transition">
                        📲 Invita Online
                      </button>
                      {todayShifts.morning && (
                        <button onClick={() => rimuoviTurno(rider, 'morning')}
                          className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold px-3 py-1 rounded-lg hover:bg-red-100 transition">
                          ✕ Mattina
                        </button>
                      )}
                      {todayShifts.afternoon && (
                        <button onClick={() => rimuoviTurno(rider, 'afternoon')}
                          className="bg-red-50 border border-red-200 text-red-600 text-xs font-bold px-3 py-1 rounded-lg hover:bg-red-100 transition">
                          ✕ Pomeriggio
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-gray-100">
          <h3 className="text-xl font-black text-gray-900 mb-4">🛵 Rider in Consegna</h3>
          {activeRiders.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <MapPin size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Nessun rider attivo al momento</p>
              <p className="text-sm">I rider appariranno qui quando accetteranno una consegna</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeRiders.map(rider => {
                const order = orders.find(o => o.id === rider.orderId);
                const updatedAt = rider.updatedAt?.toDate ? rider.updatedAt.toDate() : new Date();
                const secondsAgo = Math.round((new Date() - updatedAt) / 1000);
                return (
                  <div key={rider.id} className="border-2 border-gray-100 rounded-xl p-4 hover:border-purple-200 transition">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-black text-sm">🛵</span>
                        </div>
                        <div>
                          <p className="font-black text-gray-900">{rider.riderName}</p>
                          <p className="text-xs text-gray-500">Aggiornato {secondsAgo}s fa</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs font-bold text-green-600">LIVE</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <p className="text-xs text-gray-500">Posizione</p>
                        <p className="font-bold text-gray-700">{rider.lat?.toFixed(4)}, {rider.lng?.toFixed(4)}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <p className="text-xs text-gray-500">Ordine</p>
                        <p className="font-bold text-gray-700">#{rider.orderId?.substring(0,8)}</p>
                      </div>
                      {order && (
                        <>
                          <div className="bg-purple-50 p-2 rounded-lg">
                            <p className="text-xs text-purple-500">Cliente</p>
                            <p className="font-bold text-purple-700">{order.customerName || order.userEmail}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded-lg">
                            <p className="text-xs text-green-500">Totale Ordine</p>
                            <p className="font-bold text-green-700">€{order.total?.toFixed(2)}</p>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="mt-3">
                      <a
                        href={`https://www.google.com/maps?q=${rider.lat},${rider.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-xl font-bold text-sm hover:bg-blue-700 transition w-full"
                      >
                        <MapPin size={16} />
                        Vedi su Google Maps
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // PROMO & COUPON VIEW
  const PromoView = () => {
    const [codici, setCodici] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [showForm, setShowForm] = React.useState(false);
    const [nuovoCodice, setNuovoCodice] = React.useState({
      codice: '',
      tipo: 'percentuale', // percentuale | fisso | spedizione_gratis
      valore: '',
      maxUsi: '',
      scadenza: '',
      attivo: true,
      descrizione: ''
    });

    React.useEffect(() => {
      const unsubscribe = onSnapshot(
        collection(db, 'promo-codici'),
        (snap) => {
          setCodici(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        }
      );
      return () => unsubscribe();
    }, []);

    const creaCodice = async () => {
      if (!nuovoCodice.codice.trim()) { alert('Inserisci il codice'); return; }
      if (nuovoCodice.tipo !== 'spedizione_gratis' && !nuovoCodice.valore) { alert('Inserisci il valore sconto'); return; }
      try {
        await addDoc(collection(db, 'promo-codici'), {
          ...nuovoCodice,
          codice: nuovoCodice.codice.toUpperCase().trim(),
          valore: nuovoCodice.tipo === 'spedizione_gratis' ? 2.99 : parseFloat(nuovoCodice.valore),
          maxUsi: nuovoCodice.maxUsi ? parseInt(nuovoCodice.maxUsi) : null,
          usiTotali: 0,
          createdAt: new Date().toISOString()
        });
        setShowForm(false);
        setNuovoCodice({ codice: '', tipo: 'percentuale', valore: '', maxUsi: '', scadenza: '', attivo: true, descrizione: '' });
        alert('✅ Codice creato!');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const toggleAttivo = async (id, attuale) => {
      await updateDoc(doc(db, 'promo-codici', id), { attivo: !attuale });
    };

    const eliminaCodice = async (id) => {
      if (!window.confirm('Eliminare questo codice?')) return;
      await deleteDoc(doc(db, 'promo-codici', id));
    };

    const getSconto = (c) => {
      if (c.tipo === 'percentuale') return `-${c.valore}%`;
      if (c.tipo === 'fisso') return `-€${c.valore.toFixed(2)}`;
      if (c.tipo === 'spedizione_gratis') return 'Spedizione gratis';
      return '';
    };

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-6 text-white flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black mb-1">🏷️ Promo & Coupon</h2>
            <p className="text-pink-100 text-sm">Gestisci i codici sconto per i clienti</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-white text-purple-600 px-4 py-2 rounded-xl font-black hover:bg-purple-50 transition flex items-center gap-2"
          >
            <Plus size={18} />
            Nuovo Codice
          </button>
        </div>

        {/* FORM NUOVO CODICE */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-purple-200">
            <h3 className="font-black text-gray-900 mb-4">➕ Crea nuovo codice</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Codice *</label>
                <input
                  value={nuovoCodice.codice}
                  onChange={e => setNuovoCodice(p => ({ ...p, codice: e.target.value.toUpperCase() }))}
                  placeholder="es. PRIMA10"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl font-black uppercase focus:border-purple-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Tipo sconto</label>
                <select
                  value={nuovoCodice.tipo}
                  onChange={e => setNuovoCodice(p => ({ ...p, tipo: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none"
                >
                  <option value="percentuale">Percentuale (%)</option>
                  <option value="fisso">Importo fisso (€)</option>
                  <option value="spedizione_gratis">Spedizione gratis</option>
                </select>
              </div>
              {nuovoCodice.tipo !== 'spedizione_gratis' && (
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    Valore {nuovoCodice.tipo === 'percentuale' ? '(%)' : '(€)'}
                  </label>
                  <input
                    type="number"
                    value={nuovoCodice.valore}
                    onChange={e => setNuovoCodice(p => ({ ...p, valore: e.target.value }))}
                    placeholder={nuovoCodice.tipo === 'percentuale' ? 'es. 10' : 'es. 5'}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Max utilizzi (vuoto = illimitato)</label>
                <input
                  type="number"
                  value={nuovoCodice.maxUsi}
                  onChange={e => setNuovoCodice(p => ({ ...p, maxUsi: e.target.value }))}
                  placeholder="es. 100"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Scadenza (vuoto = mai)</label>
                <input
                  type="date"
                  value={nuovoCodice.scadenza}
                  onChange={e => setNuovoCodice(p => ({ ...p, scadenza: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-1">Descrizione interna</label>
                <input
                  value={nuovoCodice.descrizione}
                  onChange={e => setNuovoCodice(p => ({ ...p, descrizione: e.target.value }))}
                  placeholder="es. Campagna lancio aprile 2026"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold">Annulla</button>
              <button onClick={creaCodice} className="flex-1 bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 transition">✅ Crea Codice</button>
            </div>
          </div>
        )}

        {/* LISTA CODICI */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Caricamento...</div>
        ) : codici.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-md">
            <p className="text-4xl mb-4">🏷️</p>
            <p className="text-gray-500 font-semibold">Nessun codice promo creato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {codici.map(c => (
              <div key={c.id} className={`bg-white rounded-2xl shadow-md p-5 border-2 ${c.attivo ? 'border-green-200' : 'border-gray-200 opacity-60'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 px-4 py-2 rounded-xl">
                      <span className="font-black text-purple-700 text-lg tracking-wider">{c.codice}</span>
                    </div>
                    <div>
                      <p className="font-black text-gray-900">{getSconto(c)}</p>
                      {c.descrizione && <p className="text-xs text-gray-500">{c.descrizione}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleAttivo(c.id, c.attivo)}
                      className={`px-3 py-1 rounded-full text-xs font-black ${c.attivo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {c.attivo ? '✅ Attivo' : '⏸️ Disattivo'}
                    </button>
                    <button onClick={() => eliminaCodice(c.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                  <span>📊 Usi: <strong>{c.usiTotali || 0}{c.maxUsi ? `/${c.maxUsi}` : ''}</strong></span>
                  {c.scadenza && <span>📅 Scade: <strong>{new Date(c.scadenza).toLocaleDateString('it-IT')}</strong></span>}
                  <span>📅 Creato: <strong>{new Date(c.createdAt).toLocaleDateString('it-IT')}</strong></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // SEGNALAZIONI VIEW
  const SegnalazioniView = () => {
    const [segnalazioni, setSegnalazioni] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [filtro, setFiltro] = React.useState('aperta');
    const [selected, setSelected] = React.useState(null);

    React.useEffect(() => {
      const q = query(collection(db, 'segnalazioni'), orderBy('createdAt', 'desc'));
      const unsubscribe = onSnapshot(q, (snap) => {
        setSegnalazioni(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
      return () => unsubscribe();
    }, []);


    const chiudiSegnalazione = async (id) => {
      await updateDoc(doc(db, 'segnalazioni', id), { status: 'chiusa' });
      if (selected?.id === id) setSelected(prev => ({ ...prev, status: 'chiusa' }));
    };

    const eliminaSegnalazione = async (id) => {
      if (!window.confirm('Eliminare questa segnalazione?')) return;
      await deleteDoc(doc(db, 'segnalazioni', id));
      setSelected(null);
    };

    const tipoLabel = (tipo) => {
      const map = {
        prodotto_mancante: '📦 Prodotto mancante',
        prodotto_sbagliato: '❌ Prodotto sbagliato',
        prodotto_danneggiato: '💔 Prodotto danneggiato',
        rider_scortese: '😠 Rider scortese',
        consegna_ritardata: '⏰ Consegna in ritardo',
        altro: '💬 Altro'
      };
      return map[tipo] || tipo;
    };

    const filtrate = segnalazioni.filter(s => filtro === 'tutte' || s.status === filtro);
    const aperte = segnalazioni.filter(s => s.status === 'aperta').length;

    return (
      <div className="space-y-6">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-6 text-white flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black mb-1">⚠️ Segnalazioni Clienti</h2>
            <p className="text-red-100 text-sm">Problemi segnalati dopo la consegna</p>
          </div>
          {aperte > 0 && (
            <div className="bg-white text-red-600 px-4 py-2 rounded-xl font-black text-lg">
              {aperte} aperte
            </div>
          )}
        </div>

        {/* STATS */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Aperte', val: segnalazioni.filter(s => s.status === 'aperta').length, color: 'red' },
            { label: 'Chiuse', val: segnalazioni.filter(s => s.status === 'chiusa').length, color: 'green' },
            { label: 'Totali', val: segnalazioni.length, color: 'gray' },
          ].map(s => (
            <div key={s.label} className={`bg-${s.color}-50 border-2 border-${s.color}-200 p-4 rounded-2xl text-center`}>
              <p className={`text-sm font-bold text-${s.color}-600`}>{s.label}</p>
              <p className={`text-4xl font-black text-${s.color}-700`}>{s.val}</p>
            </div>
          ))}
        </div>

        {/* FILTRI */}
        <div className="flex gap-2">
          {[
            { val: 'aperta', label: '🔴 Aperte' },
            { val: 'chiusa', label: '✅ Chiuse' },
            { val: 'tutte', label: 'Tutte' },
          ].map(f => (
            <button key={f.val} onClick={() => setFiltro(f.val)}
              className={`px-4 py-2 rounded-xl font-bold text-sm transition ${
                filtro === f.val ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* LISTA */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Caricamento...</div>
        ) : filtrate.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <p className="text-4xl mb-4">✅</p>
            <p className="text-gray-500 font-semibold">Nessuna segnalazione {filtro === 'aperta' ? 'aperta' : ''}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtrate.map(s => (
              <div key={s.id}
                onClick={() => setSelected(s)}
                className={`bg-white rounded-2xl shadow-md p-5 border-2 cursor-pointer hover:border-red-300 transition ${
                  s.status === 'aperta' ? 'border-red-200' : 'border-gray-200 opacity-70'
                }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-black ${
                      s.status === 'aperta' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {s.status === 'aperta' ? '🔴 APERTA' : '✅ CHIUSA'}
                    </span>
                    <span className="text-sm font-bold text-gray-700">{tipoLabel(s.tipo)}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {s.createdAt?.toDate ? s.createdAt.toDate().toLocaleDateString('it-IT') : new Date(s.createdAt?.seconds * 1000).toLocaleDateString('it-IT')}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{s.userEmail}</p>
                <p className="text-xs text-gray-400">Ordine: #{s.orderId?.substring(0, 8)}</p>
                {s.dettagli && <p className="text-sm text-gray-600 mt-2 italic">"{s.dettagli}"</p>}
              </div>
            ))}
          </div>
        )}

        {/* MODAL DETTAGLIO */}
        {selected && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-gradient-to-r from-red-500 to-orange-500 p-5 text-white rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-black">⚠️ Dettaglio Segnalazione</h2>
                    <p className="text-red-100 text-sm">Ordine #{selected.orderId?.substring(0, 8)}</p>
                  </div>
                  <button onClick={() => setSelected(null)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl">
                    <X className="text-white" size={20} />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-xs text-gray-500 font-bold">Tipo</p>
                    <p className="font-bold text-gray-900 text-sm">{tipoLabel(selected.tipo)}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-xs text-gray-500 font-bold">Stato</p>
                    <p className={`font-bold text-sm ${selected.status === 'aperta' ? 'text-red-600' : 'text-green-600'}`}>
                      {selected.status === 'aperta' ? '🔴 Aperta' : '✅ Chiusa'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-xs text-gray-500 font-bold">Cliente</p>
                    <p className="font-bold text-gray-900 text-sm">{selected.userEmail}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <p className="text-xs text-gray-500 font-bold">Data</p>
                    <p className="font-bold text-gray-900 text-sm">
                      {selected.createdAt?.toDate ? selected.createdAt.toDate().toLocaleString('it-IT') : ''}
                    </p>
                  </div>
                </div>
                {selected.dettagli && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl">
                    <p className="text-xs text-orange-600 font-bold mb-1">Dettagli cliente</p>
                    <p className="text-gray-700 text-sm">"{selected.dettagli}"</p>
                  </div>
                )}
                <div className="bg-blue-50 p-3 rounded-xl">
                  <p className="text-xs text-blue-600 font-bold mb-1">Ordine collegato</p>
                  <p className="font-bold text-gray-900">#{selected.orderId?.substring(0, 8)}</p>
                  <button
                    onClick={() => { setSelected(null); setCurrentView('orders'); }}
                    className="text-xs text-blue-600 font-bold hover:underline mt-1">
                    Vai agli ordini →
                  </button>
                </div>
                <div className="flex gap-3">
                  {selected.status === 'aperta' && (
                    <button onClick={() => chiudiSegnalazione(selected.id)}
                      className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition">
                      ✅ Segna come Risolta
                    </button>
                  )}
                  <button onClick={() => eliminaSegnalazione(selected.id)}
                    className="flex-1 bg-red-100 text-red-600 py-3 rounded-xl font-bold hover:bg-red-200 transition">
                    🗑️ Elimina
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // REPORT VIEW
  const ReportView = () => {
    const [meseSelezionato, setMeseSelezionato] = React.useState(() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [pagamentoLoading, setPagamentoLoading] = React.useState(null);

    const COMPENSO_BASE = 3.00;
    const COMPENSO_KM = 0.20;
    const MAGGIORAZIONE_FESTIVA = 0.20;

    const isFestivo = (dateStr) => {
      const d = new Date(dateStr);
      const day = d.getDay();
      return day === 0 || day === 6;
    };

    const getReportRider = () => {
      const [anno, mese] = meseSelezionato.split('-').map(Number);
      const report = {};

      orders.forEach(order => {
        if (order.status !== 'delivered') return;
        if (!order.riderId) return;

        const dataOrdine = new Date(order.createdAt);
        if (dataOrdine.getFullYear() !== anno || dataOrdine.getMonth() + 1 !== mese) return;

        if (!report[order.riderId]) {
          const rider = riders.find(r => r.id === order.riderId);
          report[order.riderId] = {
            riderId: order.riderId,
            nome: order.riderName || rider?.name || 'Rider sconosciuto',
            email: rider?.email || '',
            stripeAccountId: rider?.stripeAccountId || '',
            consegne: 0,
            consegneFestive: 0,
            kmTotali: 0,
            compensoBase: 0,
            compensoKm: 0,
            maggiorazioneFestiva: 0,
            totaleCompetenze: 0,
            pagato: rider?.[`pagato_${meseSelezionato}`] || false,
            ordini: []
          };
        }

        const kmPercorsi = order.deliveryDistanceKm || 0;
        const festivo = isFestivo(order.createdAt);
        const compensoBaseOrdine = COMPENSO_BASE;
        const compensoKmOrdine = kmPercorsi * COMPENSO_KM;
        const subtotale = compensoBaseOrdine + compensoKmOrdine;
        const maggiorazione = festivo ? subtotale * MAGGIORAZIONE_FESTIVA : 0;
        const totaleOrdine = subtotale + maggiorazione;

        report[order.riderId].consegne += 1;
        if (festivo) report[order.riderId].consegneFestive += 1;
        report[order.riderId].kmTotali += kmPercorsi;
        report[order.riderId].compensoBase += compensoBaseOrdine;
        report[order.riderId].compensoKm += compensoKmOrdine;
        report[order.riderId].maggiorazioneFestiva += maggiorazione;
        report[order.riderId].totaleCompetenze += totaleOrdine;
        report[order.riderId].ordini.push({ ...order, totaleOrdine });
      });

      return Object.values(report).sort((a, b) => b.consegne - a.consegne);
    };

    const exportRiderCSV = (reportRider) => {
      exportCSV(
        reportRider,
        `report-rider-${meseSelezionato}.csv`,
        ['ID Rider', 'Nome', 'Email', 'Consegne', 'di cui Festive', 'Km Totali', 'Compenso Base', 'Compenso Km', 'Magg. Festiva', 'Totale Competenze', 'Pagato'],
        r => [
          r.riderId.substring(0, 8),
          r.nome,
          r.email,
          r.consegne,
          r.consegneFestive,
          r.kmTotali.toFixed(1),
          r.compensoBase.toFixed(2),
          r.compensoKm.toFixed(2),
          r.maggiorazioneFestiva.toFixed(2),
          r.totaleCompetenze.toFixed(2),
          r.pagato ? 'Sì' : 'No'
        ]
      );
    };

    const segnaComePagato = async (riderId) => {
      setPagamentoLoading(riderId);
      try {
        await updateDoc(doc(db, 'riders', riderId), {
          [`pagato_${meseSelezionato}`]: true
        });
        alert(`✅ Pagamento segnato per ${meseSelezionato}`);
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setPagamentoLoading(null);
      }
    };

    const reportRider = getReportRider();
    const totaleDaPagare = reportRider.filter(r => !r.pagato).reduce((sum, r) => sum + r.totaleCompetenze, 0);
    const totalePagato = reportRider.filter(r => r.pagato).reduce((sum, r) => sum + r.totaleCompetenze, 0);

    // Genera mesi selezionabili (ultimi 12 mesi)
    const mesiDisponibili = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      mesiDisponibili.push({ val, label });
    }

    return (
      <div className="space-y-6">

        {/* HEADER */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
          <h2 className="text-2xl font-black mb-1">📊 Report Compensi Rider</h2>
          <p className="text-purple-100 text-sm">Calcolo automatico basato su €3/consegna + €0,20/km + 20% festivo</p>
        </div>

        {/* SELETTORE MESE + EXPORT */}
        <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <label className="block text-sm font-bold text-gray-700 mb-2">📅 Seleziona Mese</label>
              <select
                value={meseSelezionato}
                onChange={e => setMeseSelezionato(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 outline-none font-bold"
              >
                {mesiDisponibili.map(m => (
                  <option key={m.val} value={m.val}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => exportRiderCSV(reportRider)}
                className="flex items-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-xl font-bold hover:bg-purple-700 transition"
              >
                <Download size={18} />
                Export CSV
              </button>
              <button
                onClick={exportOrdiniCSV}
                className="flex items-center gap-2 bg-gray-700 text-white px-4 py-3 rounded-xl font-bold hover:bg-gray-800 transition"
              >
                <Download size={18} />
                Tutti Ordini
              </button>
            </div>
          </div>
        </div>

        {/* STATS MESE */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-4 shadow-md border-2 border-gray-100 text-center">
            <p className="text-xs text-gray-500 font-bold mb-1">Rider attivi</p>
            <p className="text-3xl font-black text-purple-600">{reportRider.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-md border-2 border-red-100 text-center">
            <p className="text-xs text-red-500 font-bold mb-1">Da pagare</p>
            <p className="text-3xl font-black text-red-600">€{totaleDaPagare.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-md border-2 border-green-100 text-center">
            <p className="text-xs text-green-500 font-bold mb-1">Già pagato</p>
            <p className="text-3xl font-black text-green-600">€{totalePagato.toFixed(2)}</p>
          </div>
        </div>

        {/* LISTA RIDER */}
        {reportRider.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <p className="text-4xl mb-4">🛵</p>
            <p className="text-gray-500 font-semibold">Nessuna consegna completata in questo mese</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reportRider.map(r => (
              <div key={r.riderId} className={`bg-white rounded-2xl shadow-md p-6 border-2 ${r.pagato ? 'border-green-200' : 'border-orange-200'}`}>
                {/* HEADER RIDER */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center font-black text-purple-600 text-lg">
                      {r.nome?.split(' ').map(n => n[0]).join('').substring(0, 2) || '?'}
                    </div>
                    <div>
                      <p className="font-black text-gray-900 text-lg">{r.nome}</p>
                      <p className="text-sm text-gray-500">{r.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-black text-purple-600">€{r.totaleCompetenze.toFixed(2)}</p>
                    {r.pagato ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-black">✅ Pagato</span>
                    ) : (
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-black">⏳ Da pagare</span>
                    )}
                  </div>
                </div>

                {/* BREAKDOWN COMPENSO */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-gray-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-gray-500 font-bold">Consegne</p>
                    <p className="text-xl font-black text-gray-900">{r.consegne}</p>
                    {r.consegneFestive > 0 && (
                      <p className="text-xs text-orange-500">{r.consegneFestive} festive</p>
                    )}
                  </div>
                  <div className="bg-blue-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-blue-500 font-bold">Base (€3×n)</p>
                    <p className="text-xl font-black text-blue-700">€{r.compensoBase.toFixed(2)}</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-purple-500 font-bold">Km ({r.kmTotali.toFixed(1)} km)</p>
                    <p className="text-xl font-black text-purple-700">€{r.compensoKm.toFixed(2)}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-xl text-center">
                    <p className="text-xs text-orange-500 font-bold">Festivo +20%</p>
                    <p className="text-xl font-black text-orange-700">€{r.maggiorazioneFestiva.toFixed(2)}</p>
                  </div>
                </div>

                {/* AZIONI */}
                {!r.pagato && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => segnaComePagato(r.riderId)}
                      disabled={pagamentoLoading === r.riderId}
                      className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
                    >
                      {pagamentoLoading === r.riderId ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Salvataggio...</>
                      ) : (
                        <>✅ Segna come Pagato</>
                      )}
                    </button>
                    {/* Pulsante "Paga via Stripe" RIMOSSO: Stripe Connect è stato
                        abbandonato. I rider vengono pagati tramite bonifico bancario
                        entro il 12 del mese successivo. */}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* EXPORT ALTRI CSV */}
        <div className="bg-white rounded-2xl shadow-md p-6 border-2 border-gray-100">
          <h3 className="text-lg font-black text-gray-900 mb-4">📥 Altri Export</h3>
          <div className="grid grid-cols-2 gap-4">
            <button onClick={exportFarmacieCSV}
              className="flex items-center justify-center gap-2 bg-green-600 text-white py-3 px-4 rounded-xl font-bold hover:bg-green-700 transition">
              <Download size={18} /> Farmacie ({pharmacies.length})
            </button>
            <button onClick={exportCandidatureCSV}
              className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-xl font-bold hover:bg-blue-700 transition">
              <Download size={18} /> Candidature ({candidature.length})
            </button>
          </div>
        </div>

      </div>
    );
  };

  // ORDER MODAL
  const OrderModal = () => {
    if (!showModal || !selectedOrder) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white rounded-t-3xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black">Ordine #{selectedOrder.id.substring(0, 8)}</h2>
                <p className="text-purple-100">Dettagli completi</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition">
                <X className="text-white" size={24} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h3 className="font-black text-gray-900 mb-3">Cliente</h3>
              <div className="bg-gray-50 p-4 rounded-xl">
                <p className="text-gray-700">{selectedOrder.userEmail}</p>
                <p className="text-sm text-gray-500 mt-1">{selectedOrder.address}</p>
              </div>
            </div>

            <div>
              <h3 className="font-black text-gray-900 mb-3">Prodotti</h3>
              <div className="space-y-2">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-700">{item.quantity}x {item.name}</span>
                    <span className="font-bold text-gray-900">€{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-xl border-2 border-purple-200">
              <div className="flex justify-between items-center">
                <span className="font-black text-gray-900 text-lg">Totale</span>
                <span className="font-black text-purple-600 text-2xl">€{selectedOrder.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Ripartizione incasso — solo ordini in CONTANTI */}
            {selectedOrder.paymentMethod === 'cash' && (() => {
              const anticipoFarmacia = selectedOrder.isRicettaOrder
                ? (selectedOrder.importoPagatoFarmacia || 0)
                : (selectedOrder.subtotal || 0);
              const totale = selectedOrder.total || 0;
              const consegnaNetta = parseFloat((totale - anticipoFarmacia).toFixed(2));
              return (
                <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-200 space-y-2">
                  <h3 className="font-black text-orange-800 text-sm">💵 Ripartizione incasso contanti</h3>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Totale incassato dal rider</span>
                    <span className="font-bold text-gray-900">€{totale.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Anticipo farmacia (resta al rider)</span>
                    <span className="font-bold text-gray-700">€{anticipoFarmacia.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t border-orange-200">
                    <span className="font-black text-gray-900">Quota consegna da scalare al rider</span>
                    <span className="font-black text-orange-600">-€{consegnaNetta.toFixed(2)}</span>
                  </div>
                  <p className="text-[11px] text-orange-500 pt-1">
                    Il rider ha incassato l'intero importo in contanti. Trattiene l'anticipo farmacia (suo rimborso) e in liquidazione mensile gli viene scalata solo la quota di consegna (€{consegnaNetta.toFixed(2)}), che ha incassato per conto di FarmaciaSubito.
                  </p>
                </div>
              );
            })()}

            <div>
              <h3 className="font-black text-gray-900 mb-3">Stato</h3>
              <span className={`px-4 py-2 rounded-xl font-bold inline-block ${
                selectedOrder.status === 'delivered' ? 'bg-green-100 text-green-700' :
                selectedOrder.status === 'delivering' ? 'bg-purple-100 text-purple-700' :
                selectedOrder.status === 'ready' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
              }`}>
                {selectedOrder.status}
              </span>
            </div>

            {selectedOrder.riderName && (
              <div>
                <h3 className="font-black text-gray-900 mb-3">Rider Assegnato</h3>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <p className="text-gray-700 font-bold">{selectedOrder.riderName}</p>
                </div>
              </div>
            )}

            {selectedOrder.ricettaUrl && (
              <div>
                <h3 className="font-black text-gray-900 mb-3">📋 Ricetta Medica</h3>
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-bold text-blue-900">{selectedOrder.ricettaNome || 'Ricetta allegata'}</p>
                    <p className="text-xs text-blue-600 mt-1">Il cliente ha allegato una ricetta medica</p>
                  </div>
                  <button
                    onClick={() => window.open(selectedOrder.ricettaUrl, '_blank')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-blue-700 transition"
                  >
                    👁 Vedi Ricetta
                  </button>
                </div>
              </div>
            )}

            {selectedOrder.scontrinoUrl && (
              <div>
                <h3 className="font-black text-gray-900 mb-3">🧾 Scontrino Farmacia</h3>
                <div className="bg-green-50 border border-green-200 p-4 rounded-xl flex items-center justify-between">
                  <div>
                    <p className="font-bold text-green-900">Scontrino caricato dal rider</p>
                    {selectedOrder.scontrinoCaricatoAt && (
                      <p className="text-xs text-green-600 mt-1">
                        Caricato il {new Date(selectedOrder.scontrinoCaricatoAt).toLocaleString('it-IT')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => window.open(selectedOrder.scontrinoUrl, '_blank')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-green-700 transition"
                  >
                    👁 Vedi Scontrino
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
  <div className="flex h-screen bg-gray-50">
    <Sidebar />
    
    <div className="flex-1 overflow-y-auto">
      <div className="bg-white border-b-2 border-gray-200 p-6 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-black text-gray-900">
              {currentView === 'dashboard' && '📊 Dashboard'}
              {currentView === 'orders' && '📦 Gestione Ordini'}
              {currentView === 'revisione' && '⚠️ Revisione & Rimborsi'}
              {currentView === 'rimborsoRider' && '💸 Rimborso Rider'}
              {currentView === 'riders' && '🚴 Gestione Rider'}
              {currentView === 'pharmacies' && '🏪 Gestione Farmacie'}
              {currentView === 'support' && '💬 Sistema Supporto'}
              {currentView === 'candidature' && '📋 Candidature Rider'}
              {currentView === 'report' && '📊 Report & Export'}
              {currentView === 'livemap' && '🗺️ Mappa Live Rider'}
              {currentView === 'zona' && '📍 Zona di Copertura'}
              {currentView === 'notifiche' && '🔔 Notifiche Promozionali'}
              {currentView === 'catalogo' && '💊 Catalogo Prodotti'}
              {currentView === 'suggerimenti' && '💡 Suggerimenti Prodotti'}
              {currentView === 'promo' && '🏷️ Promo & Coupon'}
              {currentView === 'segnalazioni' && '⚠️ Segnalazioni Clienti'}
              {currentView === 'liquidazione' && '💰 Liquidazione Mensile'}
              {currentView === 'ricette' && '📋 Richieste Ricetta'}
            </h2>
            <p className="text-gray-500 font-semibold">Benvenuto nel pannello di controllo</p>
          </div>
          <div className="flex items-center gap-4 relative">
            <button 
              onClick={() => { setShowNotifications(!showNotifications); setAdminNotifiche(prev => prev.map(n => ({ ...n, letta: true }))); }}
              className="relative p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
            >
              <Bell className="text-gray-600" size={20} />
              {adminNotifiche.filter(n => !n.letta).length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {adminNotifiche.filter(n => !n.letta).length}
                </span>
              )}
            </button>

            {/* Dropdown Notifiche */}
            {showNotifications && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-black text-gray-900">Notifiche</h3>
                  <button onClick={() => setAdminNotifiche([])} className="text-xs text-red-500 font-bold">Cancella tutto</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {adminNotifiche.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Bell size={32} className="mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Nessuna notifica</p>
                    </div>
                  ) : (
                    adminNotifiche.map(n => (
                      <div key={n.id} className={`p-4 border-b border-gray-50 flex items-start gap-3 ${!n.letta ? 'bg-blue-50' : ''}`}>
                        <span className="text-2xl flex-shrink-0">{n.icon}</span>
                        <div className="flex-1">
                          <p className="font-bold text-gray-900 text-sm">{n.tipo}</p>
                          <p className="text-xs text-gray-600 mt-0.5">{n.messaggio}</p>
                          <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        {currentView === 'dashboard' && <DashboardView />}
        {currentView === 'orders' && <OrdersView />}
        {currentView === 'revisione' && <RevisioneView />}
        {currentView === 'rimborsoRider' && <RimborsoRiderView />}
        {currentView === 'riders' && <RidersView />}
        {currentView === 'pharmacies' && <PharmaciesView />}
        {currentView === 'support' && <SupportView />}
        {currentView === 'candidature' && <CandidatureView />}
        {currentView === 'report' && <ReportView />}
        {currentView === 'livemap' && <LiveMapView />}
        {currentView === 'catalogo' && <CatalogoView />}
        {currentView === 'suggerimenti' && <SuggerimentiView />}
        {currentView === 'promo' && <PromoView />}
        {currentView === 'segnalazioni' && <SegnalazioniView />}
        {currentView === 'liquidazione' && <LiquidazioneView />}
        {currentView === 'ricette' && <RichiesteRicettaView />}
        {currentView === 'zona' && <ZonaView />}
        {currentView === 'notifiche' && <NotificheView />}
      </div>
    </div>

    <OrderModal />
    <CandidaturaModal />
    <PharmacyModal />
    <TicketModal />
    <ContrattoModal />
    <RiderModal />

    {/* MODAL CAMBIA PIN */}
    {showCambiaPinModal && <CambiaPinModalInline />}
    </div>
  );
};

export default FarmaciaSubitoAdmin;